/**
 * Main 3D scene: tunnel environment with grid, notes, rails, and slides.
 * Camera perspective matches Synth Riders editor — first-person view
 * looking down the tunnel with notes approaching the player.
 * LyricAI-inspired purple/magenta color scheme.
 */

import React, { useMemo, useCallback, useEffect, useRef, useState, useContext, createContext, Suspense } from 'react';
import { Canvas, ThreeEvent, useThree, useFrame } from '@react-three/fiber';
import { Line, Text, Html, useTexture, useFBX } from '@react-three/drei';
import UnityCameraControls from './UnityCameraControls';
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { makeNeonMaterial } from './consoleShaders';
import { useEditorStore, getNoteColorsFromSettings } from '../../store/editorStore';
import type { RenderMode } from '../../store/editorStore';
import { GRID_SCALE } from '../../types/beatmap';
import type { Note, FlashEvent, BookmarkEntry } from '../../types/beatmap';
// Scroll-scrub tick uses the shared Flow sound module so the editor's
// Sound Effects console (Scroll Tick voice + volume) drives it.
import { playScrollTick } from '../../editors/flow/flowSounds';
import { seekAudio } from '../../audio/playbackController';
import {
  type BurstSpark, MAX_BURST_SPARKS, emptyBurstSpark, spawnBurst, onNoteBurst,
  placedAt, noteKey,
} from './srBurst';
import ObstacleModel, { tickObstacles, resolveWallXY, InstancedObstaclePools, InstancedAirObstaclePools, CONFIGURABLE_KEYS } from './ObstacleModel';
import type { ObstacleShape } from '../../store/editorSettings';
import DesignShapes, { pickDesignAt, DesignMeshGizmo, DesignPathEditor, isDesignGizmoDragActive } from './DesignShapes';
import { sampleDesignShape } from '../../types/designShape';
import HumanFigure from './HumanFigure';
import DancerModel from './DancerModel';
import RiderModel from './RiderModel';
import IntensityFloorTrail from './IntensityFloorTrail';
import { color, font } from '../ui/theme';
import { useTheme } from '../ui/themeStore';
import { accentHex, resolveRgb } from '../ui/themeColors';
import { PERF, RENDER_STATS } from '../../perf/adaptiveBudget';
import AdaptivePerfGovernor from '../../perf/AdaptivePerfGovernor';
import { useScaledDpr } from '../../perf/useScaledDpr';
import PotatoFrameCap from '../../perf/PotatoFrameCap';
import SharedStepLines from './StepLines';
import {
  TOOL_TO_NOTE, TOOL_TO_WALL_TYPE,
  RAIL_TOOL_MODES, NOTE_TOOL_MODES, OBSTACLE_TOOL_MODES,
} from './tunnelToolModes';

// Beat-number chip — styled like the transport play button (accent outline +
// text, dark tinted fill, pill corners) so it stays tied to the general theme.
const beatLabelStyle: React.CSSProperties = {
  fontFamily: font.sans,
  fontSize: 7,
  fontWeight: 700,
  padding: '2px 7px',
  borderRadius: 999,
  letterSpacing: 0.3,
  whiteSpace: 'nowrap',
  userSelect: 'none',
  color: 'var(--accent)',
  background: 'oklch(0.18 calc(0.026 * var(--tint-c)) var(--tint-h) / 0.4)',
  border: '1px solid var(--accent)',
};

// Plain (non-measure) beats count too — reviewers asked for 1-by-1 numbering —
// but their chips stay visually lighter than a bar chip so measures still pop.
const beatLabelDimStyle: React.CSSProperties = {
  ...beatLabelStyle,
  color: 'var(--accent-dim)',
  border: '1px solid oklch(0.62 calc(0.11 * var(--tint-c)) var(--tint-h) / 0.45)',
  fontWeight: 600,
};

// Accent-themed 3D line colours — mirrors the Flow editor's `accentLine` so the
// floor grid, snap lines and tunnel edges follow the live accent/tint theme
// (three.js can't read CSS vars, so resolve the live accent hue to hex here).
// `measure` uses the sidebar section-title tone (--accent-soft → 0.89/0.085).
function useAccentLine() {
  const themeAccent = useTheme(s => s.accent);
  const themeAccentHex = useTheme(s => s.accentHex);
  return useMemo(() => ({
    outer:   accentHex(0.58, 0.17),   // outer tunnel edges (stronger)
    inner:   accentHex(0.45, 0.13),   // inner / back-wall lines (dimmer)
    sub:     accentHex(0.78, 0.17),   // step / snap subdivision lines — accent-tinted
    beat:    accentHex(0.70, 0.205),  // whole-beat lines — canonical accent colour
    measure: accentHex(0.80, 0.18),   // measure lines — bright accent (not white)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [themeAccent, themeAccentHex]);
}

// Elastic ease-out (cartoon overshoot): 0 → overshoot → settle at 1. Drives the
// spawn "pop" when a note is placed, and the grid-alignment pulse.
function easeOutElastic(t: number): number {
  if (t <= 0) return 0;
  if (t >= 1) return 1;
  const c4 = (2 * Math.PI) / 3;
  return Math.pow(2, -10 * t) * Math.sin((t * 10 - 0.75) * c4) + 1;
}

// ── Constants ───────────────────────────────────────────────────────────────

const TUNNEL_LENGTH = 200;
const TUNNEL_WIDTH = 5;         // Wider to match the play area
const TUNNEL_HEIGHT = 4;
// BEAT_SPACING (Note Jump Speed): controls how far apart beats are in the 3D tunnel.
// Higher = notes are more spread out and appear to approach faster. Driven by store.noteJumpSpeed.
// This is a module-level variable updated by SceneContent on every frame for performance.
export let BEAT_SPACING = 2;
export const VISIBLE_BEATS_AHEAD = 50;
export const VISIBLE_BEATS_BEHIND = 2;

// The chart beat aligned with the AUDIO. note.time_beats are beat-relative (no
// offset); the audio plays the music for chart-beat B at currentTime = B*60/bpm
// + offset. So the scroll / placement / culling reference must subtract the
// offset. Identical to the old raw `(currentTime/60)*bpm` when offset is 0 (i.e.
// every map that worked before), so it only changes offset≠0 maps.
export function chartBeat(): number {
  const s = useEditorStore.getState();
  const bpm = s.beatmap?.bpm || 120;
  return ((s.playback.currentTime - (s.beatmap?.offset || 0) / 1000) / 60) * bpm;
}
export const TUNNEL_WIDTH_EXPORT = 5;   // Re-exported for IntensityFloorTrail
export const TUNNEL_LENGTH_EXPORT = 200;

// Note Y center offset — notes sit centered in the play area
const NOTE_Y_OFFSET = 1.8;

// ── Placement toast (lightweight event bus) ─────────────────────────────────
// Fires a short-lived alert message displayed over the 3D viewport.
let _toastCallback: ((msg: string) => void) | null = null;
function showPlacementToast(msg: string) {
  if (_toastCallback) _toastCallback(msg);
}

// ── Theme Colors ────────────────────────────────────────────────────────────

const THEME = {
  gridMeasure: '#3B1A8B',      // Deep purple for measure lines
  gridBeat: '#1A0F3D',         // Darker purple for beat lines
  gridCenter: '#5B2EC5',       // Brighter purple for center line
  edgePrimary: '#7C3AED',      // Vivid purple for tunnel edges
  edgeSecondary: '#3B1A8B',    // Deep purple for top edges
  playhead: color.accent,          // Magenta for playhead
  playheadGlow: color.accent,
  crosshair: color.accent,
  fog: '#050510',
  floor: '#030308',
  beatNumber: color.inkFaint,
  ambient: '#1a1030',
  pointLight1: '#7C3AED',
  pointLight2: color.accent,
};

// ── Stem Aura Colors (for dominant_stem visual feedback) ─────────────────────

const STEM_AURA_COLORS: Record<string, string> = {
  drums:  '#F87171',  // Red
  vocals: '#60A5FA',  // Blue
  bass:   '#34D399',  // Green
  other:  '#FBBF24',  // Yellow/Amber
  mixed:  color.accentSoft,  // Purple (no dominant stem)
  pattern: '#E879F9', // Fuchsia — pattern-sourced notes
};

// ── Note Fresnel Overlay ────────────────────────────────────────────────────


// ── Shared unit geometries ───────────────────────────────────────────────────
// ONE geometry instance per shape, shared by every note / rail node / floor
// marker and sized via mesh.scale. Per-component `<sphereGeometry args>` was
// allocating (and uploading to the GPU) a fresh buffer on every mount — with a
// 100-note window scrolling at playback speed that meant constant buffer churn.
const UNIT_NOTE_SPHERE = new THREE.SphereGeometry(1, 16, 12);
const UNIT_FOOT_BOX = new THREE.BoxGeometry(1.7, 0.6, 1.7); // console foot pad (×noteSize)
const UNIT_NODE_SPHERE = new THREE.SphereGeometry(1, 12, 8);
const UNIT_MARKER_SPHERE = new THREE.SphereGeometry(1, 8, 6);
const UNIT_GRID_PLANE = new THREE.PlaneGeometry(1, 1); // beat-grid lines (scaled per line)

// Note material with the fresnel rim FOLDED IN via onBeforeCompile — replaces
// the old per-note overlay mesh (its own sphere + transparent ShaderMaterial),
// halving note meshes and removing a transparent draw per note. All fresnel
// notes share one compiled program (customProgramCacheKey).
function makeNoteMaterial(opts: {
  color: string; emissive: string; metalness: number; roughness: number;
  fresnel: { color: string; intensity: number; power: number } | null;
}): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({
    color: opts.color,
    emissive: opts.emissive,
    metalness: opts.metalness,
    roughness: opts.roughness,
  });
  if (opts.fresnel) {
    const f = opts.fresnel;
    m.onBeforeCompile = (shader) => {
      shader.uniforms.uFresnelColor = { value: new THREE.Color(f.color) };
      shader.uniforms.uFresnelIntensity = { value: f.intensity };
      shader.uniforms.uFresnelPower = { value: f.power };
      shader.fragmentShader = shader.fragmentShader
        .replace('void main() {',
          'uniform vec3 uFresnelColor;\nuniform float uFresnelIntensity;\nuniform float uFresnelPower;\nvoid main() {')
        .replace('#include <emissivemap_fragment>',
          `#include <emissivemap_fragment>
          float noteFres = pow(1.0 - saturate(abs(dot(normalize(vViewPosition), normal))), uFresnelPower);
          totalEmissiveRadiance += uFresnelColor * noteFres * uFresnelIntensity;`);
    };
    m.customProgramCacheKey = () => 'note-fresnel';
  }
  return m;
}

// ── Note Mesh ───────────────────────────────────────────────────────────────

// Thin ring drawn around a selected note. Billboards to face the camera so it
// always reads as a clean circle — with a small gap from the note — no matter
// the camera angle. UNIT space: the parent mesh is a unit shape scaled by the
// note/node size, so the ring inherits that scale (surface radius = 1).
const SELECTION_RING_GEO = new THREE.RingGeometry(1.2, 1.28, 48);
function SelectionRing() {
  const ref = useRef<THREE.Mesh>(null!);
  useFrame((state) => {
    if (ref.current) ref.current.quaternion.copy(state.camera.quaternion);
  });
  return (
    <mesh ref={ref} geometry={SELECTION_RING_GEO} raycast={() => null} renderOrder={10}>
      <meshBasicMaterial
        color="#FFFFFF"
        transparent
        opacity={0.95}
        side={THREE.DoubleSide}
        toneMapped={false}
        depthTest={false}
        depthWrite={false}
      />
    </mesh>
  );
}

// ── Synth Console note meshes ────────────────────────────────────────────────
// The Console editor swaps the placeholder sphere/box notes for the real game
// note meshes (SM_Note hand / SM_Note_Ground foot). They're loaded once via a
// provider mounted (in Suspense) only for Console and handed to every NoteMesh
// through context, so the perf-critical note loop doesn't suspend per-instance.
// NoteMesh keeps ALL its editor behaviour (spawn pop, grid pulse, dim/fade,
// selection) — only the geometry changes; VR falls back to the sphere/box.
const CONSOLE_NOTE_FBX = {
  hand: '/models/console/notes/SM_Note.fbx',
  foot: '/models/console/notes/SM_Note_Ground.fbx',
};
useFBX.preload(CONSOLE_NOTE_FBX.hand);
useFBX.preload(CONSOLE_NOTE_FBX.foot);
// The game note's arrow face must point at the camera (down +Z). The FBX faces
// away, so flip it 180°. Tunable per visual check.
const CONSOLE_NOTE_ROT: [number, number, number] = [0, Math.PI, 0];

interface ConsoleNoteGeos { hand: THREE.BufferGeometry; foot: THREE.BufferGeometry; }
const ConsoleNoteGeoContext = createContext<ConsoleNoteGeos | null>(null);

// A game note is a multi-part FBX (SM_Note = arrow + cylinder body + border +
// base), so we MERGE every sub-mesh (baking each one's transform) into a single
// geometry — taking just the first mesh would drop the body/border. Attributes
// are normalized to position+normal+uv (non-indexed) so the parts always merge.
export function mergeFbxGeometry(group: THREE.Object3D, filter?: (name: string) => boolean): THREE.BufferGeometry | null {
  group.updateMatrixWorld(true);
  const parts: THREE.BufferGeometry[] = [];
  group.traverse((o) => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || !m.geometry) return;
    if (filter && !filter(m.name)) return;   // skip unwanted sub-meshes (e.g. note chain/guide)
    const src = m.geometry.index ? m.geometry.toNonIndexed() : m.geometry.clone();
    const pos = src.getAttribute('position');
    if (!pos) return;
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', (pos as THREE.BufferAttribute).clone());
    let nrm = src.getAttribute('normal') as THREE.BufferAttribute | undefined;
    if (!nrm) { src.computeVertexNormals(); nrm = src.getAttribute('normal') as THREE.BufferAttribute; }
    g.setAttribute('normal', nrm.clone());
    const uv = src.getAttribute('uv') as THREE.BufferAttribute | undefined;
    g.setAttribute('uv', uv ? uv.clone() : new THREE.BufferAttribute(new Float32Array(pos.count * 2), 2));
    g.applyMatrix4(m.matrixWorld);
    parts.push(g);
  });
  if (!parts.length) return null;
  return mergeGeometries(parts, false) ?? parts[0];
}

// Centre a clone of the FBX geometry and scale it to a ~2-unit max dimension so
// NoteMesh can size it the same way as the unit sphere (radius = noteSize).
function normalizeNoteGeometry(geo: THREE.BufferGeometry): THREE.BufferGeometry {
  const c = geo.clone();
  c.computeBoundingBox();
  const bb = c.boundingBox!;
  const size = new THREE.Vector3();
  const center = new THREE.Vector3();
  bb.getSize(size);
  bb.getCenter(center);
  const maxDim = Math.max(size.x, size.y, size.z) || 1;
  c.translate(-center.x, -center.y, -center.z);
  const s = 2 / maxDim;
  c.scale(s, s, s);
  return c;
}

function ConsoleNoteGeoProvider({ children }: { children: React.ReactNode }) {
  const handFbx = useFBX(CONSOLE_NOTE_FBX.hand);
  const footFbx = useFBX(CONSOLE_NOTE_FBX.foot);
  const value = useMemo<ConsoleNoteGeos | null>(() => {
    // Keep only the note face/body (arrow + base + outline); drop the chain link
    // and the flat sprite "guide" so the note reads as the game ring+arrow.
    const keep = (n: string) => !/chain|guide/i.test(n);
    const h = mergeFbxGeometry(handFbx, keep);
    const f = mergeFbxGeometry(footFbx, keep);
    if (!h || !f) return null;
    return { hand: normalizeNoteGeometry(h), foot: normalizeNoteGeometry(f) };
  }, [handFbx, footFbx]);
  return <ConsoleNoteGeoContext.Provider value={value}>{children}</ConsoleNoteGeoContext.Provider>;
}

function NoteMesh({
  note, index, selected,
}: {
  note: Note; index: number; selected: boolean;
}) {
  const selectNote = useEditorStore(s => s.selectNote);
  const _gp = useEditorStore(s => s.gameplaySettings);
  const noteSize = _gp.noteSize;
  const noteColors = useMemo(() => getNoteColorsFromSettings(_gp), [_gp.noteColorLeft, _gp.noteColorRight, _gp.noteColorOneHand, _gp.noteColorBothHands]);
  const noteEmissiveNormal = useEditorStore(s => s.interfaceSettings.noteEmissiveNormal);
  const noteEmissiveStemAura = useEditorStore(s => s.interfaceSettings.noteEmissiveStemAura);
  const noteEmissivePast = useEditorStore(s => s.interfaceSettings.noteEmissivePast);
  const noteOpacityPast = useEditorStore(s => s.interfaceSettings.noteOpacityPast);

  const heatmapEnabled = useEditorStore(s => s.heatmapEnabled);
  const intensityData = useEditorStore(s => s.intensityData);
  // Synth Console: a note low on the grid (Y ≤ -0.5) is a FOOT note, not a hand
  // note (the game derives this purely from Y). Render it as a flat floor pad so
  // it reads distinctly. Console-only — VR maps keep the plain sphere.
  const activeView = useEditorStore(s => s.activeView);
  const isFoot = activeView === 'console' && note.position[1] <= -0.5;
  // Console: use the real game note mesh (provided once via context) instead of
  // the sphere/box. null in VR / before the FBX loads → sphere/box fallback.
  const consoleGeos = useContext(ConsoleNoteGeoContext);
  const noteGeo = consoleGeos ? (isFoot ? consoleGeos.foot : consoleGeos.hand) : null;

  const handleClick = useCallback((e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    // If an alt-drag just ended, skip the click
    if (e.altKey) return;
    const store = useEditorStore.getState();
    const tool = store.toolMode;

    // Eraser tool: click to delete
    if (tool === 'eraser') {
      store.removeNotes([index]);
      return;
    }

    // Ctrl+click: delete
    if (e.ctrlKey || e.metaKey) {
      store.removeNotes([index]);
      return;
    }

    // Shift+click on note with a different note type: change its color/type
    if (e.shiftKey && (tool === 'note_left' || tool === 'note_right' || tool === 'note_onehand' || tool === 'note_bothhands')) {
      const mapping = TOOL_TO_NOTE[tool];
      if (mapping) {
        store.updateNote(index, { note_type: mapping.note_type, hand: mapping.hand });
      }
      return;
    }

    // Normal click: select
    selectNote(index, e.shiftKey);
  }, [index, selectNote]);

  // Double-click: travel the playhead to this note so it lands on the trigger
  // line (at grid level) — a quick "go to this note" to line it up for editing.
  const handleDoubleClick = useCallback((e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    const store = useEditorStore.getState();
    // note.time_ms is chart-relative; the audio time adds the offset (ms).
    const targetTime = Math.max(0, note.time_ms / 1000 + (store.beatmap?.offset || 0) / 1000);
    store.setCurrentTime(targetTime);
    if (store.playback.playing) seekAudio(targetTime);
  }, [note.time_ms]);

  // Right-click on a note deletes it. (Right-drag on empty space is the loop
  // tool; the _noteRightClick flag tells the canvas loop handler to skip this
  // event so it doesn't also clear the loop.)
  const handleContextMenu = useCallback((e: ThreeEvent<MouseEvent>) => {
    e.nativeEvent.preventDefault();
  }, []);

  // Alt+pointerDown: start free XY drag. Right-pointerDown: delete the note.
  const handlePointerDown = useCallback((e: ThreeEvent<PointerEvent>) => {
    if (e.button === 2) {
      e.stopPropagation();
      _noteRightClick = true;
      requestAnimationFrame(() => { _noteRightClick = false; });
      useEditorStore.getState().removeNotes([index]);
      return;
    }
    if (!e.altKey) return;
    e.stopPropagation();

    _ctrlDrag.active = true;
    _ctrlDrag.noteIndex = index;
    _ctrlDrag.isRailNode = false;
    _ctrlDrag.railNodeIndex = -1;
    _ctrlDrag.startMouseX = e.nativeEvent.clientX;
    _ctrlDrag.startMouseY = e.nativeEvent.clientY;
    _ctrlDrag.startPos = [...note.position] as [number, number, number];
    _ctrlDrag.historyStarted = false;
    document.body.style.cursor = 'move';
    useEditorStore.getState().setGizmoDragAxis('x'); // signal drag active (hides preview)

    window.addEventListener('pointermove', _ctrlDragMove);
    window.addEventListener('pointerup', _ctrlDragUp);
  }, [index, note.position]);

  // Cleanup ctrl-drag listeners on unmount
  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', _ctrlDragMove);
      window.removeEventListener('pointerup', _ctrlDragUp);
    };
  }, []);

  const color = (heatmapEnabled && intensityData && intensityData.noteColors[index])
    ? intensityData.noteColors[index]
    : noteColors[note.note_type] || '#ffffff';

  // Stem aura: tint emissive toward dominant stem color when available
  const stemKey = (note as unknown as Record<string, unknown>).dominant_stem as string | undefined;
  const stemAuraColor = stemKey ? STEM_AURA_COLORS[stemKey] : null;
  const emissiveColor = (stemAuraColor && stemKey !== 'mixed') ? stemAuraColor : color;

  // Map note position into the tunnel (X/Y are stable, Z updated imperatively)
  const x = note.position[0] * GRID_SCALE * 10;
  const y = note.position[1] * GRID_SCALE * 10 + NOTE_Y_OFFSET;

  const meshRef = useRef<THREE.Mesh>(null!);
  // Memoized material (shared program via customProgramCacheKey; fresnel rim is
  // folded in — no overlay mesh). Rebuilt only when the relevant settings
  // change; per-frame opacity/emissive mutations happen in useFrame below.
  const mat = useMemo(() => makeNoteMaterial({
    color,
    emissive: emissiveColor,
    metalness: _gp.noteMetalness,
    roughness: _gp.noteRoughness,
    fresnel: (_gp.noteFresnelEnabled && !isFoot)
      ? { color: _gp.noteFresnelColor, intensity: _gp.noteFresnelIntensity, power: _gp.noteFresnelPower }
      : null,
  }), [color, emissiveColor, _gp.noteMetalness, _gp.noteRoughness, _gp.noteFresnelEnabled,
       _gp.noteFresnelColor, _gp.noteFresnelIntensity, _gp.noteFresnelPower, isFoot]);
  useEffect(() => () => { mat.dispose(); }, [mat]);
  // Note FX animation state (performance.now() timestamps; 0 = idle):
  const spawnStartRef = useRef(0);   // elastic grow-in when first placed
  const spawnBirthRef = useRef(0);   // last placement stamp consumed
  const pulseStartRef = useRef(0);   // grid-alignment bump when the note is on-grid
  const prevOnGridRef = useRef(false); // was the note on-grid last frame (pop edge-trigger)
  useFrame(() => {
    const store = useEditorStore.getState();
    const rawBeat = chartBeat();
    const relZ = (note.time_beats - rawBeat) * BEAT_SPACING;
    const nowMs = (typeof performance !== 'undefined' ? performance.now() : Date.now());

    // ── Spawn "pop": a freshly placed note grows in with an elastic overshoot.
    const birth = placedAt(noteKey(note.time_ms, note.position[0], note.position[1]));
    if (birth && birth !== spawnBirthRef.current && (nowMs - birth) < 600) {
      spawnBirthRef.current = birth;
      spawnStartRef.current = birth;
    }

    // ── On-grid detection — the SAME idea as the move-handles gizmo: the note
    //    is "on the grid" while its centre is within ~its own radius of the
    //    playhead beat. The pop fires once when it ENTERS that zone (from either
    //    side); parking on the grid doesn't re-fire (you must leave and return),
    //    so back-and-forth jitter on the line no longer spams pops.
    // Tolerance is tied to the STEP size (≈ half a step) so the pop works at
    // every step — including very fine ones (1/64): you land within tolerance
    // when the playhead reaches the note's step, and the next step takes you
    // back outside it, re-arming the pop. (A fixed tolerance was wider than a
    // fine step, so you never left the zone and it never re-fired.)
    const gridTol = Math.max(0.006, store.scrollStep * 0.55);
    const onGrid = Math.abs(note.time_beats - rawBeat) < gridTol;
    if (onGrid && !prevOnGridRef.current) pulseStartRef.current = nowMs;
    prevOnGridRef.current = onGrid;

    // Colour stays at 100% while the note is in FRONT of, or ON, the grid; it
    // only drops to 50% once the playhead has passed it AND it's off the grid.
    const behind = note.time_beats < rawBeat;
    const dimmed = behind && !onGrid;
    const faded = relZ < -BEAT_SPACING;   // far past → configured past-opacity

    // Compose the spawn grow-in + grid-alignment pulse (both 1 = idle).
    let spawnFactor = 1;
    if (spawnStartRef.current) {
      const dt = (nowMs - spawnStartRef.current) / 1000;
      if (dt < 0.45) spawnFactor = easeOutElastic(dt / 0.45);
      else spawnStartRef.current = 0;
    }
    let pulseFactor = 1;
    let pulseGlow = 0; // 0..1 envelope, drives a brief emissive flash too
    if (pulseStartRef.current) {
      const dt = (nowMs - pulseStartRef.current) / 1000;
      const PD = 0.16;
      if (dt < PD) {
        pulseGlow = Math.sin(Math.PI * (dt / PD));
        pulseFactor = 1 + 0.55 * pulseGlow;   // peak ≈ ×1.55 as it touches the grid
      } else pulseStartRef.current = 0;
    }

    // Keep Z position in sync with current BEAT_SPACING (may change at runtime)
    const mesh = meshRef.current;
    if (mesh) {
      mesh.position.z = -note.time_beats * BEAT_SPACING;

      const vis = relZ >= -VISIBLE_BEATS_BEHIND * BEAT_SPACING &&
        relZ <= VISIBLE_BEATS_AHEAD * BEAT_SPACING;
      mesh.visible = vis;
      // Hide aura ring once the note is behind the playhead
      mesh.children.forEach(child => {
        if ((child as THREE.Mesh).userData?.isAura) {
          child.visible = !behind;
        }
      });

      // Selection no longer inflates the note (that read as a thick ring); a
      // thin SelectionRing marks it instead, so keep its true size here.
      // Every geometry is a shared UNIT shape (or the game note normalized to
      // ~2 units), so noteSize is applied via mesh scale for all of them.
      const baseScale = noteSize;
      mesh.scale.setScalar(baseScale * spawnFactor * pulseFactor);
    }
    const m = mat;
    if (m) {
      const isDragging = store.draggingNoteIndex === index;
      // 100% while in front of / on the grid; 20% once it's behind and off the
      // grid; configured past-opacity once it's a full beat behind.
      m.transparent = isDragging || faded || dimmed;
      m.opacity = isDragging ? 0.4 + 0.3 * Math.sin(Date.now() * 0.005)
        : faded ? noteOpacityPast
        : dimmed ? 0.2
        : 1.0;
      const baseEmissive = isDragging ? 1.5 : selected ? 1.0 : faded ? noteEmissivePast : (stemAuraColor && stemKey !== 'mixed') ? noteEmissiveStemAura : noteEmissiveNormal;
      m.emissiveIntensity = baseEmissive + pulseGlow * 2.2; // flash brighter on snap
    }
  });

  return (
    <mesh
      ref={meshRef}
      geometry={noteGeo ?? (isFoot ? UNIT_FOOT_BOX : UNIT_NOTE_SPHERE)}
      material={mat}
      rotation={noteGeo ? CONSOLE_NOTE_ROT : undefined}
      position={[x, y, -note.time_beats * BEAT_SPACING]}
      scale={noteSize}
      onClick={handleClick}
      onDoubleClick={handleDoubleClick}
      onContextMenu={handleContextMenu}
      onPointerDown={handlePointerDown}
    >
      {/* Children live in UNIT space (surface radius 1) — the parent mesh's
          scale={noteSize} sizes them, so absolute offsets divide by noteSize. */}
      {/* Small white sphere floating above selected notes (matches Flow). */}
      {selected && (
        <mesh position={[0, 1 + 0.12 / noteSize, 0]} raycast={() => null}>
          <sphereGeometry args={[0.32, 12, 8]} />
          <meshBasicMaterial color="#FFFFFF" toneMapped={false} />
        </mesh>
      )}
      {/* Thin selection ring with a small gap (unit space, inherits scale). */}
      {selected && <SelectionRing />}
      {/* Stem aura ring — shows dominant stem influence (hidden when past playhead via useFrame) */}
      {stemAuraColor && stemKey !== 'mixed' && (
        <mesh scale={1.8} userData={{ isAura: true }}>
          <ringGeometry args={[0.9, 1.4, 24]} />
          <meshBasicMaterial
            color={stemAuraColor}
            transparent
            opacity={0.25}
            side={2}
          />
        </mesh>
      )}
    </mesh>
  );
}

// ── Rail Renderer ───────────────────────────────────────────────────────────

function RailLine({ note, index, bpm }: { note: Note; index: number; bpm: number }) {
  const _gp2 = useEditorStore(s => s.gameplaySettings);
  const railWidth = _gp2.railWidth;
  const noteColors = useMemo(() => getNoteColorsFromSettings(_gp2), [_gp2.noteColorLeft, _gp2.noteColorRight, _gp2.noteColorOneHand, _gp2.noteColorBothHands]);
  const njs = useEditorStore(s => s.noteJumpSpeed);
  const railOpacity = useEditorStore(s => s.interfaceSettings.railOpacity);
  const color = noteColors[note.note_type] || '#ffffff';

  // Build a tube along the CatmullRom rail curve so we can give it the same
  // animated sweep-glow as the Flow editor's trails (a moving bright highlight
  // running along the rail). Recompute when note data / NJS / width changes.
  const tube = useMemo(() => {
    if (!note.has_rail || !note.rail_nodes || note.rail_nodes.length < 1) return null;

    const rawPoints: THREE.Vector3[] = [
      new THREE.Vector3(
        note.position[0] * GRID_SCALE * 10,
        note.position[1] * GRID_SCALE * 10 + NOTE_Y_OFFSET,
        -note.time_beats * njs,
      ),
    ];
    let endBeats = note.time_beats;
    for (const node of note.rail_nodes) {
      const nodeTimeMs = node[2] / 0.02;
      const nodeBeats = (nodeTimeMs / 1000.0) * (bpm / 60.0);
      endBeats = nodeBeats;
      rawPoints.push(new THREE.Vector3(
        node[0] * GRID_SCALE * 10,
        node[1] * GRID_SCALE * 10 + NOTE_Y_OFFSET,
        -nodeBeats * njs,
      ));
    }
    if (rawPoints.length < 2) return null;

    const curve = new THREE.CatmullRomCurve3(rawPoints, false, 'catmullrom', 0.5);
    const tubularSegments = Math.max(rawPoints.length * 12, 32);
    const radius = Math.max(0.01, railWidth * 0.004);
    const geometry = new THREE.TubeGeometry(curve, tubularSegments, radius, 8, false);
    return { geometry, durBeats: Math.max(0.05, endBeats - note.time_beats) };
  }, [note, bpm, njs, railWidth]);

  // Sweep-glow shader (mirrors Flow's TrailTube): base colour + a moving band +
  // bright white core. TubeGeometry UV.x runs along the rail length.
  const mat = useMemo(() => new THREE.ShaderMaterial({
    transparent: true,
    depthWrite: false,
    uniforms: {
      uColor: { value: new THREE.Color(color) },
      uOpacity: { value: railOpacity },
      uTime: { value: 0 },
      uPlaying: { value: 0 },
      uProgress: { value: -1 },
    },
    vertexShader: /* glsl */`
      varying vec2 vUv;
      void main() { vUv = uv; gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0); }
    `,
    fragmentShader: /* glsl */`
      uniform vec3 uColor; uniform float uOpacity, uTime, uPlaying, uProgress;
      varying vec2 vUv;
      void main() {
        float len = vUv.x;                                   // along the rail
        float posN = (uPlaying > 0.5) ? uProgress : mod(uTime * 0.40, 1.0);
        if (posN < 0.0 || posN > 1.0) posN = -99.0;
        float d = abs(len - posN); d = min(d, 1.0 - d);
        float band = exp(-d * d * 55.0);
        float core = exp(-d * d * 380.0);
        vec3 col = uColor * 0.72;
        col += uColor * band * 0.6;
        col += vec3(1.0) * core * 0.9;
        gl_FragColor = vec4(col, uOpacity * (0.78 + 0.22 * band));
      }
    `,
  }), [color, railOpacity]);

  useFrame(({ clock }) => {
    if (!tube) return;
    mat.uniforms.uTime.value = clock.elapsedTime;
    const st = useEditorStore.getState();
    const playing = st.playback.playing;
    mat.uniforms.uPlaying.value = playing ? 1 : 0;
    if (playing) {
      const rawBeat = chartBeat();
      mat.uniforms.uProgress.value = (rawBeat - note.time_beats) / tube.durBeats;
    }
  });

  // Dispose the tube geometry on unmount / rebuild.
  useEffect(() => () => { tube?.geometry.dispose(); }, [tube]);

  if (!tube) return null;

  // Right-click on the rail: remove just the rail (keep the head note).
  const handleContextMenu = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    e.nativeEvent.preventDefault();
    useEditorStore.getState().updateNote(index, { has_rail: false, rail_nodes: [] });
  };

  // frustumCulled off: the rail is a long tube, so don't cull it the moment its
  // bounding-sphere centre leaves the view (head past the trigger) while the
  // tail is still visible.
  // renderOrder 1: draw the rail AFTER the depth-writing walls / decor (which sit
  // at the default order 0). Its bounding-sphere centroid otherwise sorts it
  // ahead of a nearer wall in the transparent pass, so the wall's translucent
  // fill blended OVER it and the bright tube showed through. Drawing last lets the
  // wall's depth occlude the part of the rail that's actually behind it.
  return <mesh geometry={tube.geometry} material={mat} renderOrder={1} frustumCulled={false} onContextMenu={handleContextMenu} />;
}

// ── Rail Node Mesh (visible control-point sphere for each rail node) ────────

function RailNodeMesh({
  note, noteIndex, nodeIndex, node, bpm,
}: {
  note: Note; noteIndex: number; nodeIndex: number;
  node: [number, number, number]; bpm: number;
}) {
  const _gp3 = useEditorStore(s => s.gameplaySettings);
  const railNodeSize = _gp3.railNodeSize;
  const noteColors = useMemo(() => getNoteColorsFromSettings(_gp3), [_gp3.noteColorLeft, _gp3.noteColorRight, _gp3.noteColorOneHand, _gp3.noteColorBothHands]);
  const selectedRailNode = useEditorStore(s => s.selectedRailNode);

  const color = noteColors[note.note_type] || '#ffffff';
  const isSelected = selectedRailNode?.noteIndex === noteIndex && selectedRailNode?.nodeIndex === nodeIndex;

  // Decode time from node[2]: Z = timeMs * 0.02, so timeMs = Z / 0.02
  const nodeTimeMs = node[2] / 0.02;
  const nodeBeats = (nodeTimeMs / 1000.0) * (bpm / 60.0);

  const x = node[0] * GRID_SCALE * 10;
  const y = node[1] * GRID_SCALE * 10 + NOTE_Y_OFFSET;

  const meshRef = useRef<THREE.Mesh>(null!);
  const pulseStartRef = useRef(0);
  const prevOnGridRef = useRef(false);

  useFrame(() => {
    const store = useEditorStore.getState();
    const rawBeat = chartBeat();
    const relZ = (nodeBeats - rawBeat) * BEAT_SPACING;
    const mesh = meshRef.current;
    if (!mesh) return;
    mesh.position.z = -nodeBeats * BEAT_SPACING;
    // Hide the rail-node edit dots during playback — they're an editing aid only.
    mesh.visible = !store.playback.playing &&
      relZ >= -VISIBLE_BEATS_BEHIND * BEAT_SPACING &&
      relZ <= VISIBLE_BEATS_AHEAD * BEAT_SPACING;

    // Grid-alignment pop — same trigger as the note pop.
    const nowMs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const gridTol = Math.max(0.006, store.scrollStep * 0.55);
    const onGrid = Math.abs(nodeBeats - rawBeat) < gridTol;
    if (onGrid && !prevOnGridRef.current) pulseStartRef.current = nowMs;
    prevOnGridRef.current = onGrid;
    let pulseFactor = 1;
    if (pulseStartRef.current) {
      const dt = (nowMs - pulseStartRef.current) / 1000;
      const PD = 0.16;
      if (dt < PD) pulseFactor = 1 + 0.55 * Math.sin(Math.PI * (dt / PD));
      else pulseStartRef.current = 0;
    }
    // Shared UNIT sphere — bake railNodeSize into the mesh scale.
    mesh.scale.setScalar(railNodeSize * (isSelected ? 1.4 : 1) * pulseFactor);
  });

  const handleClick = useCallback((e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    // If an alt-drag just ended, skip the click
    if (e.altKey) return;
    const store = useEditorStore.getState();

    // Eraser tool: delete this rail node
    if (store.toolMode === 'eraser') {
      store.removeRailNode(noteIndex, nodeIndex);
      return;
    }

    // Ctrl+click: delete this rail node
    if (e.ctrlKey || e.metaKey) {
      store.removeRailNode(noteIndex, nodeIndex);
      return;
    }

    // Normal click: select this rail node
    store.setSelectedRailNode(noteIndex, nodeIndex);
  }, [noteIndex, nodeIndex]);

  // Alt+pointerDown: start free XY drag
  const handlePointerDown = useCallback((e: ThreeEvent<PointerEvent>) => {
    if (!e.altKey) return;
    e.stopPropagation();

    _ctrlDrag.active = true;
    _ctrlDrag.noteIndex = noteIndex;
    _ctrlDrag.isRailNode = true;
    _ctrlDrag.railNodeIndex = nodeIndex;
    _ctrlDrag.startMouseX = e.nativeEvent.clientX;
    _ctrlDrag.startMouseY = e.nativeEvent.clientY;
    _ctrlDrag.startPos = [...node] as [number, number, number];
    _ctrlDrag.historyStarted = false;
    document.body.style.cursor = 'move';

    window.addEventListener('pointermove', _ctrlDragMove);
    window.addEventListener('pointerup', _ctrlDragUp);
  }, [noteIndex, nodeIndex, node]);

  // Cleanup ctrl-drag listeners on unmount
  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', _ctrlDragMove);
      window.removeEventListener('pointerup', _ctrlDragUp);
    };
  }, []);

  return (
    <mesh
      ref={meshRef}
      geometry={UNIT_NODE_SPHERE}
      position={[x, y, -nodeBeats * BEAT_SPACING]}
      scale={railNodeSize}
      onClick={handleClick}
      onPointerDown={handlePointerDown}
    >
      <meshStandardMaterial
        color={color}
        emissive={color}
        emissiveIntensity={isSelected ? 1.2 : 0.4}
        metalness={0.3}
        roughness={0.3}
        transparent
        opacity={0.75}
      />
      {/* Small ring to distinguish from head notes (unit space, inherits scale) */}
      <mesh scale={1.5}>
        <ringGeometry args={[0.7, 1.0, 12]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={isSelected ? 0.6 : 0.3}
          side={THREE.DoubleSide}
          depthTest={false}
          fog={false}
        />
      </mesh>
      {/* Selected: the same thin white SelectionRing the notes use. */}
      {isSelected && <SelectionRing />}
    </mesh>
  );
}

// ── Crouch/Jump Markers ─────────────────────────────────────────────────────

function TimeMarker({ timeMs, bpm, color, label }: {
  timeMs: number; bpm: number; color: string; label: string;
}) {
  const beats = (timeMs / 1000.0) * (bpm / 60.0);
  const groupRef = useRef<THREE.Group>(null!);

  // Keep Z in sync with runtime BEAT_SPACING (updated by BeatScrollGroup every frame)
  useFrame(() => {
    if (groupRef.current) {
      groupRef.current.position.z = -beats * BEAT_SPACING;
    }
  });

  return (
    <group ref={groupRef} position={[0, 0, 0]}>
      {label === 'CROUCH' ? (
        // Duck-bar: a solid horizontal bar spanning the lane (you crouch under
        // it) instead of a thin line — matches the legacy editor's crouch wall.
        <mesh>
          <boxGeometry args={[TUNNEL_WIDTH, 0.28, 0.32]} />
          <meshStandardMaterial color={color} transparent opacity={0.5} emissive={color} emissiveIntensity={0.4} />
        </mesh>
      ) : (
        <Line
          points={[[-TUNNEL_WIDTH / 2, 0, 0], [TUNNEL_WIDTH / 2, 0, 0]]}
          color={color}
          lineWidth={2}
          transparent
          opacity={0.4}
        />
      )}
      <Text position={[TUNNEL_WIDTH / 2 + 0.3, 0.15, 0]} fontSize={0.15} color={color} anchorX="left">
        {label}
      </Text>
    </group>
  );
}

// (Bookmark marker is defined below, after the flash marker — it mirrors it.)

// ── Flash Event Marker (light bulb icon on LEFT side) ───────────────────────

// Set briefly after a drag that ends on empty space so the trailing Canvas
// onPointerMissed doesn't clear the note selection we want to keep.
let _suppressMissedClear = false;

// Drag state for the flash marker's move arrow (slide the light in time, snapped).
const _flashDrag = { active: false, startMouseY: 0, startBeat: 0, lastBeat: 0 };
function _flashDragMove(e: PointerEvent) {
  if (!_flashDrag.active) return;
  const store = useEditorStore.getState();
  const step = Math.max(0.0001, store.scrollStep);
  // Drag UP = later beat (forward, up the track); snap in whole grid steps.
  const steps = Math.round(-(e.clientY - _flashDrag.startMouseY) / 30);
  const newBeat = Math.max(0, _flashDrag.startBeat + steps * step);
  if (Math.abs(newBeat - _flashDrag.lastBeat) > 1e-6) {
    store.moveFlashEvent(_flashDrag.lastBeat, newBeat);
    _flashDrag.lastBeat = newBeat;
  }
}
function _flashDragUp() {
  if (!_flashDrag.active) return;
  _flashDrag.active = false;
  // Keep the current selection: the pointer-up can land on empty space and would
  // otherwise trigger Canvas onPointerMissed → clearSelection.
  _suppressMissedClear = true;
  requestAnimationFrame(() => { _suppressMissedClear = false; });
  window.removeEventListener('pointermove', _flashDragMove);
  window.removeEventListener('pointerup', _flashDragUp);
}

// Drag state for the bookmark marker's move arrow (slide the bookmark in time).
const _bookmarkDrag = { active: false, startMouseY: 0, startBeat: 0, lastBeat: 0 };
function _bookmarkDragMove(e: PointerEvent) {
  if (!_bookmarkDrag.active) return;
  const store = useEditorStore.getState();
  const step = Math.max(0.0001, store.scrollStep);
  const steps = Math.round(-(e.clientY - _bookmarkDrag.startMouseY) / 30);
  const newBeat = Math.max(0, _bookmarkDrag.startBeat + steps * step);
  if (Math.abs(newBeat - _bookmarkDrag.lastBeat) > 1e-6) {
    store.moveBookmark(_bookmarkDrag.lastBeat, newBeat);
    _bookmarkDrag.lastBeat = newBeat;
  }
}
function _bookmarkDragUp() {
  if (!_bookmarkDrag.active) return;
  _bookmarkDrag.active = false;
  _suppressMissedClear = true;
  requestAnimationFrame(() => { _suppressMissedClear = false; });
  window.removeEventListener('pointermove', _bookmarkDragMove);
  window.removeEventListener('pointerup', _bookmarkDragUp);
}

// Circular bulb badge for the flash pin — pure 3D (billboarded) instead of an
// <Html> overlay, so it (a) shrinks with perspective when far, (b) fades into
// the background by distance, and (c) can't trigger drei <Html> remount crashes.
const _badgeWorld = new THREE.Vector3();
function FlashBulbBadge({ active, onClick }: { active: boolean; onClick: (e: ThreeEvent<MouseEvent>) => void }) {
  const grp = useRef<THREE.Group>(null!);
  useFrame((state) => {
    const g = grp.current;
    if (!g) return;
    g.quaternion.copy(state.camera.quaternion); // billboard toward the camera
    const d = g.getWorldPosition(_badgeWorld).distanceTo(state.camera.position);
    const fade = THREE.MathUtils.clamp(1.25 - d / 55, 0.14, 1); // farther → dimmer
    g.traverse(o => {
      const m = (o as THREE.Mesh).material as THREE.MeshBasicMaterial | undefined;
      if (m && (m as THREE.Material).isMaterial) {
        m.transparent = true;
        m.opacity = fade * (m.userData?.baseOp ?? 1);
      }
    });
  });
  const ink = active ? '#000000' : '#FFFFFF';
  return (
    <group ref={grp} scale={0.5}>
      {/* bg disc + click target */}
      <mesh onClick={onClick}
        onPointerOver={() => { document.body.style.cursor = 'pointer'; }}
        onPointerOut={() => { document.body.style.cursor = ''; }}
        renderOrder={8}>
        <circleGeometry args={[0.26, 32]} />
        <meshBasicMaterial color={active ? '#FFFFFF' : '#0A0C16'} transparent depthTest={false} depthWrite={false} toneMapped={false} userData={{ baseOp: active ? 0.96 : 0.84 }} />
      </mesh>
      {/* badge ring */}
      <mesh renderOrder={9}>
        <ringGeometry args={[0.225, 0.265, 36]} />
        <meshBasicMaterial color="#FFFFFF" transparent side={THREE.DoubleSide} depthTest={false} depthWrite={false} toneMapped={false} userData={{ baseOp: 1 }} />
      </mesh>
      {/* bulb glass outline */}
      <mesh position={[0, 0.03, 0]} renderOrder={9}>
        <ringGeometry args={[0.075, 0.095, 20]} />
        <meshBasicMaterial color={ink} transparent side={THREE.DoubleSide} depthTest={false} depthWrite={false} toneMapped={false} userData={{ baseOp: 1 }} />
      </mesh>
      {/* bulb base (two short bars) */}
      <mesh position={[0, -0.08, 0]} renderOrder={9}>
        <boxGeometry args={[0.1, 0.02, 0.001]} />
        <meshBasicMaterial color={ink} transparent depthTest={false} depthWrite={false} toneMapped={false} userData={{ baseOp: 1 }} />
      </mesh>
      <mesh position={[0, -0.115, 0]} renderOrder={9}>
        <boxGeometry args={[0.07, 0.02, 0.001]} />
        <meshBasicMaterial color={ink} transparent depthTest={false} depthWrite={false} toneMapped={false} userData={{ baseOp: 1 }} />
      </mesh>
    </group>
  );
}

// ── Add/remove "ray burst" ──────────────────────────────────────────────────
// A one-shot sunburst of short white rays that shoots out from a marker badge
// when it's added or deleted (lights on the left, bookmarks on the right).
// Module-level list + a tiny pub/sub so any code path can fire one; the layer
// (rendered inside BeatScrollGroup) animates and removes them in useFrame.
interface RayBurst { id: number; beat: number; start: number; x: number; y: number; }
let _rayBurstSeq = 0;
const _rayBursts: RayBurst[] = [];
const _rayBurstSubs = new Set<() => void>();
function _nowSec() { return (typeof performance !== 'undefined' ? performance.now() : Date.now()) / 1000; }
function spawnRayBurst(beat: number, x: number, y: number) {
  _rayBursts.push({ id: ++_rayBurstSeq, beat, start: _nowSec(), x, y });
  _rayBurstSubs.forEach(fn => fn());
}
const _hwForBurst = TUNNEL_WIDTH / 2;
function spawnLightBurst(beat: number) { spawnRayBurst(beat, -_hwForBurst - 0.5, 0.65); }
function spawnBookmarkBurst(beat: number) { spawnRayBurst(beat, _hwForBurst + 0.5, 0.65); }
function _dropRayBurst(id: number) {
  const i = _rayBursts.findIndex(b => b.id === id);
  if (i >= 0) { _rayBursts.splice(i, 1); _rayBurstSubs.forEach(fn => fn()); }
}
const RAY_COUNT = 12;
const RAY_ANGLES = Array.from({ length: RAY_COUNT }, (_, i) => (i / RAY_COUNT) * Math.PI * 2);
const RAY_DUR = 0.22; // twice as fast as before

function RayBurstMesh({ burst }: { burst: RayBurst }) {
  const grp = useRef<THREE.Group>(null!);
  useFrame((state) => {
    const g = grp.current;
    if (!g) return;
    g.quaternion.copy(state.camera.quaternion); // billboard
    const p = Math.min(1, (_nowSec() - burst.start) / RAY_DUR);
    if (p >= 1) { _dropRayBurst(burst.id); return; }
    const ease = 1 - Math.pow(1 - p, 3);            // easeOutCubic — fast then settle
    g.scale.setScalar(ease * 1.5);                  // shoot outward FROM the centre (starts at 0)
    const op = Math.min(1, (1 - p) * 1.5);          // hold bright, then fade
    g.traverse(o => {
      const m = (o as THREE.Mesh).material as THREE.MeshBasicMaterial | undefined;
      if (m && (m as THREE.Material).isMaterial) m.opacity = op;
    });
  });
  return (
    <group position={[burst.x, burst.y, -burst.beat * BEAT_SPACING]}>
      <group ref={grp}>
        {RAY_ANGLES.map((a, i) => (
          <mesh key={i} position={[Math.cos(a) * 0.44, Math.sin(a) * 0.44, 0]} rotation={[0, 0, a]} renderOrder={10}>
            <boxGeometry args={[0.14, 0.01, 0.001]} />
            <meshBasicMaterial color="#FFFFFF" transparent opacity={1} depthTest={false} depthWrite={false} toneMapped={false} />
          </mesh>
        ))}
      </group>
    </group>
  );
}

function LightBurstLayer() {
  const [, setTick] = useState(0);
  useEffect(() => {
    const fn = () => setTick(c => c + 1);
    _rayBurstSubs.add(fn);
    return () => { _rayBurstSubs.delete(fn); };
  }, []);
  if (_rayBursts.length === 0) return null;
  return <>{_rayBursts.map(b => <RayBurstMesh key={b.id} burst={b} />)}</>;
}

function FlashEventMarker({ event }: { event: FlashEvent }) {
  const groupRef = useRef<THREE.Group>(null!);
  const badgeWrapRef = useRef<THREE.Group>(null!);
  const toggleFlashSelection = useEditorStore(s => s.toggleFlashSelection);
  // "Selected" = part of the multi-selection (the popup edits all selected at once).
  const isEditing = useEditorStore(
    s => s.selectedFlashBeats.some(b => Math.abs(b - event.time_beats) < 0.01),
  );
  const bpm = useEditorStore(s => s.beatmap?.bpm || 120);
  const [arrowHover, setArrowHover] = useState(false);
  // The move arrow only appears while the pointer is over the light. A short
  // grace period on leave keeps it up while you travel from the bulb down to the
  // arrow (they don't touch), so it doesn't blink away before you can grab it.
  const [hovered, setHovered] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showHover = () => { if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; } setHovered(true); };
  const hideHoverSoon = () => { if (hideTimer.current) clearTimeout(hideTimer.current); hideTimer.current = setTimeout(() => setHovered(false), 160); };
  useEffect(() => () => { if (hideTimer.current) clearTimeout(hideTimer.current); }, []);

  useFrame(() => {
    if (groupRef.current) {
      groupRef.current.position.z = -event.time_beats * BEAT_SPACING;
    }
    // Pop the badge (ring + bulb) as the light crosses the trigger line — the
    // bump peaks right at the trigger (rawBeat == this event's beat) and eases
    // back to normal within ~0.45 beat either side.
    const bw = badgeWrapRef.current;
    if (bw) {
      const st = useEditorStore.getState();
      const rawBeat = chartBeat();
      const prox = Math.max(0, 1 - Math.abs(rawBeat - event.time_beats) / 0.45);
      const ease = prox * prox * (3 - 2 * prox); // smoothstep
      bw.scale.setScalar(1 + 0.45 * ease);
    }
  });

  const hw = TUNNEL_WIDTH / 2;
  const repeat = event.repeat ?? 1;
  // Everything is white. Pin (L shape): a small dot sits on the track (the
  // "timeline"), a foot runs LEFT off the track, then up to the bulb badge.
  const lineColor = '#FFFFFF';
  const badgeX = -hw - 0.5;   // stem + badge sit just left of the track
  const touchX = -hw + 0.1;   // track edge reference
  const dotX = touchX - 0.3;  // dot sits on the same line as the placement rings
  const badgeY = 0.65;        // short stem: badge sits low, near the timeline

  const openEditor = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    // Shift / Ctrl / Cmd-click adds to (or removes from) the selection so you can
    // edit several lights together; a plain click selects just this one.
    const additive = e.shiftKey || e.ctrlKey || e.metaKey;
    toggleFlashSelection(event.time_beats, additive);
  };

  // Right-click deletes this light (from the bulb or anywhere in its area).
  const deleteThisLight = () => {
    _noteRightClick = true; // tell the canvas loop handler to skip this
    requestAnimationFrame(() => { _noteRightClick = false; });
    spawnLightBurst(event.time_beats);
    useEditorStore.getState().removeFlashEvent(event.time_beats);
  };

  // Grab the arrow to slide the light forward/back in time (snapped). Keep the
  // normal cursor — the hover brightness is the affordance.
  const startMove = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    _suppressMissedClear = true; // don't let the grab clear the note selection
    _flashDrag.active = true;
    _flashDrag.startMouseY = e.nativeEvent.clientY;
    _flashDrag.startBeat = event.time_beats;
    _flashDrag.lastBeat = event.time_beats;
    window.addEventListener('pointermove', _flashDragMove);
    window.addEventListener('pointerup', _flashDragUp);
  };
  const arrowColor = arrowHover ? '#AACDFF' : '#4488FF';

  // Strobe guide: one sphere per repetition, spaced by the frequency and
  // receding into the scene — the last sphere shows where the strobe ends.
  const dur = event.duration ?? (((event.fadeInTime || 0) + (event.fadeOutTime || 0)) || 0.4);
  const freq = event.frequency ?? (1 / Math.max(0.05, dur));
  const stepZ = -(1 / Math.max(0.1, freq)) * (bpm / 60) * BEAT_SPACING; // local z per pulse

  return (
    <group ref={groupRef}>
      {/* Pin (L): badge at top → down the stem → foot → dot (on the ring line). */}
      <Line
        points={[[badgeX, badgeY, 0], [badgeX, 0, 0], [dotX, 0, 0]]}
        color={lineColor}
        lineWidth={1.5}
        transparent
        opacity={isEditing ? 0.9 : 0.6}
      />
      {/* White dot at the foot — sits centred on the placement-ring line. */}
      <mesh position={[dotX, 0, 0]} raycast={() => null}>
        <sphereGeometry args={[0.07, 12, 10]} />
        <meshBasicMaterial color="#FFFFFF" toneMapped={false} />
      </mesh>
      {/* Invisible hover zone over the whole pin (bulb + stem + dot): hovering
          anywhere here reveals the move arrow, and pressing here grabs the light
          to slide it — so the area itself is the drag handle. Left-press starts
          the move (which also suppresses the box-select marquee); it sits behind
          the pin (−z) so it never steals clicks from the bulb or the arrow. */}
      <mesh position={[badgeX + 0.15, 0.4, -0.08]}
        onPointerOver={(e: ThreeEvent<PointerEvent>) => { e.stopPropagation(); showHover(); }}
        onPointerOut={hideHoverSoon}
        onPointerDown={(e: ThreeEvent<PointerEvent>) => {
          if (e.button === 2) { e.stopPropagation(); deleteThisLight(); return; } // right-click deletes
          if (e.button === 0) startMove(e);
        }}
        onContextMenu={(e: ThreeEvent<MouseEvent>) => { e.nativeEvent.preventDefault(); }}>
        <planeGeometry args={[1.0, 1.3]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} depthTest={false} side={THREE.DoubleSide} />
      </mesh>
      {/* Circular bulb badge (3D, billboarded — shrinks + fades with distance).
          Hovering it reveals the move arrow. */}
      <group ref={badgeWrapRef} position={[badgeX, badgeY, 0]}
        onPointerOver={(e: ThreeEvent<PointerEvent>) => { e.stopPropagation(); showHover(); }}
        onPointerOut={hideHoverSoon}
        onPointerDown={(e: ThreeEvent<PointerEvent>) => {
          if (e.button !== 2) return; // right-click deletes this light
          e.stopPropagation();
          deleteThisLight();
        }}
        onContextMenu={(e: ThreeEvent<MouseEvent>) => { e.nativeEvent.preventDefault(); }}>
        <FlashBulbBadge active={isEditing} onClick={openEditor} />
      </group>

      {/* Move arrow — only while hovering the light (the bulb or the arrow), at
          the line intersection (the L corner). Blue (Z axis): drag up/down to
          slide the light forward/back in time, snapped. Double-headed. */}
      {hovered && (
      <group
        position={[badgeX, 0, 0]}
        onPointerDown={startMove}
        onPointerOver={(e: ThreeEvent<PointerEvent>) => { e.stopPropagation(); setArrowHover(true); showHover(); }}
        onPointerOut={() => { setArrowHover(false); hideHoverSoon(); }}
      >
        {/* Sphere knob at the intersection (half the previous size). Lit
            (meshStandardMaterial) so the cones/shaft read as a 3D arrow; a bit
            of emissive keeps it visible in dark stretches of the scene. */}
        <mesh>
          <sphereGeometry args={[0.0425, 14, 12]} />
          <meshStandardMaterial color={arrowColor} emissive={arrowColor} emissiveIntensity={0.35} roughness={0.4} metalness={0.3} toneMapped={false} />
        </mesh>
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.018, 0.018, 0.7, 6]} />
          <meshStandardMaterial color={arrowColor} emissive={arrowColor} emissiveIntensity={0.35} roughness={0.4} metalness={0.3} toneMapped={false} />
        </mesh>
        <mesh position={[0, 0, -0.44]} rotation={[-Math.PI / 2, 0, 0]}>
          <coneGeometry args={[0.07, 0.16, 8]} />
          <meshStandardMaterial color={arrowColor} emissive={arrowColor} emissiveIntensity={0.3} roughness={0.4} metalness={0.3} toneMapped={false} />
        </mesh>
        <mesh position={[0, 0, 0.44]} rotation={[Math.PI / 2, 0, 0]}>
          <coneGeometry args={[0.07, 0.16, 8]} />
          <meshStandardMaterial color={arrowColor} emissive={arrowColor} emissiveIntensity={0.3} roughness={0.4} metalness={0.3} toneMapped={false} />
        </mesh>
        {/* invisible grab area */}
        <mesh rotation={[Math.PI / 2, 0, 0]} raycast={undefined}>
          <cylinderGeometry args={[0.18, 0.18, 1.05, 8]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} depthTest={false} />
        </mesh>
      </group>
      )}

      {/* Strobe guide — line + a sphere per repetition, receding into the scene. */}
      {repeat > 1 && (
        <group position={[badgeX, badgeY, 0]}>
          <Line
            points={[[0, 0, 0], [0, 0, stepZ * (repeat - 1)]]}
            color={lineColor}
            lineWidth={1.5}
            transparent
            opacity={isEditing ? 0.65 : 0.32}
          />
          {Array.from({ length: repeat }, (_, i) => (
            <mesh key={i} position={[0, 0, stepZ * i]}>
              <sphereGeometry args={[0.055, 10, 8]} />
              <meshBasicMaterial color={lineColor} toneMapped={false} transparent opacity={isEditing ? 0.95 : 0.6} />
            </mesh>
          ))}
        </group>
      )}
    </group>
  );
}

// ── Light placement rings ───────────────────────────────────────────────────
// When the Light tool is active (and paused), show rings beside the timeline at
// the next 5 snap slots from the trigger. Click a ring to drop a light on it;
// hover to preview the pin connecting its start to the ring's centre.

function placeFlashAt(beat: number) {
  const store = useEditorStore.getState();
  const fd = store.flashDefaults;
  store.addFlashEvent({
    time_beats: beat, color: fd.color, intensity: fd.intensity,
    fadeInTime: fd.fadeInTime, fadeOutTime: fd.fadeOutTime,
    // A new light is a normal single pulse — strobe is opt-in per light (set
    // Repeat > 1 in the popup), so placing one after a strobe doesn't inherit it.
    duration: fd.duration, repeat: 1, frequency: fd.frequency,
  });
  spawnLightBurst(beat);
  // Don't auto-open the settings popup — click the bulb to edit it.
}

function FlashRing({ beat }: { beat: number }) {
  const ringRef = useRef<THREE.Group>(null!);
  const [hovered, setHovered] = useState(false);
  const hw = TUNNEL_WIDTH / 2;
  // ringX sits clear of the timeline; the placed light's dot uses the same x, so
  // a placed light's sphere ends up centred inside the ring.
  const badgeX = -hw - 0.5, touchX = -hw + 0.1, badgeY = 0.65;
  const ringX = touchX - 0.3;

  // Billboard the ring so it always reads as a clean circle.
  useFrame((state) => { if (ringRef.current) ringRef.current.quaternion.copy(state.camera.quaternion); });

  const place = (e: ThreeEvent<MouseEvent>) => { e.stopPropagation(); placeFlashAt(beat); };
  const over = (e: ThreeEvent<PointerEvent>) => { e.stopPropagation(); setHovered(true); document.body.style.cursor = 'pointer'; };
  const out = () => { setHovered(false); document.body.style.cursor = ''; };

  return (
    <group position={[0, 0, -beat * BEAT_SPACING]}>
      {/* Ring beside the timeline, nudged left of the dot position. */}
      <group ref={ringRef} position={[ringX, 0, 0]}>
        <mesh onClick={place} onPointerOver={over} onPointerOut={out} renderOrder={9}>
          <ringGeometry args={[0.13, 0.17, 28]} />
          <meshBasicMaterial color={hovered ? '#FFFFFF' : '#7FA2C8'} transparent opacity={hovered ? 0.96 : 0.5} side={THREE.DoubleSide} depthTest={false} depthWrite={false} toneMapped={false} />
        </mesh>
        {/* larger invisible hit disc */}
        <mesh onClick={place} onPointerOver={over} onPointerOut={out}>
          <circleGeometry args={[0.22, 20]} />
          <meshBasicMaterial transparent opacity={0} depthTest={false} depthWrite={false} />
        </mesh>
      </group>

      {/* Hover preview: the pin, with its sphere sitting centred in the ring. */}
      {hovered && (
        <>
          <Line points={[[badgeX, badgeY, 0], [badgeX, 0, 0], [ringX, 0, 0]]} color="#FFFFFF" lineWidth={1.5} transparent opacity={0.55} />
          <mesh position={[ringX, 0, 0]} raycast={() => null}>
            <sphereGeometry args={[0.07, 12, 10]} />
            <meshBasicMaterial color="#FFFFFF" transparent opacity={0.85} depthTest={false} depthWrite={false} toneMapped={false} />
          </mesh>
          <group position={[badgeX, badgeY, 0]}>
            <FlashBulbBadge active={false} onClick={place} />
          </group>
        </>
      )}
    </group>
  );
}

function FlashPlacementRings() {
  const toolMode = useEditorStore(s => s.toolMode);
  const playing = useEditorStore(s => s.playback.playing);
  const currentTime = useEditorStore(s => s.playback.currentTime);
  const scrollStep = useEditorStore(s => s.scrollStep);
  const bpm = useEditorStore(s => s.beatmap?.bpm || 120);
  const flashEvents = useEditorStore(s => s.flashEvents);

  if (toolMode !== 'flash_effect' || playing) return null;
  const rawBeat = chartBeat();
  const step = Math.max(0.0001, scrollStep);
  const b0 = Math.max(0, Math.round(rawBeat / step) * step); // snap the trigger to the grid
  // Keep the ring band a roughly constant depth (~SPAN_BEATS) no matter the snap:
  // a finer snap packs more rings into the band, a coarser snap shows fewer. The
  // rings still sit on real snap points and re-space live as you Ctrl + wheel,
  // but they never fly far down the tunnel (coarse) or collapse onto the trigger
  // (fine) — which is what made them look "switched off" while changing the snap.
  const SPAN_BEATS = 4;
  const count = Math.max(2, Math.min(16, Math.round(SPAN_BEATS / step) + 1));
  return (
    <>
      {Array.from({ length: count }, (_, i) => {
        const beat = b0 + i * step;
        if (flashEvents.some(e => Math.abs(e.time_beats - beat) < 0.01)) return null; // a light is already there
        return <FlashRing key={i} beat={beat} />;
      })}
    </>
  );
}

// ── Bookmark Marker (mirror of the light pin, on the RIGHT side) ─────────────
// Same interaction model as the light marker (hover-to-reveal move arrow, drag
// to slide in time, right-click delete, pop at the trigger, ray burst), but it
// lives on the right of the track and shows a bookmark banner instead of a bulb.

const _bmBadgeWorld = new THREE.Vector3();
function BookmarkBadge({ active, onClick }: { active: boolean; onClick: (e: ThreeEvent<MouseEvent>) => void }) {
  const grp = useRef<THREE.Group>(null!);
  // Bookmark banner: a small rounded banner with a V-notch cut into the bottom.
  const shape = useMemo(() => {
    const s = new THREE.Shape();
    const w = 0.062, h = 0.088, notch = 0.034;
    s.moveTo(-w, h); s.lineTo(w, h); s.lineTo(w, -h); s.lineTo(0, -h + notch); s.lineTo(-w, -h); s.closePath();
    return s;
  }, []);
  useFrame((state) => {
    const g = grp.current;
    if (!g) return;
    g.quaternion.copy(state.camera.quaternion);
    const d = g.getWorldPosition(_bmBadgeWorld).distanceTo(state.camera.position);
    const fade = THREE.MathUtils.clamp(1.25 - d / 55, 0.14, 1);
    g.traverse(o => {
      const m = (o as THREE.Mesh).material as THREE.MeshBasicMaterial | undefined;
      if (m && (m as THREE.Material).isMaterial) { m.transparent = true; m.opacity = fade * (m.userData?.baseOp ?? 1); }
    });
  });
  const ink = active ? '#000000' : '#FFFFFF';
  return (
    <group ref={grp} scale={0.5}>
      <mesh onClick={onClick}
        onPointerOver={() => { document.body.style.cursor = 'pointer'; }}
        onPointerOut={() => { document.body.style.cursor = ''; }}
        renderOrder={8}>
        <circleGeometry args={[0.26, 32]} />
        <meshBasicMaterial color={active ? '#FFFFFF' : '#0A0C16'} transparent depthTest={false} depthWrite={false} toneMapped={false} userData={{ baseOp: active ? 0.96 : 0.84 }} />
      </mesh>
      <mesh renderOrder={9}>
        <ringGeometry args={[0.225, 0.265, 36]} />
        <meshBasicMaterial color="#FFFFFF" transparent side={THREE.DoubleSide} depthTest={false} depthWrite={false} toneMapped={false} userData={{ baseOp: 1 }} />
      </mesh>
      <mesh renderOrder={9}>
        <shapeGeometry args={[shape]} />
        <meshBasicMaterial color={ink} transparent side={THREE.DoubleSide} depthTest={false} depthWrite={false} toneMapped={false} userData={{ baseOp: 1 }} />
      </mesh>
    </group>
  );
}

// Rounded-rectangle (pill) THREE.Shape centred at the origin.
function roundedRectShape(w: number, h: number, r: number): THREE.Shape {
  const s = new THREE.Shape();
  const x = -w / 2, y = -h / 2;
  r = Math.min(r, h / 2, w / 2);
  s.moveTo(x + r, y);
  s.lineTo(x + w - r, y);
  s.quadraticCurveTo(x + w, y, x + w, y + r);
  s.lineTo(x + w, y + h - r);
  s.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  s.lineTo(x + r, y + h);
  s.quadraticCurveTo(x, y + h, x, y + h - r);
  s.lineTo(x, y + r);
  s.quadraticCurveTo(x, y, x + r, y);
  return s;
}

// The bookmark name sits inside a rounded pill that auto-sizes to the text width
// (measured via the troika text's blockBounds on sync).
function BookmarkLabel({ name }: { name: string }) {
  // PILL_H matches the badge circle diameter (BookmarkBadge ring outer 0.265 ×
  // scale 0.5 × 2). TEXT_X is pushed right so the pill clears the badge.
  const PILL_H = 0.265, RIM = 0.014, FS = 0.14, PAD_X = 0.12, TEXT_X = 0.34;
  const [tw, setTw] = useState(0.5);
  const pillW = tw + PAD_X * 2;
  // Fully rounded (radius = height/2 → stadium). Styled like the sidebar
  // containers (groupStyle): near-black translucent fill + a faint subtle border.
  const fill = useMemo(() => roundedRectShape(pillW, PILL_H - RIM * 2, (PILL_H - RIM * 2) / 2), [pillW]);
  const border = useMemo(() => roundedRectShape(pillW + RIM * 2, PILL_H, PILL_H / 2), [pillW]);
  const cx = TEXT_X + tw / 2;
  return (
    <>
      <mesh position={[cx, 0, -0.02]} renderOrder={6}>
        <shapeGeometry args={[border]} />
        <meshBasicMaterial color="#8B93A8" transparent opacity={0.2} depthTest={false} depthWrite={false} toneMapped={false} />
      </mesh>
      <mesh position={[cx, 0, -0.01]} renderOrder={7}>
        <shapeGeometry args={[fill]} />
        <meshBasicMaterial color="#080910" transparent opacity={0.72} depthTest={false} depthWrite={false} toneMapped={false} />
      </mesh>
      <Text
        position={[TEXT_X, 0, 0]}
        fontSize={FS}
        color="#FFFFFF"
        anchorX="left"
        anchorY="middle"
        renderOrder={8}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onSync={(t: any) => {
          const bb = t?.textRenderInfo?.blockBounds;
          if (bb) { const w = bb[2] - bb[0]; if (Math.abs(w - tw) > 0.002) setTw(w); }
        }}
      >
        {name}
      </Text>
    </>
  );
}

function BookmarkMarker({ bookmark }: { bookmark: BookmarkEntry }) {
  const groupRef = useRef<THREE.Group>(null!);
  const badgeWrapRef = useRef<THREE.Group>(null!);
  const nameRef = useRef<THREE.Group>(null!);
  const setEditingBookmarkBeat = useEditorStore(s => s.setEditingBookmarkBeat);
  const isEditing = useEditorStore(
    s => s.editingBookmarkBeat != null && Math.abs(s.editingBookmarkBeat - bookmark.beat) < 0.01,
  );
  const [arrowHover, setArrowHover] = useState(false);
  const [hovered, setHovered] = useState(false);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const showHover = () => { if (hideTimer.current) { clearTimeout(hideTimer.current); hideTimer.current = null; } setHovered(true); };
  const hideHoverSoon = () => { if (hideTimer.current) clearTimeout(hideTimer.current); hideTimer.current = setTimeout(() => setHovered(false), 160); };
  useEffect(() => () => { if (hideTimer.current) clearTimeout(hideTimer.current); }, []);

  useFrame((state) => {
    if (groupRef.current) groupRef.current.position.z = -bookmark.beat * BEAT_SPACING;
    if (nameRef.current) nameRef.current.quaternion.copy(state.camera.quaternion); // billboard the label
    const bw = badgeWrapRef.current;
    if (bw) {
      const st = useEditorStore.getState();
      const rawBeat = chartBeat();
      const prox = Math.max(0, 1 - Math.abs(rawBeat - bookmark.beat) / 0.45);
      const ease = prox * prox * (3 - 2 * prox);
      bw.scale.setScalar(1 + 0.45 * ease);
    }
  });

  const hw = TUNNEL_WIDTH / 2;
  const lineColor = '#FFFFFF';
  const badgeX = hw + 0.5;    // stem + badge sit just right of the track
  const touchX = hw - 0.1;    // track edge reference
  const dotX = touchX + 0.3;  // dot sits on the same line as the placement rings
  const badgeY = 0.65;

  const toggleEditor = (e: ThreeEvent<MouseEvent>) => {
    e.stopPropagation();
    setEditingBookmarkBeat(isEditing ? null : bookmark.beat);
  };

  const deleteThisBookmark = () => {
    _noteRightClick = true;
    requestAnimationFrame(() => { _noteRightClick = false; });
    spawnBookmarkBurst(bookmark.beat);
    useEditorStore.getState().removeBookmark(bookmark.beat);
  };

  const startMove = (e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    _suppressMissedClear = true;
    _bookmarkDrag.active = true;
    _bookmarkDrag.startMouseY = e.nativeEvent.clientY;
    _bookmarkDrag.startBeat = bookmark.beat;
    _bookmarkDrag.lastBeat = bookmark.beat;
    window.addEventListener('pointermove', _bookmarkDragMove);
    window.addEventListener('pointerup', _bookmarkDragUp);
  };
  const arrowColor = arrowHover ? '#AACDFF' : '#4488FF';

  return (
    <group ref={groupRef}>
      {/* Pin (L): badge → down the stem → foot → dot (on the ring line). */}
      <Line points={[[badgeX, badgeY, 0], [badgeX, 0, 0], [dotX, 0, 0]]} color={lineColor} lineWidth={1.5} transparent opacity={isEditing ? 0.9 : 0.6} />
      <mesh position={[dotX, 0, 0]} raycast={() => null}>
        <sphereGeometry args={[0.07, 12, 10]} />
        <meshBasicMaterial color="#FFFFFF" toneMapped={false} />
      </mesh>
      {/* Hover/drag zone over the whole pin. */}
      <mesh position={[badgeX - 0.15, 0.4, -0.08]}
        onPointerOver={(e: ThreeEvent<PointerEvent>) => { e.stopPropagation(); showHover(); }}
        onPointerOut={hideHoverSoon}
        onPointerDown={(e: ThreeEvent<PointerEvent>) => {
          if (e.button === 2) { e.stopPropagation(); deleteThisBookmark(); return; }
          if (e.button === 0) startMove(e);
        }}
        onContextMenu={(e: ThreeEvent<MouseEvent>) => { e.nativeEvent.preventDefault(); }}>
        <planeGeometry args={[1.0, 1.3]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} depthTest={false} side={THREE.DoubleSide} />
      </mesh>
      {/* Banner badge — click to rename, hover to reveal the move arrow. */}
      <group ref={badgeWrapRef} position={[badgeX, badgeY, 0]}
        onPointerOver={(e: ThreeEvent<PointerEvent>) => { e.stopPropagation(); showHover(); }}
        onPointerOut={hideHoverSoon}
        onPointerDown={(e: ThreeEvent<PointerEvent>) => {
          if (e.button !== 2) return;
          e.stopPropagation();
          deleteThisBookmark();
        }}
        onContextMenu={(e: ThreeEvent<MouseEvent>) => { e.nativeEvent.preventDefault(); }}>
        <BookmarkBadge active={isEditing} onClick={toggleEditor} />
      </group>

      {/* Bookmark name in a rounded pill, billboarded, just right of the badge. */}
      {bookmark.name ? (
        <group ref={nameRef} position={[badgeX, badgeY, 0]}>
          <BookmarkLabel name={bookmark.name} />
        </group>
      ) : null}

      {/* Move arrow — only while hovering the bookmark. */}
      {hovered && (
      <group
        position={[badgeX, 0, 0]}
        onPointerDown={startMove}
        onPointerOver={(e: ThreeEvent<PointerEvent>) => { e.stopPropagation(); setArrowHover(true); showHover(); }}
        onPointerOut={() => { setArrowHover(false); hideHoverSoon(); }}
      >
        <mesh>
          <sphereGeometry args={[0.0425, 14, 12]} />
          <meshStandardMaterial color={arrowColor} emissive={arrowColor} emissiveIntensity={0.35} roughness={0.4} metalness={0.3} toneMapped={false} />
        </mesh>
        <mesh rotation={[Math.PI / 2, 0, 0]}>
          <cylinderGeometry args={[0.018, 0.018, 0.7, 6]} />
          <meshStandardMaterial color={arrowColor} emissive={arrowColor} emissiveIntensity={0.35} roughness={0.4} metalness={0.3} toneMapped={false} />
        </mesh>
        <mesh position={[0, 0, -0.44]} rotation={[-Math.PI / 2, 0, 0]}>
          <coneGeometry args={[0.07, 0.16, 8]} />
          <meshStandardMaterial color={arrowColor} emissive={arrowColor} emissiveIntensity={0.3} roughness={0.4} metalness={0.3} toneMapped={false} />
        </mesh>
        <mesh position={[0, 0, 0.44]} rotation={[Math.PI / 2, 0, 0]}>
          <coneGeometry args={[0.07, 0.16, 8]} />
          <meshStandardMaterial color={arrowColor} emissive={arrowColor} emissiveIntensity={0.3} roughness={0.4} metalness={0.3} toneMapped={false} />
        </mesh>
        <mesh rotation={[Math.PI / 2, 0, 0]} raycast={undefined}>
          <cylinderGeometry args={[0.18, 0.18, 1.05, 8]} />
          <meshBasicMaterial transparent opacity={0} depthWrite={false} depthTest={false} />
        </mesh>
      </group>
      )}
    </group>
  );
}

// ── Bookmark placement rings (mirror of the light rings, RIGHT side) ─────────
function placeBookmarkAt(beat: number) {
  const store = useEditorStore.getState();
  if (store.bookmarks.some(b => Math.abs(b.beat - beat) < 0.01)) { store.setEditingBookmarkBeat(beat); return; }
  store.addBookmark(beat, '');
  spawnBookmarkBurst(beat);
  store.setEditingBookmarkBeat(beat); // open the rename popup immediately
}

function BookmarkRing({ beat }: { beat: number }) {
  const ringRef = useRef<THREE.Group>(null!);
  const [hovered, setHovered] = useState(false);
  const hw = TUNNEL_WIDTH / 2;
  const badgeX = hw + 0.5, touchX = hw - 0.1, badgeY = 0.65;
  const ringX = touchX + 0.3;

  useFrame((state) => { if (ringRef.current) ringRef.current.quaternion.copy(state.camera.quaternion); });

  const place = (e: ThreeEvent<MouseEvent>) => { e.stopPropagation(); placeBookmarkAt(beat); };
  const over = (e: ThreeEvent<PointerEvent>) => { e.stopPropagation(); setHovered(true); document.body.style.cursor = 'pointer'; };
  const out = () => { setHovered(false); document.body.style.cursor = ''; };

  return (
    <group position={[0, 0, -beat * BEAT_SPACING]}>
      <group ref={ringRef} position={[ringX, 0, 0]}>
        <mesh onClick={place} onPointerOver={over} onPointerOut={out} renderOrder={9}>
          <ringGeometry args={[0.13, 0.17, 28]} />
          <meshBasicMaterial color={hovered ? '#FFFFFF' : '#7FA2C8'} transparent opacity={hovered ? 0.96 : 0.5} side={THREE.DoubleSide} depthTest={false} depthWrite={false} toneMapped={false} />
        </mesh>
        <mesh onClick={place} onPointerOver={over} onPointerOut={out}>
          <circleGeometry args={[0.22, 20]} />
          <meshBasicMaterial transparent opacity={0} depthTest={false} depthWrite={false} />
        </mesh>
      </group>

      {hovered && (
        <>
          <Line points={[[badgeX, badgeY, 0], [badgeX, 0, 0], [ringX, 0, 0]]} color="#FFFFFF" lineWidth={1.5} transparent opacity={0.55} />
          <mesh position={[ringX, 0, 0]} raycast={() => null}>
            <sphereGeometry args={[0.07, 12, 10]} />
            <meshBasicMaterial color="#FFFFFF" transparent opacity={0.85} depthTest={false} depthWrite={false} toneMapped={false} />
          </mesh>
          <group position={[badgeX, badgeY, 0]}>
            <BookmarkBadge active={false} onClick={place} />
          </group>
        </>
      )}
    </group>
  );
}

function BookmarkPlacementRings() {
  const toolMode = useEditorStore(s => s.toolMode);
  const playing = useEditorStore(s => s.playback.playing);
  const currentTime = useEditorStore(s => s.playback.currentTime);
  const scrollStep = useEditorStore(s => s.scrollStep);
  const bpm = useEditorStore(s => s.beatmap?.bpm || 120);
  const bookmarks = useEditorStore(s => s.bookmarks);

  if (toolMode !== 'bookmark' || playing) return null;
  const rawBeat = chartBeat();
  const step = Math.max(0.0001, scrollStep);
  const b0 = Math.max(0, Math.round(rawBeat / step) * step);
  const SPAN_BEATS = 4;
  const count = Math.max(2, Math.min(16, Math.round(SPAN_BEATS / step) + 1));
  return (
    <>
      {Array.from({ length: count }, (_, i) => {
        const beat = b0 + i * step;
        if (bookmarks.some(b => Math.abs(b.beat - beat) < 0.01)) return null;
        return <BookmarkRing key={i} beat={beat} />;
      })}
    </>
  );
}

// ── Flash Light Effect (background flash during playback) ───────────────────

function FlashLightEffect() {
  const lightRef = useRef<THREE.PointLight>(null!);
  const matRef = useRef<THREE.MeshBasicMaterial>(null!);

  useFrame(() => {
    const store = useEditorStore.getState();
    const flashEvents = store.flashEvents;
    if (flashEvents.length === 0) {
      if (lightRef.current) lightRef.current.intensity = 0;
      if (matRef.current) matRef.current.opacity = 0;
      return;
    }

    const bpm = store.beatmap?.bpm || 120;
    const offsetSec = (store.beatmap?.offset || 0) / 1000; // event beats are chart-relative
    const currentTime = store.playback.currentTime;
    let totalIntensity = 0;

    // Only the most-recently-STARTED light is live: when a new light's turn comes
    // it overwrites an older one that hasn't finished fading (no blending). Pick
    // the event with the latest start that is still <= now.
    let active: FlashEvent | null = null;
    let activeStart = -Infinity;
    for (const event of flashEvents) {
      const eventTimeSec = event.time_beats / (bpm / 60) + offsetSec;
      if (eventTimeSec <= currentTime && eventTimeSec > activeStart) {
        active = event;
        activeStart = eventTimeSec;
      }
    }

    if (active) {
      const elapsed = currentTime - activeStart;
      const repeat = Math.max(1, Math.round(active.repeat ?? 1));
      // Pulse length and spacing. interval = 1/frequency; old events (no
      // frequency) fall back to a 60%-duty strobe so they still blink.
      const dur = active.duration ?? (((active.fadeInTime || 0) + (active.fadeOutTime || 0)) || 0.4);
      const freq = active.frequency ?? (1 / Math.max(0.05, dur * 1.6));
      const interval = 1 / Math.max(0.1, freq);
      // For a strobe each pulse must be shorter than its slot, or pulses merge
      // into one continuous flash (no blink). Cap on-time to leave an OFF gap.
      const onTime = repeat > 1 ? Math.min(dur, interval * 0.7) : dur;
      const total = (repeat - 1) * interval + onTime;

      // Past its own window → off (an overwritten older light never resurrects).
      if (elapsed >= 0 && elapsed <= total) {
        // Each pulse is a sine bump of length `onTime`, starts spaced `interval`
        // apart. Scan exactly the pulses that can be live at `elapsed`.
        let factor = 0;
        const iLo = Math.max(0, Math.floor((elapsed - onTime) / interval));
        const iHi = Math.min(repeat - 1, Math.floor(elapsed / interval));
        for (let i = iLo; i <= iHi; i++) {
          const t = elapsed - i * interval;
          if (t >= 0 && t <= onTime) factor = Math.max(factor, Math.sin((t / onTime) * Math.PI));
        }
        totalIntensity = factor * active.intensity;
      }
    }

    // In-game the flash is always white — drive both the light and the backdrop
    // plane in white, scaled by the strobe envelope.
    // Intensity is 0–2; scale it for display. The backdrop wash saturates near
    // intensity 1, so the extra headroom up to 2 mainly pushes the scene light
    // (everything lit ~twice as hard) — a full flash reads much brighter.
    if (lightRef.current) {
      lightRef.current.color.setRGB(1, 1, 1);
      lightRef.current.intensity = Math.min(14, totalIntensity * 5);
    }
    if (matRef.current) {
      matRef.current.color.setRGB(1, 1, 1);
      matRef.current.opacity = Math.min(1, totalIntensity * 1.1);
    }
  });

  return (
    <>
      {/* Light comes from the far end so the flash reads as the BACKGROUND
          lighting up, not a wash over the foreground. */}
      <pointLight ref={lightRef} position={[0, 8, -70]} intensity={0} distance={220} />
      {/* Big plane parked at the far backdrop (deep Z), drawn FIRST (renderOrder
          -10) + depthTest off so it tints the background area behind the tunnel
          instead of overlaying the timeline. */}
      <mesh position={[0, 8, -98]} renderOrder={-10}>
        <planeGeometry args={[320, 200]} />
        <meshBasicMaterial
          ref={matRef}
          transparent
          opacity={0}
          color="#000000"
          depthWrite={false}
          depthTest={false}
          blending={THREE.AdditiveBlending}
        />
      </mesh>
    </>
  );
}

// ── Beat Grid (floor + vertical lines) ──────────────────────────────────────

function BeatGrid() {
  const gridLineWidth = useEditorStore(s => s.interfaceSettings.gridLineWidth);
  const gridMeasureLineWidth = useEditorStore(s => s.interfaceSettings.gridMeasureLineWidth);
  const gridLineVisible = useEditorStore(s => s.interfaceSettings.gridLineVisible);
  // Master on/off for the whole 3D beat grid (the "3D timeline" — beat + measure
  // lines and their number chips). Toggled from the Transport bar.
  const beatGridVisible = useEditorStore(s => s.interfaceSettings.beatGridVisible);
  const al = useAccentLine();
  const beatNumberSize = useEditorStore(s => s.interfaceSettings.beatNumberSize);
  const gridYOffset = useEditorStore(s => s.interfaceSettings.gridYOffset);
  // Subscribe to NJS so the line positions recompute when it loads from prefs /
  // changes — otherwise BEAT_SPACING (driven by NJS) updates but the memo below
  // keeps stale spacing until a scroll changes windowBeat.
  const njs = useEditorStore(s => s.noteJumpSpeed);

  // Use a ref + state pair: ref for the useFrame comparison (no stale closure),
  // state to trigger React re-render when the window actually shifts.
  const [windowBeat, setWindowBeat] = React.useState(0);
  const windowBeatRef = useRef(0);
  const groupRef = useRef<THREE.Group>(null!);

  useFrame(() => {
    const cb = useEditorStore.getState().playback.currentBeat;
    const floored = Math.floor(cb);
    if (floored !== windowBeatRef.current) {
      windowBeatRef.current = floored;
      setWindowBeat(floored);
    }
    // Translate group so lines scroll smoothly between integer beats
    if (groupRef.current) {
      groupRef.current.position.z = cb * BEAT_SPACING;
      groupRef.current.position.y = gridYOffset;
    }
  });

  // Beat-line MESH POOL — fixed slots updated imperatively every frame. The old
  // `lines.map` re-created ~36 plane geometries + materials on EVERY integer
  // beat during playback (constant GPU buffer churn + React reconciliation).
  // Only the Html beat-number chips (few, measures only) stay on the React
  // path via the windowBeat state.
  const GRID_BEATS_AHEAD = 32;
  const POOL_SIZE = VISIBLE_BEATS_BEHIND + GRID_BEATS_AHEAD + 4;
  const lineRefs = useRef<Array<THREE.Mesh | null>>([]);
  const lineMats = useMemo(
    () => Array.from({ length: POOL_SIZE }, () =>
      new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  useEffect(() => () => { lineMats.forEach(m => m.dispose()); }, [lineMats]);

  const hw = TUNNEL_WIDTH / 2;

  useFrame(() => {
    const cb = useEditorStore.getState().playback.currentBeat;
    const wb = Math.floor(cb);
    const startBeat = Math.max(0, wb - VISIBLE_BEATS_BEHIND);
    const endBeat = wb + 1 + GRID_BEATS_AHEAD;
    for (let i = 0; i < POOL_SIZE; i++) {
      const mesh = lineRefs.current[i];
      if (!mesh) continue;
      const b = startBeat + i;
      const isMeasure = b % 4 === 0;
      if (b > endBeat || (!isMeasure && !gridLineVisible)) {
        mesh.visible = false;
        continue;
      }
      mesh.visible = true;
      mesh.position.z = -b * BEAT_SPACING;
      // UNIT plane scaled to the line's width/thickness. Reviewers couldn't
      // tell a MEASURE from a plain beat: the bar line is now clearly heavier
      // (and brighter) than a beat, and a beat is clearly heavier than a snap
      // subdivision (drawn by StepLines at ~0.25 opacity).
      mesh.scale.set(hw * 2 - 0.1, (isMeasure ? gridMeasureLineWidth * 0.9 : gridLineWidth * 0.55) * 0.06, 1);
      const ahead = b - wb;
      const factor = ahead <= 6 ? 1 : Math.max(0.08, 1 - (ahead - 6) / 28);
      const m = lineMats[i];
      m.color.set(isMeasure ? al.measure : al.beat);
      m.opacity = (isMeasure ? 1 : 0.55) * factor;
    }
  });

  // Beat numbers for the Html chips (React path). Reviewers asked the count to
  // run 1-by-1 (356, 357, 358…) instead of only every measure, in ALL editors.
  // Chips are DOM sprites, so the labelled range stays shorter than the full
  // line pool — beyond it the lines are heavily faded anyway and numbers there
  // would be unreadable clutter (and ~40 Html sprites of per-frame CSS math).
  const LABEL_BEATS_AHEAD = 20;
  const labelBeats = useMemo(() => {
    const startBeat = Math.max(0, windowBeat - VISIBLE_BEATS_BEHIND);
    const endBeat = windowBeat + 1 + LABEL_BEATS_AHEAD;
    const out: number[] = [];
    for (let b = startBeat; b <= endBeat; b++) out.push(b);
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowBeat, njs]);

  // Master off — hide the whole 3D beat grid (after all hooks, so hook order is stable).
  if (!beatGridVisible) return null;

  return (
    <group ref={groupRef}>
      {/* Floor grid — horizontal beat lines only (no verticals). Fixed mesh pool;
          position/scale/opacity written imperatively above. */}
      {Array.from({ length: POOL_SIZE }, (_, i) => (
        <mesh
          key={`bl-${i}`}
          ref={(el) => { lineRefs.current[i] = el; }}
          geometry={UNIT_GRID_PLANE}
          material={lineMats[i]}
          rotation={[-Math.PI / 2, 0, 0]}
          position={[0, 0.012, 0]}
          visible={false}
          raycast={() => null}
        />
      ))}
      {/* Beat numbers on the right side — Flow-style chips, EVERY beat (1-by-1
          count) in every perf mode; measure chips keep the heavier accent
          look. (Perf mode used to drop the in-between chips to save DOM
          sprites — mappers count beats off them, so readability wins.) */}
      {labelBeats.map((beat) => (
        <Html
          key={`bn-${beat}`}
          position={[hw + 0.55, 0.08, -beat * BEAT_SPACING]}
          transform
          sprite
          distanceFactor={Math.max(1, beatNumberSize * 25) * (beat % 4 === 0 ? 1 : 0.8)}
          occlude={false}
          zIndexRange={[10, 0]}
          style={{ pointerEvents: 'none' }}
        >
          <div style={beat % 4 === 0 ? beatLabelStyle : beatLabelDimStyle}>{String(beat)}</div>
        </Html>
      ))}
    </group>
  );
}

// ── Step Lines (white markers on floor showing scroll step intervals) ──────

// Thin adapter: feeds the shared <StepLines> the VR tunnel's geometry. The
// tunnel is linear (z = -beat * BEAT_SPACING) with the trigger at z = 0.
// Fixed WORLD depth (in scene units) the snap lines should blanket ahead of the
// trigger, converted to a step COUNT for the current NJS + step. Keeping the
// coverage constant in world space (not a fixed step count) means the snap grid
// looks the same on-screen at any NJS — otherwise high NJS (large BEAT_SPACING)
// spreads the beats so far apart that only a couple of the fixed 12 lines land
// in the visible runway and the rest bunch up at the vanishing point.
const SNAP_RUNWAY_AHEAD = 60;
const SNAP_RUNWAY_BEHIND = 12;

function StepLines() {
  const scrollStep = useEditorStore(s => s.scrollStep);
  const stepLineWidth = useEditorStore(s => s.interfaceSettings.stepLineWidth);
  const al = useAccentLine();
  const stepLineOpacity = useEditorStore(s => s.interfaceSettings.stepLineOpacity);
  const gridYOffset = useEditorStore(s => s.interfaceSettings.gridYOffset);
  const njs = useEditorStore(s => s.noteJumpSpeed); // BEAT_SPACING === njs; re-render when it changes
  const playing = useEditorStore(s => s.playback.playing);
  // Part of the 3D timeline — hidden with the master Beat Grid toggle.
  const beatGridVisible = useEditorStore(s => s.interfaceSettings.beatGridVisible);
  // Reviewer: "markers fade during playback" — the snap lines used to ease out
  // the moment you hit play, which is exactly when you're checking timing.
  // They now stay visible; Settings → Interface can restore the old fade.
  const fadeOnPlay = useEditorStore(s => s.interfaceSettings.fadeBeatLinesOnPlay);
  const stepWorld = Math.max(0.001, scrollStep * njs);
  const stepsAhead = Math.max(8, Math.min(80, Math.round(SNAP_RUNWAY_AHEAD / stepWorld)));
  const stepsBehind = Math.max(2, Math.min(24, Math.round(SNAP_RUNWAY_BEHIND / stepWorld)));
  if (!beatGridVisible) return null;
  return (
    <SharedStepLines
      width={TUNNEL_WIDTH}
      step={scrollStep}
      beatToZ={(b, cb) => -(b - cb) * BEAT_SPACING}
      getCurrentBeat={() => useEditorStore.getState().playback.currentBeat}
      playing={playing}
      fadeWhilePlaying={fadeOnPlay === true}
      color={al.sub}
      lineWidth={stepLineWidth}
      opacity={stepLineOpacity}
      y={gridYOffset + 0.005}
      stepsAhead={stepsAhead}
      stepsBehind={stepsBehind}
    />
  );
}

// ── Playhead (hit zone line) ───────────────────────────────────────────────

function Playhead() {
  const hw = TUNNEL_WIDTH / 2;
  const phColor = useEditorStore(s => s.interfaceSettings.playheadColor);
  const phLineWidth = useEditorStore(s => s.interfaceSettings.playheadLineWidth);
  const phSideWidth = useEditorStore(s => s.interfaceSettings.playheadSideLineWidth);
  const floorVisible = useEditorStore(s => s.interfaceSettings.playheadFloorVisible);
  const sidesVisible = useEditorStore(s => s.interfaceSettings.playheadSidesVisible);
  // The trigger/hit-zone lines are part of the 3D timeline — hidden with the
  // master Beat Grid toggle for a clean stage.
  const beatGridVisible = useEditorStore(s => s.interfaceSettings.beatGridVisible);
  const al = useAccentLine();
  if (!beatGridVisible) return null;
  return (
    <group position={[0, 0, 0]}>
      {/* Trigger floor line — uses the canonical accent colour. */}
      {floorVisible && (
        <Line points={[[-hw + 0.05, 0, 0], [hw - 0.05, 0, 0]]} color={al.beat} lineWidth={phLineWidth} />
      )}
      {/* Vertical side lines + top connector */}
      {sidesVisible && (
        <>
          <Line points={[[-hw, 0, 0], [-hw, TUNNEL_HEIGHT, 0]]} color={phColor} lineWidth={phSideWidth} />
          <Line points={[[hw, 0, 0], [hw, TUNNEL_HEIGHT, 0]]} color={phColor} lineWidth={phSideWidth} />
          <Line points={[[-hw, TUNNEL_HEIGHT, 0], [hw, TUNNEL_HEIGHT, 0]]} color={phColor} lineWidth={phSideWidth} />
        </>
      )}
    </group>
  );
}

// ── Tunnel Frame ────────────────────────────────────────────────────────────

function TunnelFrame() {
  const hw = TUNNEL_WIDTH / 2;
  const nearZ = VISIBLE_BEATS_BEHIND * BEAT_SPACING;
  const farZ = -TUNNEL_LENGTH;
  const edgeWidth = useEditorStore(s => s.interfaceSettings.edgeLineWidth);
  const al = useAccentLine();
  const edgePrimary = al.outer;
  const floorColor = useEditorStore(s => s.interfaceSettings.floorColor);
  const floorOpacity = useEditorStore(s => s.interfaceSettings.floorOpacity);
  // The floor + highway edges are part of the 3D track — hidden with the master
  // Beat Grid / 3D Track toggle for a fully clean stage.
  const beatGridVisible = useEditorStore(s => s.interfaceSettings.beatGridVisible);
  if (!beatGridVisible) return null;

  return (
    <group>
      {/* Dark floor */}
      <mesh position={[0, -0.01, -TUNNEL_LENGTH / 2]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[TUNNEL_WIDTH, TUNNEL_LENGTH + nearZ]} />
        <meshStandardMaterial color={floorColor} transparent opacity={floorOpacity} />
      </mesh>
      {/* Floor edge lines only — the upper (y=h) edges and the near vertical
          edges were removed (they splayed out at the top and added clutter). */}
      <Line points={[[-hw, 0, nearZ], [-hw, 0, farZ]]} color={edgePrimary} lineWidth={edgeWidth} />
      <Line points={[[hw, 0, nearZ], [hw, 0, farZ]]} color={edgePrimary} lineWidth={edgeWidth} />
    </group>
  );
}

// ── Synth Console road ───────────────────────────────────────────────────────
// Replaces the VR tunnel floor in the Console editor: the game's dark hexagon
// BeatTrack surface (real `T_BeatTrack_01_BC` texture) with neon cyan (left) /
// magenta (right) edge lines, matching the in-game hoverboard track. The hex
// pattern scrolls toward the player with the chart beat to sell forward motion.
// Static stage (notes/obstacles scroll over it via BeatScrollGroup); console-only.
const ROAD_WIDTH = 6;
const ROAD_HEX_TILE = 8;             // world units per hex-texture repeat along Z
const ROAD_LEFT_COLOR = '#1CC5F0';   // console left-hand cyan
const ROAD_RIGHT_COLOR = '#F02D87';  // console right-hand magenta

// Convert a rail node's world Z to a chart beat (matches RailLine: world z =
// seconds*20 = ms*0.02, so ms = z/0.02). Used to find where an air rail ends.
function railNodeBeat(nodeZ: number, bpm: number): number {
  return (nodeZ / 0.02 / 1000) * (bpm / 60);
}

// Air-rail (floor→air) beat spans: a note with a long multi-node rail is the
// green "air ribbon"; the rider flies/grinds it while the floor dissolves and
// the camera pulls back. Padded ±0.5 beat so transitions ease in/out. Shared by
// ConsoleRoad (floor dissolve) and CameraController (air pull-back).
function consoleAirSpans(notes: Note[] | undefined, bpm: number): Array<[number, number]> {
  const spans: Array<[number, number]> = [];
  if (!Array.isArray(notes)) return spans;
  for (const n of notes) {
    if (!n.has_rail || !n.rail_nodes || n.rail_nodes.length < 4) continue;
    const last = n.rail_nodes[n.rail_nodes.length - 1];
    spans.push([n.time_beats - 0.5, railNodeBeat(last[2], bpm) + 0.5]);
  }
  return spans;
}

function ConsoleRoad({ notes, bpm }: { notes: Note[]; bpm: number }) {
  const tex = useTexture('/models/console/track/T_BeatTrack_01_BC.png');
  const edgeWidth = useEditorStore(s => s.interfaceSettings.edgeLineWidth);
  const nearZ = VISIBLE_BEATS_BEHIND * BEAT_SPACING;
  const length = TUNNEL_LENGTH + nearZ;
  const hw = ROAD_WIDTH / 2;

  const floorMatRef = useRef<THREE.MeshStandardMaterial>(null);
  // drei <Line> forwards a Line2 ref; keep it loose so we can poke material.opacity.
  const leftRef = useRef<any>(null);
  const rightRef = useRef<any>(null);
  const opaRef = useRef(0.97);

  // Air-rail sections: a long multi-node rail (the game's green "air rail"
  // ribbon — special/one-hand) means the floor breaks and the rider flies it.
  // Collect each one's beat span so we can dissolve the floor across it.
  const airSpans = useMemo(() => consoleAirSpans(notes, bpm), [notes, bpm]);

  // Configure the hex texture for tiling down the track.
  useMemo(() => {
    tex.wrapS = tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(1, length / ROAD_HEX_TILE);
    tex.anisotropy = 4;
  }, [tex, length]);

  useFrame(() => {
    const cb = chartBeat();
    // Scroll the hex pattern toward the camera as the chart advances.
    tex.offset.y = (-cb * (BEAT_SPACING / ROAD_HEX_TILE)) % 1;
    // Floor→air: dissolve the road while the playhead is inside an air-rail span.
    let inAir = false;
    for (let i = 0; i < airSpans.length; i++) {
      if (cb >= airSpans[i][0] && cb <= airSpans[i][1]) { inAir = true; break; }
    }
    const target = inAir ? 0.06 : 0.97;
    opaRef.current += (target - opaRef.current) * 0.08; // smooth fade
    if (floorMatRef.current) floorMatRef.current.opacity = opaRef.current;
    const edgeOpacity = 0.12 + ((opaRef.current - 0.06) / 0.91) * 0.88;
    if (leftRef.current?.material) leftRef.current.material.opacity = edgeOpacity;
    if (rightRef.current?.material) rightRef.current.material.opacity = edgeOpacity;
  });

  return (
    <group>
      <mesh position={[0, -0.01, -TUNNEL_LENGTH / 2]} rotation={[-Math.PI / 2, 0, 0]}>
        <planeGeometry args={[ROAD_WIDTH, length]} />
        <meshStandardMaterial
          ref={floorMatRef}
          map={tex}
          emissiveMap={tex}
          color="#0a0a33"
          emissive="#143a8c"
          emissiveIntensity={0.55}
          transparent
          opacity={0.97}
        />
      </mesh>
      {/* Neon edge rails (cyan left, magenta right) — fade with the floor in air */}
      <Line ref={leftRef} points={[[-hw, 0.02, nearZ], [-hw, 0.02, -TUNNEL_LENGTH]]} color={ROAD_LEFT_COLOR} lineWidth={edgeWidth + 1.5} transparent />
      <Line ref={rightRef} points={[[hw, 0.02, nearZ], [hw, 0.02, -TUNNEL_LENGTH]]} color={ROAD_RIGHT_COLOR} lineWidth={edgeWidth + 1.5} transparent />
    </group>
  );
}

// ── Tool → Note Type Mapping ─────────────────────────────────────────────────


// ── Grid Guide (visual placement grid at playhead) ─────────────────────────

function GridGuide() {
  const gridGuideVisible = useEditorStore(s => s.gridGuideVisible);
  const gridWidth = useEditorStore(s => s.gridWidth);
  const gridHeight = useEditorStore(s => s.gridHeight);
  const gridDivisionsH = useEditorStore(s => s.gridDivisionsH);
  const gridDivisionsV = useEditorStore(s => s.gridDivisionsV);
  const notesGridYOffset = useEditorStore(s => s.notesGridYOffset);
  const gridColor = useEditorStore(s => s.gridColor);
  const gridOpacity = useEditorStore(s => s.gridOpacity);
  const toolMode = useEditorStore(s => s.toolMode);
  const beatmap = useEditorStore(s => s.beatmap);

  // Determine if a placement tool is active (for hover preview)
  const isPlacementTool = NOTE_TOOL_MODES.has(toolMode) || OBSTACLE_TOOL_MODES.has(toolMode)
    || toolMode === 'jump' || toolMode === 'eraser';

  // Build thin mesh rectangles for each grid line
  const lines = useMemo(() => {
    const halfW = gridWidth / 2;
    const halfH = gridHeight / 2;
    const thickness = 0.006;
    const thickLine = 0.01;
    const result: Array<{
      px: number; py: number;
      sx: number; sy: number;
      isCenter: boolean; isBorder: boolean;
    }> = [];

    const centerH = Math.floor(gridDivisionsH / 2);
    const centerV = Math.floor(gridDivisionsV / 2);

    // Vertical lines (columns)
    for (let i = 0; i <= gridDivisionsH; i++) {
      const x = -halfW + (gridWidth / gridDivisionsH) * i;
      const isBorder = i === 0 || i === gridDivisionsH;
      const isCenter = i === centerH;
      const t = (isBorder || isCenter) ? thickLine : thickness;
      result.push({ px: x, py: 0, sx: t, sy: gridHeight, isCenter, isBorder });
    }

    // Horizontal lines (rows)
    for (let j = 0; j <= gridDivisionsV; j++) {
      const y = -halfH + (gridHeight / gridDivisionsV) * j;
      const isBorder = j === 0 || j === gridDivisionsV;
      const isCenter = j === centerV;
      const t = (isBorder || isCenter) ? thickLine : thickness;
      result.push({ px: 0, py: y, sx: gridWidth, sy: t, isCenter, isBorder });
    }

    return result;
  }, [gridWidth, gridHeight, gridDivisionsH, gridDivisionsV]);

  // ── Pointer handlers for note preview + placement on the grid ──
  const handlePointerMove = useCallback((e: ThreeEvent<PointerEvent>) => {
    if (!isPlacementTool) return;
    e.stopPropagation();
    const store = useEditorStore.getState();
    // Convert hit point (world space on grid plane) to note-space
    let x = e.point.x / (GRID_SCALE * 10);
    let y = (e.point.y - NOTE_Y_OFFSET) / (GRID_SCALE * 10);
    if (store.snapToGrid) {
      [x, y] = store.snapToGridPosition(x, y);
    }
    [x, y] = _clampToGrid(x, y);
    store.setPreviewPosition([x, y]);
  }, [isPlacementTool]);

  const handlePointerLeave = useCallback(() => {
    useEditorStore.getState().setPreviewPosition(null);
  }, []);

  const handleClick = useCallback((e: ThreeEvent<MouseEvent>) => {
    if (!beatmap) return;
    const store = useEditorStore.getState();

    // In select mode, let clicks pass through to notes/rail nodes behind the grid
    if (store.toolMode === 'select') return;

    e.stopPropagation();

    // Place at the offset-adjusted chart beat (same frame as BeatScrollGroup +
    // note.time_beats). setPlaying snaps this beat to a step on pause, so it's
    // already the correct placement beat — don't re-snap. time_ms is chart-
    // relative (offset-free), matching the .synth.
    const bpm = store.beatmap?.bpm || 120;
    const rawBeat = chartBeat();
    const timeMs = store.beatToTimeMs(rawBeat);
    const timeSeconds = timeMs / 1000;

    // Convert click to note-space
    let x = e.point.x / (GRID_SCALE * 10);
    let y = (e.point.y - NOTE_Y_OFFSET) / (GRID_SCALE * 10);
    if (store.snapToGrid) {
      [x, y] = store.snapToGridPosition(x, y);
    }

    // Clamp to grid bounds (prevent placement outside the grid)
    [x, y] = _clampToGrid(x, y);

    // ── Drag-to-move finish ──
    if (store.draggingNoteIndex !== null) {
      store.finishDrag(rawBeat, x, y);
      return;
    }

    // ── Skip if gizmo drag just ended (prevents click-through placement) ──
    if (Date.now() - _gizmoDrag.endedAt < 200) return;

    // ── Alt held = move mode, skip placement ──
    if (e.nativeEvent.altKey) return;

    // ── Note / Rail placement ──
    if (NOTE_TOOL_MODES.has(store.toolMode)) {
      const mapping = TOOL_TO_NOTE[store.toolMode];
      if (!mapping) return;

      const isRail = RAIL_TOOL_MODES.has(store.toolMode);

      if (store.railPlacementActive && isRail) {
        store.addRailNode([x, y, timeMs * 0.02]);
        return;
      }

      // ── Duplicate same-color check ──
      const diff = store.getCurrentDifficulty();
      if (diff) {
        const duplicate = diff.notes.some(
          (n: Note) => Math.abs(n.time_beats - rawBeat) < 0.001 && n.note_type === mapping.note_type
        );
        if (duplicate) {
          showPlacementToast("Can't place 2 notes of the same color");
          return;
        }
      }

      const note: Note = {
        time_ms: timeMs,
        time_seconds: timeSeconds,
        time_beats: rawBeat,
        position: [x, y, timeMs * 0.02] as [number, number, number],
        note_type: mapping.note_type,
        hand: mapping.hand,
        direction: 0,
        has_rail: isRail,
        rail_nodes: isRail ? [] : null,
        combo_id: 0,
        note_id: '',
      };
      // Rails keep plain placement (no live mirror — that's the rail toolkit's
      // job); normal notes go through the Mirror-Mode-aware path.
      if (isRail) store.addNote(note);
      else store.addPlacedNote(note);

      if (isRail) {
        const diff = store.getCurrentDifficulty();
        if (diff) {
          const idx = diff.notes.findIndex(
            (n: Note) => n.time_ms === timeMs && n.note_type === mapping.note_type
          );
          if (idx >= 0) store.startRailPlacement(idx);
        }
      }
      return;
    }

    // ── Obstacle placement ──
    if (OBSTACLE_TOOL_MODES.has(store.toolMode)) {
      const wallType = TOOL_TO_WALL_TYPE[store.toolMode];
      if (!wallType) return;
      const slide: import('../../types/beatmap').Slide = {
        time_ms: timeMs, time_seconds: timeSeconds, time_beats: rawBeat,
        slide_type: 0, wall_type: wallType, initialized: true,
      };
      store.addPlacedSlide(slide);
      return;
    }

    if (store.toolMode === 'jump') { store.toggleJumpAtBeat(rawBeat); return; }
  }, [beatmap]);

  if (!gridGuideVisible) return null;

  return (
    <group position={[0, NOTE_Y_OFFSET + notesGridYOffset, 0.05]}>
      {/* Invisible raycast plane covering the grid area — for hover + click */}
      <mesh
        position={[0, 0, 0.001]}
        onPointerMove={handlePointerMove}
        onPointerLeave={handlePointerLeave}
        onClick={handleClick}
        renderOrder={9}
      >
        <planeGeometry args={[gridWidth, gridHeight]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} side={THREE.DoubleSide} />
      </mesh>
      {/* Grid lines */}
      {lines.map((l, i) => {
        const op = l.isBorder
          ? Math.min(gridOpacity + 0.15, 0.9)
          : l.isCenter
            ? Math.min(gridOpacity + 0.08, 0.8)
            : gridOpacity;
        return (
          <mesh key={i} position={[l.px, l.py, 0]} renderOrder={10}>
            <planeGeometry args={[l.sx, l.sy]} />
            <meshBasicMaterial
              color={l.isCenter ? '#ffffff' : gridColor}
              transparent
              opacity={l.isCenter ? Math.min(gridOpacity + 0.06, 0.5) : op}
              depthWrite={false}
              depthTest={false}
              side={THREE.DoubleSide}
              fog={false}
            />
          </mesh>
        );
      })}
    </group>
  );
}

// ── Note Preview (ghost note that follows cursor on grid) ────────────────────

function NotePreview() {
  const previewPosition = useEditorStore(s => s.previewPosition);
  const toolMode = useEditorStore(s => s.toolMode);
  const _gp4 = useEditorStore(s => s.gameplaySettings);
  const noteSize = _gp4.noteSize;
  const noteColors = useMemo(() => getNoteColorsFromSettings(_gp4), [_gp4.noteColorLeft, _gp4.noteColorRight, _gp4.noteColorOneHand, _gp4.noteColorBothHands]);
  const railNodeSize = _gp4.railNodeSize;
  const railPlacementActive = useEditorStore(s => s.railPlacementActive);

  // Hide preview when dragging a note (gizmo or alt-drag)
  const gizmoDragAxis = useEditorStore(s => s.gizmoDragAxis);
  const draggingNoteIndex = useEditorStore(s => s.draggingNoteIndex);

  // Determine color from tool mode
  const mapping = TOOL_TO_NOTE[toolMode];
  const noteType = mapping?.note_type;
  const color = noteType ? (noteColors[noteType] || '#ffffff') : null;

  if (!previewPosition || !color || !NOTE_TOOL_MODES.has(toolMode)) return null;
  if (gizmoDragAxis || draggingNoteIndex !== null) return null;

  // When in active rail placement, show smaller control-point preview
  const size = railPlacementActive ? railNodeSize : noteSize;

  const worldX = previewPosition[0] * GRID_SCALE * 10;
  const worldY = previewPosition[1] * GRID_SCALE * 10 + NOTE_Y_OFFSET;

  return (
    <group renderOrder={15}>
      <mesh position={[worldX, worldY, 0.08]} renderOrder={15}>
        <sphereGeometry args={[size, 16, 12]} />
        <meshBasicMaterial
          color={color}
          transparent
          opacity={0.5}
          depthWrite={false}
          depthTest={false}
          fog={false}
        />
      </mesh>
      {/* Crosshair guides across the grid (reviewer: "it's hard to tell where
          you're placing notes"). The ghost alone reads as just another note in
          a dense chart; these two thin lines pin it to the grid so the landing
          spot is unmistakable. */}
      <PlacementCrosshair x={worldX} y={worldY} color={color} />
    </group>
  );
}

/** Two thin lines through the placement point, spanning the trigger grid. */
function PlacementCrosshair({ x, y, color }: { x: number; y: number; color: string }) {
  const gridWidth = useEditorStore(s => s.gridWidth);
  const gridHeight = useEditorStore(s => s.gridHeight);
  const t = 0.012;                       // line thickness (world units)
  const z = 0.07;                        // just behind the ghost note
  const common = {
    transparent: true as const,
    opacity: 0.45,
    depthWrite: false as const,
    depthTest: false as const,
    fog: false as const,
  };
  return (
    <group>
      <mesh position={[0, y, z]} renderOrder={14}>
        <planeGeometry args={[gridWidth, t]} />
        <meshBasicMaterial color={color} {...common} />
      </mesh>
      <mesh position={[x, NOTE_Y_OFFSET, z]} renderOrder={14}>
        <planeGeometry args={[t, gridHeight]} />
        <meshBasicMaterial color={color} {...common} />
      </mesh>
    </group>
  );
}

// ── Ctrl+Drag (free XY movement on notes/rail nodes) ────────────────────────

const _ctrlDrag = {
  active: false,
  noteIndex: -1,
  isRailNode: false,
  railNodeIndex: -1,
  startMouseX: 0,
  startMouseY: 0,
  startPos: [0, 0, 0] as [number, number, number],
  // One undo snapshot on the first move (see beginDrag/endDrag), not per step.
  historyStarted: false,
};

/** Clamp X/Y to grid bounds in note-space.
 *  Accounts for notesGridYOffset and subtracts half noteSize so
 *  the sphere visuals don't extend past the grid border. */
function _clampToGrid(x: number, y: number): [number, number] {
  const store = useEditorStore.getState();
  const SCALE = GRID_SCALE * 10;
  // Half the note sphere radius in note-space so the sphere edge stays inside
  const noteRadius = (store.gameplaySettings.noteSize ?? 0.175) / SCALE;
  const halfW = (store.gridWidth / 2) / SCALE - noteRadius;
  const halfH = (store.gridHeight / 2) / SCALE - noteRadius;
  // Grid may be shifted vertically — note-space center of grid is offset/SCALE
  const centerY = (store.notesGridYOffset ?? 0) / SCALE;
  return [
    Math.max(-halfW, Math.min(halfW, x)),
    Math.max(centerY - halfH, Math.min(centerY + halfH, y)),
  ];
}

function _ctrlDragMove(e: PointerEvent) {
  if (!_ctrlDrag.active) return;

  const dx = e.clientX - _ctrlDrag.startMouseX;
  const dy = e.clientY - _ctrlDrag.startMouseY;
  const store = useEditorStore.getState();
  if (!_ctrlDrag.historyStarted) {
    store.beginDrag(_ctrlDrag.isRailNode ? 'Move rail node' : 'Move note');
    _ctrlDrag.historyStarted = true;
  }
  const SCALE = GRID_SCALE * 10;
  const sensitivity = 0.005 / SCALE;

  let newX = _ctrlDrag.startPos[0] + dx * sensitivity * SCALE;
  let newY = _ctrlDrag.startPos[1] + (-dy) * sensitivity * SCALE;
  if (store.snapToGrid) {
    [newX, newY] = store.snapToGridPosition(newX, newY);
  }
  [newX, newY] = _clampToGrid(newX, newY);

  if (_ctrlDrag.isRailNode) {
    store.updateRailNode(
      _ctrlDrag.noteIndex,
      _ctrlDrag.railNodeIndex,
      [newX, newY, _ctrlDrag.startPos[2]],
    );
  } else {
    // Route through moveSelectedNotes so a rail head drags its whole rail along
    // (rigid move, preserving shape) instead of detaching the head.
    store.moveSelectedNotes([{
      index: _ctrlDrag.noteIndex,
      position: [newX, newY, _ctrlDrag.startPos[2]] as [number, number, number],
    }]);
  }
}

function _ctrlDragUp(_e: PointerEvent) {
  if (!_ctrlDrag.active) return;
  if (_ctrlDrag.historyStarted) useEditorStore.getState().endDrag();
  _ctrlDrag.active = false;
  _ctrlDrag.isRailNode = false;
  _ctrlDrag.historyStarted = false;
  document.body.style.cursor = '';
  useEditorStore.getState().setGizmoDragAxis(null); // clear drag signal
  window.removeEventListener('pointermove', _ctrlDragMove);
  window.removeEventListener('pointerup', _ctrlDragUp);
}

// ── Note Gizmo (3-axis move handle) ───────────────────────────────────────────

// Shared drag state — module-level for window event handlers
const _gizmoDrag = {
  active: false,
  axis: '' as 'x' | 'y' | 'z',
  noteIndex: -1,
  startMouseX: 0,
  startMouseY: 0,
  notePos: [0, 0, 0] as [number, number, number],
  noteBeat: 0,
  // Rail node specific
  isRailNode: false,
  railNodeIndex: -1,
  // Obstacle (slide) specific — only the time (z) axis applies
  isSlide: false,
  // Crouch / jump (time-marker) — moved by value; only time (z) applies
  timeKind: '' as '' | 'crouch' | 'jump',
  lastTimeMs: 0,
  // Timestamp when gizmo drag ended — used to suppress click-through placement
  endedAt: 0,
  // Group move: when the dragged note is part of a multi-selection, every
  // selected note's start position/beat is captured here so they all move by
  // the same delta. null = single-note drag (the normal path).
  group: null as null | Array<{ index: number; startPos: [number, number, number]; startBeat: number }>,
  // Becomes true once the first actual move records the single undo snapshot.
  historyStarted: false,
};

/**
 * Window-level pointer handlers for gizmo dragging.
 * Using window events instead of R3F mesh events because:
 * - R3F drag planes are unreliable (pointer can leave the mesh)
 * - Window events always fire regardless of where the mouse moves
 */
function _gizmoWindowMove(e: PointerEvent) {
  if (!_gizmoDrag.active) return;

  const dx = e.clientX - _gizmoDrag.startMouseX;
  const dy = e.clientY - _gizmoDrag.startMouseY;
  const store = useEditorStore.getState();
  // One undo snapshot on the first move — not per micro-step (see endDrag).
  if (!_gizmoDrag.historyStarted) {
    store.beginDrag(
      _gizmoDrag.group ? `Move ${_gizmoDrag.group.length} notes`
      : _gizmoDrag.isSlide ? 'Move obstacle'
      : _gizmoDrag.isRailNode ? 'Move rail node'
      : _gizmoDrag.timeKind ? `Move ${_gizmoDrag.timeKind}`
      : 'Move note',
    );
    _gizmoDrag.historyStarted = true;
  }
  const startPos = _gizmoDrag.notePos;
  const SCALE = GRID_SCALE * 10;

  // Sensitivity: pixels of mouse movement → world units → note-space units
  const sensitivity = 0.005 / SCALE;

  if (_gizmoDrag.timeKind) {
    // ── Crouch / jump drag — TIME only, identified by value ──
    const beatDelta = dy * 0.01;
    const newBeat = Math.max(0, store.snapBeat(_gizmoDrag.noteBeat + beatDelta));
    const newTimeMs = store.beatToTimeMs(newBeat);
    if (_gizmoDrag.timeKind === 'crouch') store.moveCrouch(_gizmoDrag.lastTimeMs, newTimeMs);
    else store.moveJump(_gizmoDrag.lastTimeMs, newTimeMs);
    _gizmoDrag.lastTimeMs = newTimeMs;
    return;
  }

  if (_gizmoDrag.isSlide) {
    // ── Obstacle drag — X / Y free, Z snaps to the grid steps ──
    const idx = _gizmoDrag.noteIndex;
    if (_gizmoDrag.axis === 'z') {
      // Depth arrow → move in time in whole grid steps: every ~30px of drag
      // advances exactly one snap step. Independent of BEAT_SPACING, so it never
      // flies too far and never gets stuck below one step.
      const step = Math.max(0.0001, store.scrollStep);
      const steps = Math.round(dy / 30);
      const newBeat = Math.max(0, _gizmoDrag.noteBeat + steps * step);
      const newTimeMs = store.beatToTimeMs(newBeat);
      store.updateSlide(idx, {
        time_beats: newBeat, time_ms: newTimeMs, time_seconds: newTimeMs / 1000,
      });
    } else if (_gizmoDrag.axis === 'x') {
      const newX = startPos[0] + dx * 0.012;
      store.updateSlide(idx, { position: [newX, startPos[1], startPos[2]] });
    } else {
      const newY = startPos[1] + (-dy) * 0.012;
      store.updateSlide(idx, { position: [startPos[0], newY, startPos[2]] });
    }
    return;
  }

  if (_gizmoDrag.isRailNode) {
    // ── Rail node drag ──
    const nIdx = _gizmoDrag.noteIndex;
    const rnIdx = _gizmoDrag.railNodeIndex;

    if (_gizmoDrag.axis === 'x') {
      let newX = startPos[0] + dx * sensitivity * SCALE;
      if (store.snapToGrid) [newX] = store.snapToGridPosition(newX, startPos[1]);
      [newX] = _clampToGrid(newX, startPos[1]);
      store.updateRailNode(nIdx, rnIdx, [newX, startPos[1], startPos[2]]);
    } else if (_gizmoDrag.axis === 'y') {
      let newY = startPos[1] + (-dy) * sensitivity * SCALE;
      if (store.snapToGrid) [, newY] = store.snapToGridPosition(startPos[0], newY);
      [, newY] = _clampToGrid(startPos[0], newY);
      store.updateRailNode(nIdx, rnIdx, [startPos[0], newY, startPos[2]]);
    } else {
      // Z axis: move rail node in time
      const beatDelta = dy * 0.01;
      const startBeat = _gizmoDrag.noteBeat;
      const newBeat = Math.max(0, store.snapBeat(startBeat + beatDelta));
      const newTimeMs = store.beatToTimeMs(newBeat);
      store.updateRailNode(nIdx, rnIdx, [startPos[0], startPos[1], newTimeMs * 0.02]);
    }
  } else {
    // ── Regular note drag ── (group = move the whole multi-selection together)
    const idx = _gizmoDrag.noteIndex;
    const group = _gizmoDrag.group;

    if (_gizmoDrag.axis === 'x') {
      let newX = startPos[0] + dx * sensitivity * SCALE;
      if (store.snapToGrid) [newX] = store.snapToGridPosition(newX, startPos[1]);
      [newX] = _clampToGrid(newX, startPos[1]);
      if (group) {
        const deltaX = newX - startPos[0];
        store.moveSelectedNotes(group.map(m => ({
          index: m.index,
          position: [m.startPos[0] + deltaX, m.startPos[1], m.startPos[2]] as [number, number, number],
        })));
      } else {
        store.updateNote(idx, { position: [newX, startPos[1], startPos[2]] as [number, number, number] });
      }
    } else if (_gizmoDrag.axis === 'y') {
      let newY = startPos[1] + (-dy) * sensitivity * SCALE;
      if (store.snapToGrid) [, newY] = store.snapToGridPosition(startPos[0], newY);
      [, newY] = _clampToGrid(startPos[0], newY);
      if (group) {
        const deltaY = newY - startPos[1];
        store.moveSelectedNotes(group.map(m => ({
          index: m.index,
          position: [m.startPos[0], m.startPos[1] + deltaY, m.startPos[2]] as [number, number, number],
        })));
      } else {
        store.updateNote(idx, { position: [startPos[0], newY, startPos[2]] as [number, number, number] });
      }
    } else {
      const beatDelta = dy * 0.01;
      const startBeat = _gizmoDrag.noteBeat;
      const newBeat = Math.max(0, store.snapBeat(startBeat + beatDelta));
      if (group) {
        const db = newBeat - startBeat;
        store.moveSelectedNotes(group.map(m => {
          const mBeat = Math.max(0, m.startBeat + db);
          return { index: m.index, time: { beats: mBeat, ms: store.beatToTimeMs(mBeat) } };
        }));
      } else {
        const newTimeMs = store.beatToTimeMs(newBeat);
        store.updateNote(idx, {
          time_beats: newBeat, time_ms: newTimeMs, time_seconds: newTimeMs / 1000,
          position: [startPos[0], startPos[1], newTimeMs * 0.02] as [number, number, number],
        });
      }
    }
  }
}

function _gizmoWindowUp(_e: PointerEvent) {
  if (!_gizmoDrag.active) return;
  if (_gizmoDrag.isSlide) useEditorStore.getState().sortSlides(); // re-sort after the drag
  if (_gizmoDrag.historyStarted) useEditorStore.getState().endDrag(); // stop suppressing undo
  _gizmoDrag.active = false;
  _gizmoDrag.isRailNode = false;
  _gizmoDrag.isSlide = false;
  _gizmoDrag.timeKind = '';
  _gizmoDrag.group = null;
  _gizmoDrag.historyStarted = false;
  _gizmoDrag.endedAt = Date.now();
  useEditorStore.getState().setGizmoDragAxis(null);
  document.body.style.cursor = '';
  window.removeEventListener('pointermove', _gizmoWindowMove);
  window.removeEventListener('pointerup', _gizmoWindowUp);
}

function GizmoArrow({
  axis, color, direction, noteIndex, isSlide = false, timeMarker,
}: {
  axis: 'x' | 'y' | 'z';
  color: string;
  direction: [number, number, number];
  noteIndex: number;
  /** When true, noteIndex is a SLIDE index and the drag moves the obstacle in time. */
  isSlide?: boolean;
  /** Crouch / jump time-marker — drag moves it in time, identified by its time_ms. */
  timeMarker?: { kind: 'crouch' | 'jump'; timeMs: number; beat: number };
}) {
  const lineLen = 0.45;
  const coneLen = 0.12;
  const coneRad = 0.045;
  const lineRad = 0.012;
  // Invisible hit area — much larger than the visible cone for easier clicking
  const hitRad = 0.12;
  const hitLen = lineLen + coneLen;

  const rot = useMemo((): [number, number, number] => {
    if (axis === 'y') return [0, 0, 0];
    if (axis === 'x') return [0, 0, -Math.PI / 2];
    return [Math.PI / 2, 0, 0];
  }, [axis]);

  const conePos: [number, number, number] = [
    direction[0] * (lineLen + coneLen / 2),
    direction[1] * (lineLen + coneLen / 2),
    direction[2] * (lineLen + coneLen / 2),
  ];
  const linePos: [number, number, number] = [
    direction[0] * lineLen / 2,
    direction[1] * lineLen / 2,
    direction[2] * lineLen / 2,
  ];
  const hitPos: [number, number, number] = [
    direction[0] * hitLen / 2,
    direction[1] * hitLen / 2,
    direction[2] * hitLen / 2,
  ];

  const handleDown = useCallback((e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    const store = useEditorStore.getState();
    const diff = store.getCurrentDifficulty();
    if (!diff) return;

    _gizmoDrag.active = true;
    _gizmoDrag.axis = axis;
    _gizmoDrag.noteIndex = noteIndex;
    _gizmoDrag.isRailNode = false;
    _gizmoDrag.isSlide = isSlide;
    _gizmoDrag.timeKind = timeMarker ? timeMarker.kind : '';
    _gizmoDrag.group = null;
    _gizmoDrag.historyStarted = false;
    _gizmoDrag.startMouseX = e.nativeEvent.clientX;
    _gizmoDrag.startMouseY = e.nativeEvent.clientY;
    if (timeMarker) {
      _gizmoDrag.lastTimeMs = timeMarker.timeMs;
      _gizmoDrag.noteBeat = timeMarker.beat;
      _gizmoDrag.notePos = [0, 0, 0];
    } else if (isSlide) {
      const s = diff.slides[noteIndex];
      if (!s) { _gizmoDrag.active = false; return; }
      // Current world X/Y comes from the per-instance position if set (and
      // plausible — see resolveWallXY) else the wall_type's config default.
      // Must match what's actually rendered, or grabbing a wall whose legacy
      // position was rejected would jump it the instant the drag starts.
      // Z (notePos[2]) preserves any existing position.z so a time-drag
      // doesn't wipe it.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cfg = (store.gameplaySettings.obstacleConfigs as any)[s.wall_type];
      const [curX, curY] = resolveWallXY(s.wall_type, s.position, cfg);
      _gizmoDrag.notePos = [curX, curY, s.position?.[2] ?? 0];
      _gizmoDrag.noteBeat = s.time_beats;
    } else {
      if (noteIndex >= diff.notes.length) { _gizmoDrag.active = false; return; }
      const note = diff.notes[noteIndex];
      _gizmoDrag.notePos = [...note.position] as [number, number, number];
      _gizmoDrag.noteBeat = note.time_beats;
      // If this note is part of a multi-selection, capture every selected note's
      // start so dragging this handle moves the whole group by the same delta.
      const sel = store.selectedNoteIds;
      if (sel.length > 1 && sel.includes(noteIndex)) {
        _gizmoDrag.group = sel
          .filter(i => i >= 0 && i < diff.notes.length)
          .map(i => ({
            index: i,
            startPos: [...diff.notes[i].position] as [number, number, number],
            startBeat: diff.notes[i].time_beats,
          }));
      }
    }

    store.setGizmoDragAxis(axis);
    document.body.style.cursor = 'grabbing';

    // Attach window-level move/up listeners for reliable dragging
    window.addEventListener('pointermove', _gizmoWindowMove);
    window.addEventListener('pointerup', _gizmoWindowUp);
  }, [axis, noteIndex, isSlide, timeMarker]);

  return (
    <group>
      {/* Visible shaft. transparent so it renders in the transparent pass (after
          the transparent obstacle/wall meshes) and, with depthTest off + a high
          renderOrder, always draws ON TOP instead of being hidden inside a wall. */}
      <mesh position={linePos} rotation={rot} renderOrder={21}>
        <cylinderGeometry args={[lineRad, lineRad, lineLen, 6]} />
        <meshBasicMaterial color={color} fog={false} transparent depthTest={false} depthWrite={false} />
      </mesh>
      {/* Visible cone tip */}
      <mesh position={conePos} rotation={rot} renderOrder={21}>
        <coneGeometry args={[coneRad, coneLen, 8]} />
        <meshBasicMaterial color={color} fog={false} transparent depthTest={false} depthWrite={false} />
      </mesh>
      {/* Invisible hit area — covers whole arrow for easy grabbing */}
      <mesh
        position={hitPos}
        rotation={rot}
        renderOrder={22}
        onPointerDown={handleDown}
        onPointerOver={(e: ThreeEvent<PointerEvent>) => { e.stopPropagation(); document.body.style.cursor = 'grab'; }}
        onPointerLeave={() => { if (!_gizmoDrag.active) document.body.style.cursor = ''; }}
      >
        <cylinderGeometry args={[hitRad, hitRad, hitLen, 8]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} depthTest={false} />
      </mesh>
    </group>
  );
}

/** Single gizmo instance rendered per note at the playhead */
function NoteGizmoInstance({ noteIndex }: { noteIndex: number }) {
  const groupRef = useRef<THREE.Group>(null);
  // Keep the handles the same SIZE ON SCREEN whatever the FOV: at the
  // desktop-editor's 35° they'd otherwise cover half the grid.
  const fov = useEditorStore(s => s.interfaceSettings.cameraFov);
  const gizmoScale = Math.max(0.4, Math.min(1.2, fov / 60));

  useFrame(() => {
    if (!groupRef.current) return;
    const store = useEditorStore.getState();
    const diff = store.getCurrentDifficulty();
    if (!diff || noteIndex >= diff.notes.length) { groupRef.current.visible = false; return; }
    const note = diff.notes[noteIndex];
    const rawBeat = chartBeat();
    const x = note.position[0] * GRID_SCALE * 10;
    const y = note.position[1] * GRID_SCALE * 10 + NOTE_Y_OFFSET;
    const z = -(note.time_beats - rawBeat) * BEAT_SPACING;
    groupRef.current.position.set(x, y, z);
    groupRef.current.scale.setScalar(gizmoScale);
    groupRef.current.visible = true;
  });

  return (
    <group ref={groupRef} renderOrder={20}>
      <GizmoArrow axis="x" color="#FF4444" direction={[1, 0, 0]} noteIndex={noteIndex} />
      <GizmoArrow axis="y" color="#44FF44" direction={[0, 1, 0]} noteIndex={noteIndex} />
      <GizmoArrow axis="z" color="#4488FF" direction={[0, 0, -1]} noteIndex={noteIndex} />
    </group>
  );
}

function NoteGizmo() {
  const beatmap = useEditorStore(s => s.beatmap);
  const difficulty = useEditorStore(s =>
    s.beatmap ? s.beatmap.difficulties[s.activeDifficulty] : null,
  );
  const playing = useEditorStore(s => s.playback.playing);
  const currentBeat = useEditorStore(s => s.playback.currentBeat);
  const scrollStep = useEditorStore(s => s.scrollStep);

  // Find all notes AND rail nodes at the current playhead beat
  const { notesAtPlayhead, railNodesAtPlayhead } = useMemo(() => {
    if (!difficulty || !beatmap || playing) return { notesAtPlayhead: [], railNodesAtPlayhead: [] };
    const bpm = beatmap.bpm || 120;
    // Use raw beat (same reference frame as note.time_beats)
    const store = useEditorStore.getState();
    const rawBeat = chartBeat();
    // Match the note "on-grid" pop tolerance (tied to the step) so the move
    // arrows ALWAYS appear whenever the note is on the grid — a fixed 0.01 was
    // tighter than the pop, so the note could pop yet show no gizmo.
    // CAPPED at ~a 1/4 beat: with a coarse step (1/1, 1/2) the old window was
    // ±0.55 beats, so changing the step with Ctrl+wheel lit up move arrows on a
    // whole cluster of notes at once (reviewer report).
    const tolerance = Math.min(0.26, Math.max(0.01, store.scrollStep * 0.55));
    const noteIndices: number[] = [];
    const railNodes: Array<{ noteIndex: number; nodeIndex: number }> = [];

    // Scan a bounded window around the playhead, not the whole chart. Note HEADS
    // live within `tolerance` of rawBeat; a rail NODE at the playhead belongs to
    // a head up to RAIL_LOOKBACK beats behind (rails extend forward in time), so
    // look back that far. RAIL_LOOKBACK matches the WindowedNotes rail cap (64).
    const RAIL_LOOKBACK = 64;
    const from = _lowerBound(difficulty.notes, rawBeat - RAIL_LOOKBACK);
    const to = _upperBound(difficulty.notes, rawBeat + tolerance);
    for (let i = from; i < to; i++) {
      const note = difficulty.notes[i];
      if (Math.abs(note.time_beats - rawBeat) < tolerance) {
        noteIndices.push(i);
      }
      // Also check rail nodes of this note
      if (note.has_rail && note.rail_nodes) {
        for (let j = 0; j < note.rail_nodes.length; j++) {
          const node = note.rail_nodes[j];
          const nodeTimeMs = node[2] / 0.02;
          const nodeBeats = (nodeTimeMs / 1000.0) * (bpm / 60.0);
          if (Math.abs(nodeBeats - rawBeat) < tolerance) {
            railNodes.push({ noteIndex: i, nodeIndex: j });
          }
        }
      }
    }
    return { notesAtPlayhead: noteIndices, railNodesAtPlayhead: railNodes };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [difficulty, beatmap, playing, currentBeat, scrollStep]);

  // Clean up window event listeners on unmount
  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', _gizmoWindowMove);
      window.removeEventListener('pointerup', _gizmoWindowUp);
    };
  }, []);

  if (!beatmap || !difficulty || playing) return null;
  if (notesAtPlayhead.length === 0 && railNodesAtPlayhead.length === 0) return null;

  // A chord can legitimately put 2–4 notes on one beat; beyond that the arrows
  // stop being handles and become a wall of geometry (reviewer report). Past
  // the cap only the SELECTED notes keep their handles.
  const GIZMO_CAP = 4;
  const selected = useEditorStore.getState().selectedNoteIds;
  const gizmoNotes = notesAtPlayhead.length > GIZMO_CAP
    ? notesAtPlayhead.filter(i => selected.includes(i))
    : notesAtPlayhead;

  return (
    <>
      {gizmoNotes.map(idx => (
        <NoteGizmoInstance key={`note-${idx}`} noteIndex={idx} />
      ))}
      {railNodesAtPlayhead.map(({ noteIndex, nodeIndex }) => (
        <RailNodeGizmoInstance
          key={`rn-${noteIndex}-${nodeIndex}`}
          noteIndex={noteIndex}
          railNodeIndex={nodeIndex}
        />
      ))}
    </>
  );
}

// ── Obstacle (slide) move gizmo — TIME-only arrow, centred in the mesh ────────
function SlideGizmoInstance({ slideIndex }: { slideIndex: number }) {
  const groupRef = useRef<THREE.Group>(null);
  const configs = useEditorStore(s => s.gameplaySettings.obstacleConfigs);
  useFrame(() => {
    if (!groupRef.current) return;
    const store = useEditorStore.getState();
    const diff = store.getCurrentDifficulty();
    if (!diff || slideIndex >= diff.slides.length) { groupRef.current.visible = false; return; }
    const s = diff.slides[slideIndex];
    const rawBeat = chartBeat();
    // Centre the gizmo on the wall: its per-instance position if moved, else the
    // wall_type config default.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const cfg = (configs as any)[s.wall_type];
    const xPos = s.position ? s.position[0] : (cfg ? cfg.defaultX : 0);
    const yPos = NOTE_Y_OFFSET + (s.position ? s.position[1] : (cfg ? cfg.defaultY : 0));
    groupRef.current.position.set(xPos, yPos, -(s.time_beats - rawBeat) * BEAT_SPACING);
    groupRef.current.visible = true;
  });
  // Full X / Y / Z handles — X/Y move the wall freely, the depth (Z) arrow moves
  // it in time (snapped to the grid). Arrows draw on top of the wall mesh.
  return (
    <group ref={groupRef} renderOrder={20}>
      <GizmoArrow axis="x" color="#FF4444" direction={[1, 0, 0]} noteIndex={slideIndex} isSlide />
      <GizmoArrow axis="y" color="#44FF44" direction={[0, 1, 0]} noteIndex={slideIndex} isSlide />
      <GizmoArrow axis="z" color="#4488FF" direction={[0, 0, -1]} noteIndex={slideIndex} isSlide />
    </group>
  );
}

function SlideGizmo() {
  const beatmap = useEditorStore(s => s.beatmap);
  const difficulty = useEditorStore(s => (s.beatmap ? s.beatmap.difficulties[s.activeDifficulty] : null));
  const playing = useEditorStore(s => s.playback.playing);
  const currentBeat = useEditorStore(s => s.playback.currentBeat);
  const scrollStep = useEditorStore(s => s.scrollStep);

  const slidesAtPlayhead = useMemo(() => {
    if (!difficulty || !beatmap || playing) return [];
    const bpm = beatmap.bpm || 120;
    const rawBeat = chartBeat();
    const tol = Math.max(0.01, scrollStep * 0.55);
    const idxs: number[] = [];
    difficulty.slides.forEach((s, i) => { if (Math.abs(s.time_beats - rawBeat) < tol) idxs.push(i); });
    return idxs;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [difficulty, beatmap, playing, currentBeat, scrollStep]);

  if (!beatmap || !difficulty || playing || slidesAtPlayhead.length === 0) return null;
  return <>{slidesAtPlayhead.map(i => <SlideGizmoInstance key={`sg-${i}`} slideIndex={i} />)}</>;
}

// ── Crouch / Jump move gizmo — TIME-only arrow, centred over the marker ───────
function TimeMarkerGizmoInstance({ kind, timeMs }: { kind: 'crouch' | 'jump'; timeMs: number }) {
  const groupRef = useRef<THREE.Group>(null);
  const bpm = useEditorStore(s => s.beatmap?.bpm || 120);
  const beat = (timeMs / 1000) * (bpm / 60);
  useFrame(() => {
    if (!groupRef.current) return;
    const store = useEditorStore.getState();
    const rawBeat = chartBeat();
    groupRef.current.position.set(0, NOTE_Y_OFFSET, -(beat - rawBeat) * BEAT_SPACING);
  });
  return (
    <group ref={groupRef} renderOrder={20}>
      <GizmoArrow axis="y" color="#44FF44" direction={[0, 1, 0]} noteIndex={0} timeMarker={{ kind, timeMs, beat }} />
    </group>
  );
}

function TimeMarkerGizmo() {
  const beatmap = useEditorStore(s => s.beatmap);
  const difficulty = useEditorStore(s => (s.beatmap ? s.beatmap.difficulties[s.activeDifficulty] : null));
  const playing = useEditorStore(s => s.playback.playing);
  const currentBeat = useEditorStore(s => s.playback.currentBeat);
  const scrollStep = useEditorStore(s => s.scrollStep);

  const targets = useMemo(() => {
    if (!difficulty || !beatmap || playing) return [] as Array<{ kind: 'crouch' | 'jump'; timeMs: number }>;
    const bpm = beatmap.bpm || 120;
    const rawBeat = chartBeat();
    const tol = Math.max(0.01, scrollStep * 0.55);
    const within = (t: number) => Math.abs((t / 1000) * (bpm / 60) - rawBeat) < tol;
    const out: Array<{ kind: 'crouch' | 'jump'; timeMs: number }> = [];
    difficulty.crouches.forEach(t => { if (within(t)) out.push({ kind: 'crouch', timeMs: t }); });
    difficulty.jumps.forEach(t => { if (within(t)) out.push({ kind: 'jump', timeMs: t }); });
    return out;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [difficulty, beatmap, playing, currentBeat, scrollStep]);

  if (!beatmap || !difficulty || playing || targets.length === 0) return null;
  return <>{targets.map((t, i) => (
    <TimeMarkerGizmoInstance key={`tmg-${t.kind}-${i}`} kind={t.kind} timeMs={t.timeMs} />
  ))}</>;
}

// ── Rail Node Gizmo (3-axis handle on selected rail control points) ───────────

function RailNodeGizmoArrow({
  axis, color, direction, noteIndex, railNodeIndex,
}: {
  axis: 'x' | 'y' | 'z';
  color: string;
  direction: [number, number, number];
  noteIndex: number;
  railNodeIndex: number;
}) {
  const lineLen = 0.35;
  const coneLen = 0.10;
  const coneRad = 0.035;
  const lineRad = 0.010;
  const hitRad = 0.10;
  const hitLen = lineLen + coneLen;

  const rot = useMemo((): [number, number, number] => {
    if (axis === 'y') return [0, 0, 0];
    if (axis === 'x') return [0, 0, -Math.PI / 2];
    return [Math.PI / 2, 0, 0];
  }, [axis]);

  const conePos: [number, number, number] = [
    direction[0] * (lineLen + coneLen / 2),
    direction[1] * (lineLen + coneLen / 2),
    direction[2] * (lineLen + coneLen / 2),
  ];
  const linePos: [number, number, number] = [
    direction[0] * lineLen / 2,
    direction[1] * lineLen / 2,
    direction[2] * lineLen / 2,
  ];
  const hitPos: [number, number, number] = [
    direction[0] * hitLen / 2,
    direction[1] * hitLen / 2,
    direction[2] * hitLen / 2,
  ];

  const handleDown = useCallback((e: ThreeEvent<PointerEvent>) => {
    e.stopPropagation();
    const store = useEditorStore.getState();
    const diff = store.getCurrentDifficulty();
    if (!diff || noteIndex >= diff.notes.length) return;
    const note = diff.notes[noteIndex];
    if (!note.rail_nodes || railNodeIndex >= note.rail_nodes.length) return;
    const node = note.rail_nodes[railNodeIndex];
    const nodeTimeMs = node[2] / 0.02;
    const bpm = store.beatmap?.bpm || 120;
    const nodeBeats = (nodeTimeMs / 1000.0) * (bpm / 60.0);

    _gizmoDrag.active = true;
    _gizmoDrag.axis = axis;
    _gizmoDrag.noteIndex = noteIndex;
    _gizmoDrag.isRailNode = true;
    _gizmoDrag.railNodeIndex = railNodeIndex;
    _gizmoDrag.startMouseX = e.nativeEvent.clientX;
    _gizmoDrag.startMouseY = e.nativeEvent.clientY;
    _gizmoDrag.notePos = [...node] as [number, number, number];
    _gizmoDrag.noteBeat = nodeBeats;

    store.setGizmoDragAxis(axis);
    document.body.style.cursor = 'grabbing';

    window.addEventListener('pointermove', _gizmoWindowMove);
    window.addEventListener('pointerup', _gizmoWindowUp);
  }, [axis, noteIndex, railNodeIndex]);

  return (
    <group>
      <mesh position={linePos} rotation={rot} renderOrder={21}>
        <cylinderGeometry args={[lineRad, lineRad, lineLen, 6]} />
        <meshBasicMaterial color={color} fog={false} transparent depthTest={false} depthWrite={false} />
      </mesh>
      <mesh position={conePos} rotation={rot} renderOrder={21}>
        <coneGeometry args={[coneRad, coneLen, 8]} />
        <meshBasicMaterial color={color} fog={false} transparent depthTest={false} depthWrite={false} />
      </mesh>
      <mesh
        position={hitPos}
        rotation={rot}
        renderOrder={22}
        onPointerDown={handleDown}
        onPointerOver={(e: ThreeEvent<PointerEvent>) => { e.stopPropagation(); document.body.style.cursor = 'grab'; }}
        onPointerLeave={() => { if (!_gizmoDrag.active) document.body.style.cursor = ''; }}
      >
        <cylinderGeometry args={[hitRad, hitRad, hitLen, 8]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} depthTest={false} />
      </mesh>
    </group>
  );
}

function RailNodeGizmoInstance({ noteIndex, railNodeIndex }: { noteIndex: number; railNodeIndex: number }) {
  const groupRef = useRef<THREE.Group>(null);

  useFrame(() => {
    if (!groupRef.current) return;
    const store = useEditorStore.getState();
    const diff = store.getCurrentDifficulty();
    if (!diff || noteIndex >= diff.notes.length) { groupRef.current.visible = false; return; }
    const note = diff.notes[noteIndex];
    if (!note.rail_nodes || railNodeIndex >= note.rail_nodes.length) { groupRef.current.visible = false; return; }
    const node = note.rail_nodes[railNodeIndex];
    const nodeTimeMs = node[2] / 0.02;
    const bpm = store.beatmap?.bpm || 120;
    const nodeBeats = (nodeTimeMs / 1000.0) * (bpm / 60.0);
    const rawBeat = chartBeat();
    const x = node[0] * GRID_SCALE * 10;
    const y = node[1] * GRID_SCALE * 10 + NOTE_Y_OFFSET;
    const z = -(nodeBeats - rawBeat) * BEAT_SPACING;
    groupRef.current.position.set(x, y, z);
    groupRef.current.visible = true;
  });

  return (
    <group ref={groupRef} renderOrder={20}>
      <RailNodeGizmoArrow axis="x" color="#FF4444" direction={[1, 0, 0]} noteIndex={noteIndex} railNodeIndex={railNodeIndex} />
      <RailNodeGizmoArrow axis="y" color="#44FF44" direction={[0, 1, 0]} noteIndex={noteIndex} railNodeIndex={railNodeIndex} />
      <RailNodeGizmoArrow axis="z" color="#4488FF" direction={[0, 0, -1]} noteIndex={noteIndex} railNodeIndex={railNodeIndex} />
    </group>
  );
}

function RailNodeGizmo() {
  const selectedRailNode = useEditorStore(s => s.selectedRailNode);
  const beatmap = useEditorStore(s => s.beatmap);
  const playing = useEditorStore(s => s.playback.playing);

  useEffect(() => {
    return () => {
      window.removeEventListener('pointermove', _gizmoWindowMove);
      window.removeEventListener('pointerup', _gizmoWindowUp);
    };
  }, []);

  if (!beatmap || playing || !selectedRailNode) return null;

  return (
    <RailNodeGizmoInstance
      noteIndex={selectedRailNode.noteIndex}
      railNodeIndex={selectedRailNode.nodeIndex}
    />
  );
}

// ── Click-to-place Handler (for tunnel depth clicks — obstacles, rail nodes) ─

function PlacementPlane() {
  const toolMode = useEditorStore(s => s.toolMode);
  const beatmap = useEditorStore(s => s.beatmap);

  const handleClick = useCallback((e: ThreeEvent<MouseEvent>) => {
    if (!beatmap) return;

    const store = useEditorStore.getState();
    const point = e.point;
    // Use raw beat (same frame as note.time_beats) to avoid offset mismatch
    const bpm = store.beatmap?.bpm || 120;
    const rawBeat = chartBeat();
    const beat = store.snapBeat(rawBeat + (-point.z / BEAT_SPACING));
    const timeMs = store.beatToTimeMs(beat);
    const timeSeconds = timeMs / 1000;

    // ── Drag-to-move: if a drag is in progress, finish it here ─
    if (store.draggingNoteIndex !== null) {
      e.stopPropagation();
      let x = point.x / (GRID_SCALE * 10);
      let y = (point.y - NOTE_Y_OFFSET) / (GRID_SCALE * 10);
      if (store.snapToGrid) {
        [x, y] = store.snapToGridPosition(x, y);
      }
      [x, y] = _clampToGrid(x, y);
      store.finishDrag(beat, x, y);
      return;
    }

    // ── Empty-floor click with the Select tool clears the selection + loop. ──
    if (toolMode === 'select' && (store.selectedNoteIds.length > 0 || store.selectedSlideIds.length > 0 || store.loopRegion)) {
      e.stopPropagation();
      if (store.selectedNoteIds.length > 0 || store.selectedSlideIds.length > 0) store.clearSelection();
      if (store.loopRegion) store.setLoopRegion(null);
      return;
    }

    // ── Rail node placement along tunnel depth ──
    if (store.railPlacementActive && RAIL_TOOL_MODES.has(toolMode)) {
      e.stopPropagation();
      let x = point.x / (GRID_SCALE * 10);
      let y = (point.y - NOTE_Y_OFFSET) / (GRID_SCALE * 10);
      if (store.snapToGrid) {
        [x, y] = store.snapToGridPosition(x, y);
      }
      [x, y] = _clampToGrid(x, y);
      store.addRailNode([x, y, timeMs * 0.02]);
      return;
    }

    // ── Obstacle placement ────────────────────────────────────
    if (OBSTACLE_TOOL_MODES.has(toolMode)) {
      e.stopPropagation();
      const wallType = TOOL_TO_WALL_TYPE[toolMode];
      if (!wallType) return;

      const slide: import('../../types/beatmap').Slide = {
        time_ms: timeMs,
        time_seconds: timeSeconds,
        time_beats: beat,
        slide_type: 0,
        wall_type: wallType,
        initialized: true,
      };
      store.addSlide(slide);
      return;
    }

    // ── Jump placement ────────────────────────────────────────
    if (toolMode === 'jump') {
      e.stopPropagation();
      store.toggleJumpAtBeat(beat);
      return;
    }

    // ── Flash event placement ──────────────────────────────────
    if (toolMode === 'flash_effect') {
      e.stopPropagation();
      const fd = store.flashDefaults;
      store.addFlashEvent({
        time_beats: beat,
        color: fd.color,
        intensity: fd.intensity,
        fadeInTime: fd.fadeInTime,
        fadeOutTime: fd.fadeOutTime,
        duration: fd.duration,
        repeat: 1, // normal single pulse; strobe is opt-in per light via the popup
        frequency: fd.frequency,
      });
      spawnLightBurst(beat);
      // Don't auto-open the settings popup — click the bulb to edit it.
      return;
    }

    // ── Bookmark placement ──────────────────────────────────────
    if (toolMode === 'bookmark') {
      e.stopPropagation();
      placeBookmarkAt(beat); // adds + bursts + opens the rename popup
      return;
    }
  }, [toolMode, beatmap]);

  return (
    <group>
      {/* Invisible click target: vertical plane spanning tunnel depth */}
      <mesh
        position={[0, TUNNEL_HEIGHT / 2, -TUNNEL_LENGTH / 2]}
        onClick={handleClick}
        renderOrder={-1}
      >
        <planeGeometry args={[TUNNEL_WIDTH, TUNNEL_LENGTH]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
      {/* Horizontal floor plane for depth clicks */}
      <mesh
        position={[0, NOTE_Y_OFFSET, -TUNNEL_LENGTH / 2]}
        rotation={[-Math.PI / 2, 0, 0]}
        onClick={handleClick}
        renderOrder={-1}
      >
        <planeGeometry args={[TUNNEL_WIDTH, TUNNEL_LENGTH]} />
        <meshBasicMaterial transparent opacity={0} depthWrite={false} />
      </mesh>
    </group>
  );
}

// ── Camera Controller ────────────────────────────────────────────────────────

// Built-in camera presets (fallback when user has no overrides)
// DEFAULT matches the desktop (Unity) editor's [Default Camera]: it sits 3
// units back, level with the grid (Unity: y 0.08 above a grid centred on 0 →
// here NOTE_Y_OFFSET + 0.08), with NO downward tilt and a 35° FOV. Reviewers
// asked for the same framing they know from the Unity build; the old pose was
// lower (1.6) and tilted (-0.15), which squashed the top row of the grid.
const BUILTIN_CAMERA_PRESETS: Record<string, { position: [number, number, number]; rotation: [number, number, number] }> = {
  // Unity's [Default Camera] is 3 back / level / 35° FOV, but its rig looks
  // slightly DOWN the track — reviewers want that: enough tilt to see the
  // runway ahead, with the trigger line sitting around the middle of the view.
  default: { position: [0, 2.15, 3.2], rotation: [-0.21, 0, 0] },
  left: { position: [-3, 2.15, 1], rotation: [-0.12, -0.8, 0] },
  right: { position: [3, 2.15, 1], rotation: [-0.12, 0.8, 0] },
};

/** FOV of the desktop editor's default camera. */
export const UNITY_DEFAULT_FOV = 35;

// Exported so SettingsDialog can reference them as defaults
export { BUILTIN_CAMERA_PRESETS };

// Console pulls the camera back vs VR (the hoverboard track reads too close
// otherwise — user feedback), and pulls back further during an air section so
// the floor→air jump feels more open/distant (mirrors the game's JumpLong
// camera preset). All tunable.
const CONSOLE_CAM_BACK = 5;
const CONSOLE_CAM_UP = 1;
const AIR_CAM_BACK = 4;    // extra pull-back in the air (toned down — rider stays readable)
const AIR_CAM_UP = 1.8;
const AIR_FOV_PLUS = 7;    // widen FOV in the air (the game's JumpLong feel, gentler)

function CameraController() {
  const { camera } = useThree();
  const cameraView = useEditorStore(s => s.cameraView);
  const customPos = useEditorStore(s => s.customCameraPosition);
  const customRot = useEditorStore(s => s.customCameraRotation);
  // User-overridable preset cameras
  const defaultPos = useEditorStore(s => s.defaultCameraPosition);
  const defaultRot = useEditorStore(s => s.defaultCameraRotation);
  const leftPos = useEditorStore(s => s.leftCameraPosition);
  const leftRot = useEditorStore(s => s.leftCameraRotation);
  const rightPos = useEditorStore(s => s.rightCameraPosition);
  const rightRot = useEditorStore(s => s.rightCameraRotation);
  const isConsole = useEditorStore(s => s.activeView === 'console');
  const difficulty = useEditorStore(s => (s.beatmap ? s.beatmap.difficulties[s.activeDifficulty] : null));
  const bpm = useEditorStore(s => s.beatmap?.bpm || 120);
  const airSpans = useMemo(() => consoleAirSpans(difficulty?.notes, bpm), [difficulty?.notes, bpm]);

  // The resting (preset) camera position — Console adds a back/up offset; the
  // useFrame below adds the air pull-back on top of it.
  const basePos = useRef<[number, number, number]>([0, 1.88, 3]);
  const airLerp = useRef(0);
  const baseFov = useRef(0);

  // FOV is a live setting, but the <Canvas camera={{fov}}> prop only applies at
  // creation — so a change in Settings (or the Camera View ⚙) did nothing until
  // a reload. Push it onto the camera whenever it changes.
  const cameraFov = useEditorStore(s => s.interfaceSettings.cameraFov);
  useEffect(() => {
    const persp = camera as THREE.PerspectiveCamera;
    if (!persp.isPerspectiveCamera) return;
    if (Math.abs(persp.fov - cameraFov) > 0.01) {
      persp.fov = cameraFov;
      persp.updateProjectionMatrix();
    }
    baseFov.current = cameraFov;   // re-latch the Console air-FOV baseline
  }, [camera, cameraFov]);

  useEffect(() => {
    // Free and custom both use UnityCameraControls — skip preset snap for free
    if (cameraView === 'free') return;

    if (cameraView === 'custom') {
      const def = BUILTIN_CAMERA_PRESETS['default'];
      const cp = (customPos && customRot) ? customPos : def.position;
      const cr = (customPos && customRot) ? customRot : def.rotation;
      // Console adds the same back/up offset on top of the user's custom view
      // (non-destructive — the saved customPos is untouched).
      const fpos: [number, number, number] = isConsole
        ? [cp[0], cp[1] + CONSOLE_CAM_UP, cp[2] + CONSOLE_CAM_BACK]
        : [cp[0], cp[1], cp[2]];
      camera.position.set(...fpos);
      camera.rotation.set(...cr);
      basePos.current = fpos;
      return;
    }

    // Use user override if set, otherwise built-in preset
    const overrides: Record<string, { pos: [number, number, number] | null; rot: [number, number, number] | null }> = {
      default: { pos: defaultPos, rot: defaultRot },
      left: { pos: leftPos, rot: leftRot },
      right: { pos: rightPos, rot: rightRot },
    };
    const override = overrides[cameraView];
    const builtin = BUILTIN_CAMERA_PRESETS[cameraView];
    if (builtin) {
      const pos = override?.pos || builtin.position;
      const rot = override?.rot || builtin.rotation;
      // Console sits further back; the useFrame pulls back more during air.
      const fpos: [number, number, number] = isConsole
        ? [pos[0], pos[1] + CONSOLE_CAM_UP, pos[2] + CONSOLE_CAM_BACK]
        : [pos[0], pos[1], pos[2]];
      camera.position.set(...fpos);
      camera.rotation.set(...rot);
      basePos.current = fpos;
    }
  }, [cameraView, camera, customPos, customRot, defaultPos, defaultRot, leftPos, leftRot, rightPos, rightRot, isConsole]);

  // Console only: ease the camera back/up while the playhead is inside an air
  // section (floor→air), then return. Overrides position each frame; VR/Flow
  // and free/custom views are untouched (early return → useEffect snap stands).
  useFrame(() => {
    if (!isConsole || cameraView === 'free') return;
    const cb = chartBeat();
    let inAir = false;
    for (let i = 0; i < airSpans.length; i++) {
      if (cb >= airSpans[i][0] && cb <= airSpans[i][1]) { inAir = true; break; }
    }
    airLerp.current += ((inAir ? 1 : 0) - airLerp.current) * 0.05;
    const e = airLerp.current;
    const bp = basePos.current;
    camera.position.set(bp[0], bp[1] + e * AIR_CAM_UP, bp[2] + e * AIR_CAM_BACK);
    // Widen FOV in the air (mirrors the game's JumpLong FOV punch).
    const persp = camera as THREE.PerspectiveCamera;
    if (persp.isPerspectiveCamera) {
      if (baseFov.current === 0) baseFov.current = persp.fov;
      const targetFov = baseFov.current + e * AIR_FOV_PLUS;
      if (Math.abs(persp.fov - targetFov) > 0.01) {
        persp.fov = targetFov;
        persp.updateProjectionMatrix();
      }
    }
  });

  // Only 'free' mode gets Unity-style camera controls.
  // 'custom' mode is locked — position/rotation set via Settings dialog.
  if (cameraView === 'free') {
    return <UnityCameraControls />;
  }

  return null;
}

// ── Scroll-to-scrub Handler ─────────────────────────────────────────────────

function ScrollHandler() {
  const { gl } = useThree();
  const setCurrentBeat = useEditorStore(s => s.setCurrentBeat);
  const playing = useEditorStore(s => s.playback.playing);
  const scrollStep = useEditorStore(s => s.scrollStep);
  const cameraView = useEditorStore(s => s.cameraView);

  useEffect(() => {
    const canvas = gl.domElement;

    const onWheel = (e: WheelEvent) => {
      // In free camera mode, let OrbitControls handle scroll
      if (cameraView === 'free') return;
      // Ctrl+wheel cycles step size (handled by useKeybindingEngine) — don't scroll timeline
      if (e.ctrlKey || e.metaKey) return;

      e.preventDefault();
      const state = useEditorStore.getState();
      // Scrub works while playing too (like Flow): seek the audio to the new
      // position and keep playing from there.
      const isPlaying = state.playback.playing;

      // Shift+wheel — jump to the previous/next ACTUAL note (ignores the snap
      // grid). Seeks straight to the note's time so it lands on the playhead.
      if (e.shiftKey) {
        const diff = state.beatmap?.difficulties[state.activeDifficulty];
        if (diff && diff.notes.length) {
          const offsetSec = (state.beatmap?.offset || 0) / 1000; // note times are chart-relative
          const cur = state.playback.currentTime;
          const eps = 0.001;
          let target: number | null = null;
          if (e.deltaY < 0) { // wheel up = forward → next note
            for (const n of diff.notes) { const t = n.time_ms / 1000 + offsetSec; if (t > cur + eps) { target = t; break; } }
          } else {            // wheel down = backward → previous note
            for (let i = diff.notes.length - 1; i >= 0; i--) { const t = diff.notes[i].time_ms / 1000 + offsetSec; if (t < cur - eps) { target = t; break; } }
          }
          if (target !== null) { state.setCurrentTime(target); if (isPlaying) seekAudio(target); playScrollTick(); }
        }
        return;
      }

      const step = state.scrollStep;
      const delta = e.deltaY > 0 ? -step : step;
      const maxBeat = state.playback.duration > 0
        ? ((state.playback.duration - (state.beatmap?.offset || 0) / 1000) / 60) * (state.beatmap?.bpm || 120)
        : Infinity;
      const newBeat = Math.min(maxBeat, Math.max(0, state.playback.currentBeat + delta));

      if (newBeat !== state.playback.currentBeat) {
        setCurrentBeat(newBeat);
        if (isPlaying) {
          const bpm = state.beatmap?.bpm || 120;
          const offsetSec = (state.beatmap?.offset || 0) / 1000; // offset is stored in ms
          seekAudio((newBeat / bpm) * 60 + offsetSec);
        }
        playScrollTick();
      }
    };

    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [gl, setCurrentBeat, playing, scrollStep, cameraView]);

  return null;
}

// Set when a right-press lands on a note (which deletes it) so the canvas-level
// loop handler skips that event instead of arming/clearing the loop region.
let _noteRightClick = false;

// ── Box-select (left-drag marquee) ────────────────────────────────────────────
// Left-drag on the runway draws a selection box and selects every note whose
// projected centre falls inside it. Shift = add, Ctrl/Cmd = subtract. A plain
// click (no drag past THRESH) is left untouched so note placement / click-select
// still work. Skipped in free-cam (OrbitControls owns left-drag) and on Alt
// (note free-drag).
function BoxSelectHandler() {
  const { gl, camera } = useThree();
  useEffect(() => {
    const canvas = gl.domElement;
    const THRESH = 5;
    let active: null | 'left' | 'right' = null;
    let moved = false;
    let mode: 'replace' | 'add' | 'subtract' = 'replace';
    let start = { x: 0, y: 0 };
    let boxEl: HTMLDivElement | null = null;
    let loopStartBeat = 0;
    const v = new THREE.Vector3();
    const ray = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    const hit = new THREE.Vector3();
    const floor = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0); // y = 0

    const local = (e: PointerEvent) => {
      const r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };

    // Snapped raw-beat under the cursor (raycast onto the floor), restricted to
    // the runway. Same frame as note.time_beats so the loop aligns with notes.
    const screenToBeat = (p: { x: number; y: number }): number | null => {
      const r = canvas.getBoundingClientRect();
      if (!r.width || !r.height) return null;
      ndc.x = (p.x / r.width) * 2 - 1;
      ndc.y = -(p.y / r.height) * 2 + 1;
      ray.setFromCamera(ndc, camera);
      if (!ray.ray.intersectPlane(floor, hit)) return null;
      if (Math.abs(hit.x) > TUNNEL_WIDTH / 2 + 1) return null;
      const st = useEditorStore.getState();
      const rawBeat = chartBeat();
      const b = rawBeat - hit.z / BEAT_SPACING;
      return Math.max(0, st.snapBeat ? st.snapBeat(b) : Math.round(b));
    };

    const removeBox = () => { if (boxEl) { boxEl.remove(); boxEl = null; } };

    const commit = (p: { x: number; y: number }) => {
      const st = useEditorStore.getState();
      const r = canvas.getBoundingClientRect();
      const minX = Math.min(start.x, p.x), maxX = Math.max(start.x, p.x);
      const minY = Math.min(start.y, p.y), maxY = Math.max(start.y, p.y);
      const rawBeat = chartBeat();

      // While the Shapes panel is open, the marquee selects DECOR GROUPS
      // (a group is hit if ANY of its walls' projected centre lands in the box)
      // instead of notes. Shift = add, Ctrl/Cmd = subtract, plain = replace.
      if (st.shapeDesignerOpen) {
        const hitGroups = new Set<string>();
        for (const g of st.designShapes) {
          for (const w of sampleDesignShape(g)) {
            v.set(w.x, NOTE_Y_OFFSET + w.y, -(w.beat - rawBeat) * BEAT_SPACING);
            v.project(camera);
            if (v.z < -1 || v.z > 1) continue;
            const sx = (v.x * 0.5 + 0.5) * r.width;
            const sy = (1 - (v.y * 0.5 + 0.5)) * r.height;
            if (sx >= minX && sx <= maxX && sy >= minY && sy <= maxY) { hitGroups.add(g.id); break; }
          }
        }
        const cur = st.selectedDesignShapeIds;
        const hitArr = Array.from(hitGroups);
        let nextIds: string[];
        if (mode === 'add') nextIds = Array.from(new Set([...cur, ...hitArr]));
        else if (mode === 'subtract') nextIds = cur.filter(id => !hitGroups.has(id));
        else nextIds = hitArr;
        st.setSelectedDesignShapeIds(nextIds);
        return;
      }

      const diff = st.beatmap?.difficulties[st.activeDifficulty];
      if (!diff) return;
      const hits: number[] = [];
      diff.notes.forEach((note: Note, i: number) => {
        v.set(
          note.position[0] * GRID_SCALE * 10,
          note.position[1] * GRID_SCALE * 10 + NOTE_Y_OFFSET,
          -(note.time_beats - rawBeat) * BEAT_SPACING,
        );
        v.project(camera);
        if (v.z < -1 || v.z > 1) return; // behind camera
        const sx = (v.x * 0.5 + 0.5) * r.width;
        const sy = (1 - (v.y * 0.5 + 0.5)) * r.height;
        if (sx >= minX && sx <= maxX && sy >= minY && sy <= maxY) hits.push(i);
      });
      const cur = st.selectedNoteIds;
      let next: number[];
      if (mode === 'add') next = Array.from(new Set([...cur, ...hits]));
      else if (mode === 'subtract') next = cur.filter(i => !hits.includes(i));
      else next = hits;
      st.selectNotes(next);

      // Also marquee-select regular obstacles (walls) so they can be deleted
      // together with notes. Uses the same placement math as ObstacleModel.
      const slideHits: number[] = [];
      (diff.slides || []).forEach((slide: import('../../types/beatmap').Slide, i: number) => {
        const cfg = (st.gameplaySettings.obstacleConfigs as any)[slide.wall_type];
        const [sxw, syw] = resolveWallXY(slide.wall_type, slide.position, cfg);
        v.set(sxw, NOTE_Y_OFFSET + syw, -(slide.time_beats - rawBeat) * BEAT_SPACING);
        v.project(camera);
        if (v.z < -1 || v.z > 1) return;
        const px = (v.x * 0.5 + 0.5) * r.width;
        const py = (1 - (v.y * 0.5 + 0.5)) * r.height;
        if (px >= minX && px <= maxX && py >= minY && py <= maxY) slideHits.push(i);
      });
      const curS = st.selectedSlideIds;
      let nextS: number[];
      if (mode === 'add') nextS = Array.from(new Set([...curS, ...slideHits]));
      else if (mode === 'subtract') nextS = curS.filter(i => !slideHits.includes(i));
      else nextS = slideHits;
      st.selectSlides(nextS);
    };

    const onDown = (e: PointerEvent) => {
      if (useEditorStore.getState().cameraView === 'free') return;
      if (e.button === 2) {
        // Right press on a note deletes it (handled by the note) — skip so we
        // don't also clear the loop.
        if (_noteRightClick) { e.preventDefault(); return; }
        // Right press → arm the loop tool. A drag (on the runway) defines the
        // region; a plain right-click anywhere clears it. We arm regardless of
        // where the press lands so clearing always works.
        e.preventDefault();
        active = 'right'; moved = false; start = local(e);
        const b = screenToBeat(local(e));
        loopStartBeat = b == null ? NaN : b;
        return;
      }
      if (e.button !== 0 || e.altKey) return;
      // Don't start a selection marquee while a note is being moved (free
      // alt-drag or gizmo-arrow drag) or a light is being slid — the move owns
      // the pointer.
      if (_ctrlDrag.active || _gizmoDrag.active || _flashDrag.active || _bookmarkDrag.active || isDesignGizmoDragActive()) return;
      active = 'left'; moved = false; start = local(e);
      mode = e.shiftKey ? 'add' : (e.ctrlKey || e.metaKey) ? 'subtract' : 'replace';
    };
    const onMove = (e: PointerEvent) => {
      if (!active) return;
      // A note move (free alt-drag or gizmo-arrow drag) arms via R3F's pointer
      // listener, which runs AFTER this child's onDown — so the flag isn't set
      // yet at onDown. Cancel any selection marquee here, where it IS set.
      if (active === 'left' && (_ctrlDrag.active || _gizmoDrag.active || _flashDrag.active || _bookmarkDrag.active || isDesignGizmoDragActive())) {
        active = null; moved = false; removeBox(); return;
      }
      if (active === 'right') {
        const rp = local(e);
        if (Math.abs(rp.x - start.x) > THRESH || Math.abs(rp.y - start.y) > THRESH) moved = true;
        e.preventDefault();
        // Only build a region once we have a valid start beat on the runway.
        if (!Number.isNaN(loopStartBeat)) {
          const endB = screenToBeat(rp);
          if (endB != null && endB !== loopStartBeat) {
            useEditorStore.getState().setLoopRegion({
              startBeat: Math.min(loopStartBeat, endB),
              endBeat: Math.max(loopStartBeat, endB),
            });
          }
        }
        return;
      }
      const p = local(e);
      if (!moved && (Math.abs(p.x - start.x) > THRESH || Math.abs(p.y - start.y) > THRESH)) {
        moved = true;
        boxEl = document.createElement('div');
        Object.assign(boxEl.style, {
          position: 'fixed', pointerEvents: 'none', zIndex: '60',
          border: '1px solid var(--accent)',
          background: 'oklch(0.70 calc(0.2 * var(--accent-c)) var(--accent-h) / 0.12)',
          borderRadius: '2px',
        } as CSSStyleDeclaration);
        document.body.appendChild(boxEl);
      }
      if (moved && boxEl) {
        const r = canvas.getBoundingClientRect();
        boxEl.style.left = (r.left + Math.min(start.x, p.x)) + 'px';
        boxEl.style.top = (r.top + Math.min(start.y, p.y)) + 'px';
        boxEl.style.width = Math.abs(p.x - start.x) + 'px';
        boxEl.style.height = Math.abs(p.y - start.y) + 'px';
      }
    };
    const onUp = (e: PointerEvent) => {
      if (!active) return;
      if (active === 'right') {
        // Right-click without a drag → clear the loop.
        if (!moved) useEditorStore.getState().setLoopRegion(null);
        active = null; moved = false;
        return;
      }
      if (moved) {
        commit(local(e));
        // A box-select ends with a pointerup on empty space, which would also
        // fire Canvas onPointerMissed — suppress that so it doesn't immediately
        // clear the selection we just made.
        _suppressMissedClear = true;
        requestAnimationFrame(() => { _suppressMissedClear = false; });
      } else {
        // A plain CLICK (no drag). While the Shapes panel is open this
        // selects the nearest decor group (or clears when you click empty
        // space) — handled here in the native canvas handler so it never races
        // with R3F's own click/raycast on the same canvas.
        const st = useEditorStore.getState();
        if (st.shapeDesignerOpen && st.toolMode === 'select') {
          const p = local(e);
          const rr = canvas.getBoundingClientRect();
          // Raycast the ACTUAL decor wall geometry (precise for any wall size,
          // unlike centre-proximity which missed big/curved decorations).
          ndc.x = (p.x / rr.width) * 2 - 1;
          ndc.y = -(p.y / rr.height) * 2 + 1;
          ray.setFromCamera(ndc, camera);
          const bestId = pickDesignAt(ray);
          if (bestId) {
            if (e.shiftKey) {
              const cur = st.selectedDesignShapeIds;
              st.setSelectedDesignShapeIds(cur.includes(bestId) ? cur.filter(x => x !== bestId) : [...cur, bestId]);
            } else {
              st.selectDesignShape(bestId);
            }
            _suppressMissedClear = true;
            requestAnimationFrame(() => { _suppressMissedClear = false; });
          } else if (!e.shiftKey) {
            st.setSelectedDesignShapeIds([]);   // clicked empty → clear decor selection
          }
        }
      }
      active = null; moved = false;
      removeBox();
    };
    // Suppress the browser context menu so right-drag/right-click drive the loop.
    const onContextMenu = (e: MouseEvent) => e.preventDefault();

    // Double-click a decor wall → open the Shapes panel + select that group
    // (works even when the panel is closed, unlike the single-click select which
    // only runs while the panel is already open).
    const onDblClick = (e: MouseEvent) => {
      const st = useEditorStore.getState();
      if (st.cameraView === 'free') return;
      const rr = canvas.getBoundingClientRect();
      if (!rr.width || !rr.height) return;
      ndc.x = ((e.clientX - rr.left) / rr.width) * 2 - 1;
      ndc.y = -((e.clientY - rr.top) / rr.height) * 2 + 1;
      ray.setFromCamera(ndc, camera);
      const id = pickDesignAt(ray);
      if (id) {
        st.selectDesignShape(id);
        st.setShapeDesignerOpen(true);
      }
    };

    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('contextmenu', onContextMenu);
    canvas.addEventListener('dblclick', onDblClick);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('contextmenu', onContextMenu);
      canvas.removeEventListener('dblclick', onDblClick);
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
      removeBox();
    };
  }, [gl, camera]);
  return null;
}

// ── Loop region band ──────────────────────────────────────────────────────────
// Teal floor band marking the right-drag loop (start..end beats). Rendered as a
// child of BeatScrollGroup, so it scrolls with the notes automatically.
function LoopOverlay() {
  const loop = useEditorStore(s => s.loopRegion);
  if (!loop) return null;
  const hw = TUNNEL_WIDTH / 2;
  const zStart = -loop.startBeat * BEAT_SPACING;
  const zEnd = -loop.endBeat * BEAT_SPACING;
  const zCenter = (zStart + zEnd) / 2;
  const length = Math.max(0.01, Math.abs(zStart - zEnd));
  return (
    <group>
      <mesh position={[0, 0.012, zCenter]} rotation={[-Math.PI / 2, 0, 0]} raycast={() => null}>
        <planeGeometry args={[TUNNEL_WIDTH, length]} />
        <meshBasicMaterial color="#1FE0C8" transparent opacity={0.12} depthWrite={false} />
      </mesh>
      <Line points={[[-hw, 0.02, zStart], [hw, 0.02, zStart]]} color="#1FE0C8" lineWidth={2} />
      <Line points={[[-hw, 0.02, zEnd], [hw, 0.02, zEnd]]} color="#1FE0C8" lineWidth={2} />
    </group>
  );
}

// ── Selection floor overlay (3D twin of the timeline marching-ants) ───────────
// White band on the floor over the selected notes' beat range, with an animated
// dashed "marching-ants" border. Rendered inside BeatScrollGroup so it scrolls
// with the notes. Mirrors the Flow editor's SelectionFloorOverlay.
const SEL_DASH = 0.18;
const SEL_GAP = 0.10;
const SEL_DASH_SPEED = 0.9;

function SelectionFloorOverlay() {
  const selectedNoteIds = useEditorStore(s => s.selectedNoteIds);
  const beatmap = useEditorStore(s => s.beatmap);
  const activeDifficulty = useEditorStore(s => s.activeDifficulty);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const lineRef = useRef<any>(null);

  const bounds = useMemo(() => {
    const diff = beatmap?.difficulties[activeDifficulty];
    if (!diff || selectedNoteIds.length === 0) return null;
    let minBeat = Infinity, maxBeat = -Infinity;
    for (const i of selectedNoteIds) {
      const n = diff.notes[i];
      if (!n) continue;
      minBeat = Math.min(minBeat, n.time_beats);
      maxBeat = Math.max(maxBeat, n.time_beats);
    }
    if (minBeat === Infinity) return null;
    const safeMax = Math.max(minBeat + 0.5, maxBeat); // visible width for a single note
    return { z1: -minBeat * BEAT_SPACING, z2: -safeMax * BEAT_SPACING };
  }, [beatmap, activeDifficulty, selectedNoteIds]);

  useFrame((_, delta) => {
    const line = lineRef.current;
    if (!line || !bounds) return;
    const mat = line.material;
    if (mat && 'dashOffset' in mat) {
      const period = SEL_DASH + SEL_GAP;
      mat.dashOffset = (mat.dashOffset - SEL_DASH_SPEED * Math.min(delta, 0.05)) % period;
    }
  });

  if (!bounds) return null;
  const { z1, z2 } = bounds;
  const hw = TUNNEL_WIDTH / 2;
  const inset = 0.05;
  const y = 0.03;
  const centerZ = (z1 + z2) / 2;
  const fillLen = Math.max(0.05, Math.abs(z2 - z1));
  const borderPoints: [number, number, number][] = [
    [-hw + inset, y, z1], [hw - inset, y, z1],
    [hw - inset, y, z2], [-hw + inset, y, z2], [-hw + inset, y, z1],
  ];
  return (
    <>
      <mesh position={[0, y - 0.001, centerZ]} rotation={[-Math.PI / 2, 0, 0]} raycast={() => null}>
        <planeGeometry args={[TUNNEL_WIDTH - inset * 2, fillLen]} />
        <meshBasicMaterial color="#FFFFFF" transparent opacity={0.10} depthWrite={false} />
      </mesh>
      <Line
        ref={lineRef}
        points={borderPoints}
        color="#FFFFFF"
        lineWidth={1.5}
        dashed
        dashSize={SEL_DASH}
        gapSize={SEL_GAP}
        dashOffset={0}
        transparent
        opacity={0.85}
        raycast={() => null}
      />
    </>
  );
}

// ── Beat Scroll Group ────────────────────────────────────────────────────────
// Translates children along Z by currentBeat * BEAT_SPACING every frame via
// useFrame.  Children place objects at STATIC beat positions (z = -beats * BEAT_SPACING)
// and get the scrolling "for free" without re-rendering.

function BeatScrollGroup({ children }: { children: React.ReactNode }) {
  const groupRef = useRef<THREE.Group>(null!);
  /** Eased position used while PAUSED (see below). NaN = snap on the next frame. */
  const easedZ = useRef(NaN);
  useFrame((_s, delta) => {
    const store = useEditorStore.getState();
    // Update module-level BEAT_SPACING from store (cheap read)
    BEAT_SPACING = store.noteJumpSpeed;
    // Scroll with the offset-adjusted chart beat so notes line up with the AUDIO.
    // A note's music plays at currentTime = noteBeat*60/bpm + offset, so the note
    // must reach the trigger at that audio time. (chartBeat() == the old raw beat
    // when offset is 0, so this only affects offset≠0 maps.)
    const target = chartBeat() * BEAT_SPACING;

    // Camera travel speed (reviewer request: "adjustable camera speed for
    // jumping between notes"). While PAUSED, jumps/scrubs glide to the new
    // position at this speed instead of teleporting, which makes it much easier
    // to keep your bearings. 0 = instant (the old behaviour). During playback
    // the position is always exact — easing there would desync the audio.
    const glide = store.interfaceSettings.cameraTravelSpeed ?? 0;
    if (store.playback.playing || glide <= 0 || !isFinite(easedZ.current)) {
      easedZ.current = target;
    } else {
      // glide 1..10 → ~0.35s..0.04s to close the gap (framerate independent).
      const k = 1 - Math.exp(-delta * (4 + glide * 4));
      easedZ.current += (target - easedZ.current) * k;
      if (Math.abs(target - easedZ.current) < 0.001) easedZ.current = target;
    }
    groupRef.current.position.z = easedZ.current;
  });
  return <group ref={groupRef}>{children}</group>;
}

// ── Main Scene Content ──────────────────────────────────────────────────────

function BackgroundGradient() {
  // The backdrop is now a full-screen DOM gradient layer behind the transparent
  // canvas (the shared Flow background system — see App.tsx + bg3dLayerStyle), so
  // keep the 3D scene background cleared and let that layer + any .visualizer
  // show through the alpha canvas.
  const { scene } = useThree();
  useEffect(() => {
    scene.background = null;
    return () => { scene.background = null; };
  }, [scene]);

  return null;
}

// ── Delete "explosion" bursts ────────────────────────────────────────────────
// A recycled spark pool that fires a quick outward burst at a deleted note's
// position (paired with the delete SFX). Lives in fixed world space so the
// burst stays where the note was, even while the chart scrolls past.
function DeleteBursts() {
  const meshRef = useRef<THREE.InstancedMesh>(null!);
  const sparksRef = useRef<BurstSpark[]>([]);
  if (sparksRef.current.length === 0) {
    sparksRef.current = Array.from({ length: MAX_BURST_SPARKS }, emptyBurstSpark);
  }
  const dummy = useMemo(() => new THREE.Object3D(), []);
  const tmpColor = useMemo(() => new THREE.Color(), []);

  useEffect(() => {
    return onNoteBurst((e) => {
      const store = useEditorStore.getState();
      const bpm = store.beatmap?.bpm || 120;
      const rawBeat = chartBeat();
      // The emit carries the note's RAW grid position. Map it to world space the
      // same way NoteMesh does so the burst fires from the note, not the floor.
      const x = e.x * GRID_SCALE * 10;
      const y = e.y * GRID_SCALE * 10 + NOTE_Y_OFFSET;
      // Note world Z mirrors NoteMesh: -(time_beats - rawBeat) * BEAT_SPACING.
      const z = -(e.timeBeats - rawBeat) * BEAT_SPACING;
      spawnBurst(sparksRef.current, x, y, z, e.color);
    });
  }, []);

  useFrame((_, dt) => {
    const mesh = meshRef.current;
    if (!mesh) return;
    const d = Math.min(dt, 0.05);
    const sparks = sparksRef.current;
    let anyActive = false;
    for (let i = 0; i < sparks.length; i++) {
      const s = sparks[i];
      if (s.active) {
        s.life -= d;
        if (s.life <= 0) s.active = false;
      }
      if (!s.active) {
        dummy.position.set(0, -9999, 0);
        dummy.scale.setScalar(0);
        dummy.updateMatrix();
        mesh.setMatrixAt(i, dummy.matrix);
        continue;
      }
      anyActive = true;
      s.velocity.y -= 7 * d;                       // gravity
      s.position.addScaledVector(s.velocity, d);
      const t = s.life / s.maxLife;                // 1 → 0
      dummy.position.copy(s.position);
      dummy.scale.setScalar(0.06 * (0.35 + t * 0.65));
      dummy.updateMatrix();
      mesh.setMatrixAt(i, dummy.matrix);
      mesh.setColorAt(i, tmpColor.copy(s.color));
    }
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    mesh.visible = anyActive;
  });

  return (
    <instancedMesh ref={meshRef} args={[undefined, undefined, MAX_BURST_SPARKS]} frustumCulled={false}>
      <sphereGeometry args={[1, 6, 6]} />
      <meshBasicMaterial toneMapped={false} transparent blending={THREE.AdditiveBlending} depthWrite={false} />
    </instancedMesh>
  );
}

// ── Spark Particles ──────────────────────────────────────────────────────────
// All constants are read from store.particleSettings (configurable in Settings > Particles).

function SparkParticles() {
  const groupRef = useRef<THREE.Group>(null!);
  const sparkCount = useEditorStore(s => s.particleSettings.sparkCount);
  const sparkColor = useEditorStore(s => s.particleSettings.sparkColor);
  const sparkSize  = useEditorStore(s => s.particleSettings.sparkSize);

  const sparkRef = useRef<{
    points: THREE.Points;
    positions: Float32Array;
    velocities: Float32Array;
    phases: Float32Array;
    fade: number;
    ready: boolean;
    count: number;
    colorTime: number;
  }>({
    points: null!,
    positions: new Float32Array(0),
    velocities: new Float32Array(0),
    phases: new Float32Array(0),
    fade: 0,
    ready: false,
    count: 0,
    colorTime: 0,
  });

  // Re-init arrays when sparkCount, sparkColor, or sparkSize changes
  useEffect(() => {
    const ps = useEditorStore.getState().particleSettings;
    const s = sparkRef.current;

    // Clean up previous
    if (s.points && groupRef.current) {
      groupRef.current.remove(s.points);
      s.points.geometry.dispose();
      (s.points.material as THREE.PointsMaterial).dispose();
    }

    const count = ps.sparkCount;
    s.count = count;
    s.positions = new Float32Array(count * 3);
    s.velocities = new Float32Array(count);
    s.phases = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      s.positions[i * 3]     = (Math.random() - 0.5) * ps.sparkSpreadX;
      s.positions[i * 3 + 1] = ps.sparkYMin + Math.random() * (ps.sparkYMax - ps.sparkYMin);
      s.positions[i * 3 + 2] = -Math.random() * ps.sparkSpreadZ;
      s.velocities[i] = ps.sparkSpeedMin + Math.random() * (ps.sparkSpeedMax - ps.sparkSpeedMin);
      s.phases[i] = Math.random() * Math.PI * 2;
    }

    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(s.positions, 3));

    const mat = new THREE.PointsMaterial({
      color: new THREE.Color(ps.sparkColor),
      size: ps.sparkSize,
      transparent: true,
      opacity: 0,
      depthWrite: false,
      depthTest: false,
      blending: THREE.AdditiveBlending,
      sizeAttenuation: false,
    });

    const points = new THREE.Points(geo, mat);
    points.frustumCulled = false;
    points.renderOrder = 999;
    points.visible = false;
    s.points = points;
    s.ready = true;

    if (groupRef.current) {
      groupRef.current.add(points);
    }

    return () => {
      if (groupRef.current) groupRef.current.remove(points);
      geo.dispose();
      mat.dispose();
      s.ready = false;
    };
  }, [sparkCount, sparkColor, sparkSize]);

  useFrame((_, delta) => {
    const s = sparkRef.current;
    if (!s.ready) return;

    const store = useEditorStore.getState();
    const active = store.particleSettings.sparkLoop;
    const ps = store.particleSettings;

    const fadeSpeed = active ? ps.sparkFadeInSpeed : ps.sparkFadeOutSpeed;
    s.fade += ((active ? 1 : 0) - s.fade) * Math.min(1, delta * fadeSpeed);

    if (s.fade < 0.005) {
      s.points.visible = false;
      return;
    }
    s.points.visible = true;

    // Live-update material color & size without full re-init
    const mat = s.points.material as THREE.PointsMaterial;
    mat.size = ps.sparkSize;

    // Color blending between 4 colors
    if (ps.sparkColorBlend) {
      s.colorTime += delta;
      const totalCycle = ps.sparkColorTransition * 4;
      const t = (s.colorTime % totalCycle) / ps.sparkColorTransition;
      const idx = Math.floor(t) % 4;
      const frac = t - Math.floor(t);
      const colors = [ps.sparkColor, ps.sparkColor2, ps.sparkColor3, ps.sparkColor4];
      const c1 = new THREE.Color(colors[idx]);
      const c2 = new THREE.Color(colors[(idx + 1) % 4]);
      mat.color.copy(c1).lerp(c2, frac);
    } else {
      mat.color.set(ps.sparkColor);
    }

    // Apply position offset to group
    if (groupRef.current) {
      groupRef.current.position.set(ps.sparkOffsetX, ps.sparkOffsetY, ps.sparkOffsetZ);
      // Apply rotation speeds (accumulate over time)
      if (ps.sparkRotationX !== 0) {
        groupRef.current.rotation.x += (ps.sparkRotationX * Math.PI / 180) * delta;
      }
      if (ps.sparkRotationY !== 0) {
        groupRef.current.rotation.y += (ps.sparkRotationY * Math.PI / 180) * delta;
      }
    }

    // Compute direction vector from angle + tilt
    const angleRad = (ps.sparkDirectionAngle * Math.PI) / 180;
    const tiltRad = (ps.sparkDirectionTilt * Math.PI) / 180;
    // angle=0 → up (+Y), angle=90 → right (+X), angle=180 → down (-Y), angle=270 → left (-X)
    const dirX = Math.sin(angleRad) * Math.cos(tiltRad);
    const dirY = Math.cos(angleRad) * Math.cos(tiltRad);
    const dirZ = -Math.sin(tiltRad);  // positive tilt = toward camera (-Z)

    const time = Date.now() * 0.001;
    const pos = s.positions;
    const count = s.count;

    for (let i = 0; i < count; i++) {
      const i3 = i * 3;
      const speed = s.velocities[i] * delta;
      pos[i3]     += dirX * speed;
      pos[i3 + 1] += dirY * speed;
      pos[i3 + 2] += dirZ * speed;

      const phase = s.phases[i];
      pos[i3]     += Math.sin(time * 2.0 + phase) * ps.sparkTurbulence * delta;
      pos[i3 + 2] += Math.cos(time * 1.5 + phase * 0.7) * ps.sparkTurbulence * 0.6 * delta;

      // Recycle particles that leave the bounding area
      // Check both Y bounds (for up/down directions) and general distance from origin
      const outOfBoundsY = (dirY >= 0 && pos[i3 + 1] > ps.sparkYMax) ||
                           (dirY < 0 && pos[i3 + 1] < ps.sparkYMin);
      const outOfBoundsX = Math.abs(pos[i3]) > ps.sparkSpreadX * 1.5;
      const outOfBoundsZ = Math.abs(pos[i3 + 2]) > ps.sparkSpreadZ * 1.5;

      if (outOfBoundsY || outOfBoundsX || outOfBoundsZ) {
        // Respawn at the "source" end opposite to direction
        pos[i3]     = (Math.random() - 0.5) * ps.sparkSpreadX - dirX * 2;
        pos[i3 + 1] = dirY >= 0 ? ps.sparkYMin : ps.sparkYMax;
        pos[i3 + 2] = -Math.random() * ps.sparkSpreadZ - dirZ * 2;
        s.velocities[i] = ps.sparkSpeedMin + Math.random() * (ps.sparkSpeedMax - ps.sparkSpeedMin);
        s.phases[i] = Math.random() * Math.PI * 2;
      }
    }

    (s.points.geometry.attributes.position as THREE.BufferAttribute).needsUpdate = true;
    mat.opacity = s.fade;
  });

  return <group ref={groupRef} />;
}

// ── Windowed Note/Rail/Obstacle Renderer ─────────────────────────────────────
// Only mounts components within the visible beat range + margin.
// Prevents thousands of React component instances when maps have many notes.

const WINDOW_MARGIN_AHEAD = 10;   // extra beats beyond visible range to pre-mount
const WINDOW_MARGIN_BEHIND = 5;
// Hard cap on how many notes (and rails/obstacles) are mounted as Three.js
// objects at once. The beat window above can still contain hundreds of notes in
// a dense section; this keeps the scene to the NEAREST ~100 (the rest mount as
// the playhead approaches), matching the Flow editor's small-window budget.
const MAX_VISIBLE_NOTES = 100;

/** Binary-search the first index where notes[i].time_beats >= minBeat */
function _lowerBound(notes: Note[], minBeat: number): number {
  let lo = 0, hi = notes.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (notes[mid].time_beats < minBeat) lo = mid + 1; else hi = mid;
  }
  return lo;
}

/** Binary-search the first index where notes[i].time_beats > maxBeat */
function _upperBound(notes: Note[], maxBeat: number): number {
  let lo = 0, hi = notes.length;
  while (lo < hi) {
    const mid = (lo + hi) >>> 1;
    if (notes[mid].time_beats <= maxBeat) lo = mid + 1; else hi = mid;
  }
  return lo;
}

// Small sphere on the FLOOR below a note OR rail node (ground projection). They
// float above the grid, so this guide makes it easy to read exactly where each
// sits on the step grid. It also hops up when it crosses the trigger (the moment
// it aligns with the snap line — same trigger as the note pop).
const FLOOR_MARKER_BASE_Y = 0.04;
function FloorMarker({ x, beat, color, baseY = FLOOR_MARKER_BASE_Y }: { x: number; beat: number; color: string; baseY?: number }) {
  const meshRef = useRef<THREE.Mesh>(null!);
  const prevOnGridRef = useRef(false);
  const hopStartRef = useRef(0);
  const wasPlayingRef = useRef(false);
  const bounceStartRef = useRef(0);
  useFrame(() => {
    const store = useEditorStore.getState();
    const rawBeat = chartBeat();
    const relZ = (beat - rawBeat) * BEAT_SPACING;
    const m = meshRef.current;
    if (!m) return;
    m.position.z = -beat * BEAT_SPACING;

    // Hidden while playing (editing guide only); springs back when play stops.
    if (store.playback.playing) {
      m.visible = false;
      wasPlayingRef.current = true;
      return;
    }
    m.visible = relZ >= -VISIBLE_BEATS_BEHIND * BEAT_SPACING && relZ <= VISIBLE_BEATS_AHEAD * BEAT_SPACING;

    const nowMs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    // On the play → stop transition, pop back in with an overshoot bounce.
    if (wasPlayingRef.current) { bounceStartRef.current = nowMs; wasPlayingRef.current = false; }
    let scale = 1;
    if (bounceStartRef.current) {
      const dt = (nowMs - bounceStartRef.current) / 1000;
      const DUR = 0.5;
      if (dt < DUR) {
        const t = dt / DUR, c1 = 1.70158, c3 = c1 + 1;       // easeOutBack
        scale = Math.max(0, 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2));
      } else { bounceStartRef.current = 0; }
    }
    m.scale.setScalar(scale);

    // Hop up when crossing the trigger / snap line (same trigger as the pop).
    const gridTol = Math.max(0.006, store.scrollStep * 0.55);
    const onGrid = Math.abs(beat - rawBeat) < gridTol;
    if (onGrid && !prevOnGridRef.current) hopStartRef.current = nowMs;
    prevOnGridRef.current = onGrid;
    let y = baseY;
    if (hopStartRef.current) {
      const dt = (nowMs - hopStartRef.current) / 1000;
      const DUR = 0.34;
      if (dt < DUR) y = baseY + Math.sin(Math.PI * (dt / DUR)) * 0.5;
      else hopStartRef.current = 0;
    }
    m.position.y = y;
  });
  return (
    <mesh ref={meshRef} position={[x, baseY, -beat * BEAT_SPACING]} raycast={() => null}>
      <sphereGeometry args={[0.04, 12, 10]} />
      <meshBasicMaterial color={color} transparent opacity={0.9} toneMapped={false} />
    </mesh>
  );
}

// ── Instanced floor markers ──────────────────────────────────────────────────
// ONE InstancedMesh + ONE useFrame replaces the per-note and per-rail-node
// FloorMarker crowd (hundreds of meshes, materials and useFrame callbacks in a
// dense window). Markers are an editing aid: hidden while playing (count=0 →
// zero cost), never raycast, and the editing FX (grid-cross hop, resume
// bounce) are preserved per instance.
const MARKER_LIMIT = 1024;
const MARKER_RADIUS = 0.04;
const _mQuat = new THREE.Quaternion();
const _mPos = new THREE.Vector3();
const _mScale = new THREE.Vector3();
const _mMatrix = new THREE.Matrix4();
const _mColor = new THREE.Color();

interface MarkerDesc { x: number; beat: number; color: string }

function InstancedFloorMarkers({ markers }: { markers: MarkerDesc[] }) {
  const ref = useRef<THREE.InstancedMesh>(null!);
  // Per-instance FX state in flat pools — no per-marker React state.
  const fx = useRef({
    prevOnGrid: new Uint8Array(MARKER_LIMIT),
    hopStart: new Float64Array(MARKER_LIMIT),
    wasPlaying: false,
    bounceStart: 0,
  });

  const material = useMemo(
    () => new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.9, toneMapped: false }),
    [],
  );
  useEffect(() => () => { material.dispose(); }, [material]);

  useEffect(() => {
    const m = ref.current;
    if (m) m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  }, []);

  // Colors follow the window contents. FX state resets on a window shift so
  // hops don't misfire when instance indices move under the markers.
  useEffect(() => {
    const m = ref.current;
    if (!m) return;
    const n = Math.min(markers.length, MARKER_LIMIT);
    for (let i = 0; i < n; i++) {
      _mColor.set(markers[i].color);
      m.setColorAt(i, _mColor);
    }
    if (m.instanceColor) m.instanceColor.needsUpdate = true;
    fx.current.prevOnGrid.fill(0);
    fx.current.hopStart.fill(0);
  }, [markers]);

  useFrame(() => {
    const m = ref.current;
    if (!m) return;
    const store = useEditorStore.getState();
    const f = fx.current;
    if (store.playback.playing) {
      m.count = 0;               // markers are an editing aid — free during playback
      f.wasPlaying = true;
      return;
    }
    const nowMs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    // Play → stop: every marker pops back with the same overshoot bounce.
    if (f.wasPlaying) { f.bounceStart = nowMs; f.wasPlaying = false; }
    let bounce = 1;
    if (f.bounceStart) {
      const dt = (nowMs - f.bounceStart) / 1000;
      const DUR = 0.5;
      if (dt < DUR) {
        const t = dt / DUR, c1 = 1.70158, c3 = c1 + 1;       // easeOutBack
        bounce = Math.max(0, 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2));
      } else { f.bounceStart = 0; }
    }
    const rawBeat = chartBeat();
    const gridTol = Math.max(0.006, store.scrollStep * 0.55);
    const n = Math.min(markers.length, MARKER_LIMIT);
    for (let i = 0; i < n; i++) {
      const mk = markers[i];
      const relZ = (mk.beat - rawBeat) * BEAT_SPACING;
      const vis = relZ >= -VISIBLE_BEATS_BEHIND * BEAT_SPACING && relZ <= VISIBLE_BEATS_AHEAD * BEAT_SPACING;
      // Grid-cross hop — same trigger as the note pop.
      const onGrid = Math.abs(mk.beat - rawBeat) < gridTol;
      if (onGrid && !f.prevOnGrid[i]) f.hopStart[i] = nowMs;
      f.prevOnGrid[i] = onGrid ? 1 : 0;
      let y = FLOOR_MARKER_BASE_Y;
      if (f.hopStart[i]) {
        const dt = (nowMs - f.hopStart[i]) / 1000;
        const DUR = 0.34;
        if (dt < DUR) y = FLOOR_MARKER_BASE_Y + Math.sin(Math.PI * (dt / DUR)) * 0.5;
        else f.hopStart[i] = 0;
      }
      const s = vis ? MARKER_RADIUS * bounce : 0;   // scale 0 hides w/o compaction
      _mPos.set(mk.x, y, -mk.beat * BEAT_SPACING);
      _mScale.set(s, s, s);
      _mMatrix.compose(_mPos, _mQuat, _mScale);
      m.setMatrixAt(i, _mMatrix);
    }
    m.count = n;
    m.instanceMatrix.needsUpdate = true;
  });

  return (
    <instancedMesh
      ref={ref}
      args={[UNIT_MARKER_SPHERE, material, MARKER_LIMIT]}
      frustumCulled={false}
      raycast={() => null}
    />
  );
}

// ── Instanced note pools (VR) ────────────────────────────────────────────────
// Stage C: all non-selected notes render through THREE InstancedMesh pools
// (normal / dimmed / faded) — 3 draw calls + 3 raycast targets instead of one
// mesh + material + useFrame per note. Opacity is a per-POOL material property
// (no per-instance alpha shader surgery); per-note color goes through
// instanceColor (diffuse via three's built-in tint, emissive via an injected
// vColor multiply). Selected / dragging notes are EXCLUDED here and keep the
// classic NoteMesh (selection ring, drag flash) — they're always few.
// Events use e.instanceId → a per-pool slot→noteIndex map rebuilt every frame.
const NOTE_POOL_LIMIT = 512; // ≥ the adaptive budget's notesVR ceiling (512)

function makeInstancedNoteMaterial(opts: {
  metalness: number; roughness: number;
  opacity: number; emissiveIntensity: number;
  fresnel: { color: string; intensity: number; power: number } | null;
}): THREE.MeshStandardMaterial {
  const m = new THREE.MeshStandardMaterial({
    color: '#ffffff',
    emissive: '#ffffff',           // per-instance tint multiplies this in-shader
    emissiveIntensity: opts.emissiveIntensity,
    metalness: opts.metalness,
    roughness: opts.roughness,
    transparent: opts.opacity < 1,
    opacity: opts.opacity,
    depthWrite: opts.opacity >= 1,
  });
  const f = opts.fresnel;
  m.onBeforeCompile = (shader) => {
    if (f) {
      shader.uniforms.uFresnelColor = { value: new THREE.Color(f.color) };
      shader.uniforms.uFresnelIntensity = { value: f.intensity };
      shader.uniforms.uFresnelPower = { value: f.power };
    }
    shader.fragmentShader = shader.fragmentShader
      .replace('void main() {',
        (f ? 'uniform vec3 uFresnelColor;\nuniform float uFresnelIntensity;\nuniform float uFresnelPower;\n' : '') + 'void main() {')
      .replace('#include <emissivemap_fragment>',
        // NOTE: in the FRAGMENT stage three defines USE_COLOR for instanceColor
        // (USE_INSTANCING_COLOR is a vertex-only define — see WebGLProgram.js).
        `#include <emissivemap_fragment>
        #ifdef USE_COLOR
        totalEmissiveRadiance *= vColor;
        #endif` + (f ? `
        float noteFres = pow(1.0 - saturate(abs(dot(normalize(vViewPosition), normal))), uFresnelPower);
        totalEmissiveRadiance += uFresnelColor * noteFres * uFresnelIntensity;` : ''));
  };
  m.customProgramCacheKey = () => `note-inst-${f ? 'f' : 'p'}-${opts.opacity < 1 ? 't' : 'o'}`;
  return m;
}

const _npColor = new THREE.Color();
const _npPos = new THREE.Vector3();
const _npScale = new THREE.Vector3();
const _npQuat = new THREE.Quaternion();
const _npMatrix = new THREE.Matrix4();

type PoolKey = 'normal' | 'dim' | 'faded';

function InstancedNotePools({
  visibleNotes,
}: {
  visibleNotes: Array<{ note: Note; index: number }>;
}) {
  const gp = useEditorStore(s => s.gameplaySettings);
  const iface = useEditorStore(s => s.interfaceSettings);
  const heatmapEnabled = useEditorStore(s => s.heatmapEnabled);
  const intensityData = useEditorStore(s => s.intensityData);
  const noteColors = useMemo(() => getNoteColorsFromSettings(gp),
    [gp.noteColorLeft, gp.noteColorRight, gp.noteColorOneHand, gp.noteColorBothHands]); // eslint-disable-line react-hooks/exhaustive-deps

  const refs = {
    normal: useRef<THREE.InstancedMesh>(null!),
    dim: useRef<THREE.InstancedMesh>(null!),
    faded: useRef<THREE.InstancedMesh>(null!),
  };
  // slot → absolute note index, per pool (rebuilt every frame by the writer).
  const slotMaps = useRef({
    normal: new Int32Array(NOTE_POOL_LIMIT),
    dim: new Int32Array(NOTE_POOL_LIMIT),
    faded: new Int32Array(NOTE_POOL_LIMIT),
  });
  // Per-note FX state, keyed by absolute note index (grown on demand).
  const fx = useRef({
    spawnStart: new Float64Array(0),
    spawnBirth: new Float64Array(0),
    pulseStart: new Float64Array(0),
    prevOnGrid: new Uint8Array(0),
  });

  const fresnel = gp.noteFresnelEnabled
    ? { color: gp.noteFresnelColor, intensity: gp.noteFresnelIntensity, power: gp.noteFresnelPower }
    : null;
  const mats = useMemo(() => ({
    normal: makeInstancedNoteMaterial({
      metalness: gp.noteMetalness, roughness: gp.noteRoughness,
      opacity: 1, emissiveIntensity: iface.noteEmissiveNormal, fresnel,
    }),
    dim: makeInstancedNoteMaterial({
      metalness: gp.noteMetalness, roughness: gp.noteRoughness,
      opacity: 0.2, emissiveIntensity: iface.noteEmissiveNormal, fresnel,
    }),
    faded: makeInstancedNoteMaterial({
      metalness: gp.noteMetalness, roughness: gp.noteRoughness,
      opacity: Math.max(0.01, iface.noteOpacityPast), emissiveIntensity: iface.noteEmissivePast, fresnel,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [gp.noteMetalness, gp.noteRoughness, gp.noteFresnelEnabled, gp.noteFresnelColor,
       gp.noteFresnelIntensity, gp.noteFresnelPower, iface.noteEmissiveNormal,
       iface.noteEmissivePast, iface.noteOpacityPast]);
  useEffect(() => () => { mats.normal.dispose(); mats.dim.dispose(); mats.faded.dispose(); }, [mats]);

  useEffect(() => {
    for (const k of ['normal', 'dim', 'faded'] as PoolKey[]) {
      const m = refs[k].current;
      if (m) m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── The writer: classify + place every visible note each frame ──
  useFrame(() => {
    const store = useEditorStore.getState();
    const rawBeat = chartBeat();
    const nowMs = (typeof performance !== 'undefined' ? performance.now() : Date.now());
    const gridTol = Math.max(0.006, store.scrollStep * 0.55);
    const noteSize = gp.noteSize;
    const selected = store.selectedNoteIds;
    const dragging = store.draggingNoteIndex;

    // Grow FX arrays to cover the largest absolute index we'll touch.
    const maxIdx = visibleNotes.length ? visibleNotes[visibleNotes.length - 1].index + 1 : 0;
    if (fx.current.spawnStart.length < maxIdx) {
      const n = Math.max(maxIdx, 256);
      fx.current = {
        spawnStart: new Float64Array(n), spawnBirth: new Float64Array(n),
        pulseStart: new Float64Array(n), prevOnGrid: new Uint8Array(n),
      };
    }
    const f = fx.current;

    const counts: Record<PoolKey, number> = { normal: 0, dim: 0, faded: 0 };
    const maps = slotMaps.current;

    for (const { note, index } of visibleNotes) {
      // Selected / dragging notes render as classic NoteMesh — skip here.
      if (dragging === index || selected.includes(index)) continue;

      const relZ = (note.time_beats - rawBeat) * BEAT_SPACING;
      if (relZ < -VISIBLE_BEATS_BEHIND * BEAT_SPACING || relZ > PERF.noteViewBeats * BEAT_SPACING) continue;

      // Spawn "pop" for freshly placed notes.
      const birth = placedAt(noteKey(note.time_ms, note.position[0], note.position[1]));
      if (birth && birth !== f.spawnBirth[index] && (nowMs - birth) < 600) {
        f.spawnBirth[index] = birth;
        f.spawnStart[index] = birth;
      }
      let spawnFactor = 1;
      if (f.spawnStart[index]) {
        const dt = (nowMs - f.spawnStart[index]) / 1000;
        if (dt < 0.45) spawnFactor = easeOutElastic(dt / 0.45);
        else f.spawnStart[index] = 0;
      }

      // Grid-cross pulse (scale bump + brightness flash).
      const onGrid = Math.abs(note.time_beats - rawBeat) < gridTol;
      if (onGrid && !f.prevOnGrid[index]) f.pulseStart[index] = nowMs;
      f.prevOnGrid[index] = onGrid ? 1 : 0;
      let pulseFactor = 1, pulseGlow = 0;
      if (f.pulseStart[index]) {
        const dt = (nowMs - f.pulseStart[index]) / 1000;
        const PD = 0.16;
        if (dt < PD) {
          pulseGlow = Math.sin(Math.PI * (dt / PD));
          pulseFactor = 1 + 0.55 * pulseGlow;
        } else f.pulseStart[index] = 0;
      }

      const behind = note.time_beats < rawBeat;
      const pool: PoolKey = (relZ < -BEAT_SPACING) ? 'faded' : (behind && !onGrid) ? 'dim' : 'normal';
      const mesh = refs[pool].current;
      if (!mesh) continue;
      const slot = counts[pool]++;
      if (slot >= NOTE_POOL_LIMIT) continue;
      maps[pool][slot] = index;

      const baseColor = (heatmapEnabled && intensityData && intensityData.noteColors[index])
        ? intensityData.noteColors[index]
        : noteColors[note.note_type] || '#ffffff';
      _npColor.set(baseColor).multiplyScalar(1 + pulseGlow * 1.4);

      _npPos.set(
        note.position[0] * GRID_SCALE * 10,
        note.position[1] * GRID_SCALE * 10 + NOTE_Y_OFFSET,
        -note.time_beats * BEAT_SPACING,
      );
      const s = noteSize * spawnFactor * pulseFactor;
      _npScale.set(s, s, s);
      _npMatrix.compose(_npPos, _npQuat, _npScale);
      mesh.setMatrixAt(slot, _npMatrix);
      mesh.setColorAt(slot, _npColor);
    }

    for (const k of ['normal', 'dim', 'faded'] as PoolKey[]) {
      const m = refs[k].current;
      if (!m) continue;
      m.count = counts[k];
      m.instanceMatrix.needsUpdate = true;
      if (m.instanceColor) m.instanceColor.needsUpdate = true;
    }
    RENDER_STATS.notes = counts.normal + counts.dim + counts.faded;
  });

  // ── Events (instanceId → note index) — mirror the classic NoteMesh handlers ──
  const makeHandlers = (pool: PoolKey) => {
    const idxOf = (e: ThreeEvent<MouseEvent> | ThreeEvent<PointerEvent>): number | null => {
      const id = (e as { instanceId?: number }).instanceId;
      return (id == null) ? null : slotMaps.current[pool][id];
    };
    return {
      onClick: (e: ThreeEvent<MouseEvent>) => {
        e.stopPropagation();
        if (e.altKey) return;
        const index = idxOf(e); if (index == null) return;
        const store = useEditorStore.getState();
        const tool = store.toolMode;
        if (tool === 'eraser' || e.ctrlKey || e.metaKey) { store.removeNotes([index]); return; }
        if (e.shiftKey && (tool === 'note_left' || tool === 'note_right' || tool === 'note_onehand' || tool === 'note_bothhands')) {
          const mapping = TOOL_TO_NOTE[tool];
          if (mapping) store.updateNote(index, { note_type: mapping.note_type, hand: mapping.hand });
          return;
        }
        store.selectNote(index, e.shiftKey);
      },
      onDoubleClick: (e: ThreeEvent<MouseEvent>) => {
        e.stopPropagation();
        const index = idxOf(e); if (index == null) return;
        const store = useEditorStore.getState();
        const note = store.beatmap?.difficulties[store.activeDifficulty]?.notes[index];
        if (!note) return;
        const targetTime = Math.max(0, note.time_ms / 1000 + (store.beatmap?.offset || 0) / 1000);
        store.setCurrentTime(targetTime);
        if (store.playback.playing) seekAudio(targetTime);
      },
      onContextMenu: (e: ThreeEvent<MouseEvent>) => { e.nativeEvent.preventDefault(); },
      onPointerDown: (e: ThreeEvent<PointerEvent>) => {
        const index = idxOf(e); if (index == null) return;
        if (e.button === 2) {
          e.stopPropagation();
          _noteRightClick = true;
          requestAnimationFrame(() => { _noteRightClick = false; });
          useEditorStore.getState().removeNotes([index]);
          return;
        }
        if (!e.altKey) return;
        e.stopPropagation();
        const store = useEditorStore.getState();
        const note = store.beatmap?.difficulties[store.activeDifficulty]?.notes[index];
        if (!note) return;
        _ctrlDrag.active = true;
        _ctrlDrag.noteIndex = index;
        _ctrlDrag.isRailNode = false;
        _ctrlDrag.railNodeIndex = -1;
        _ctrlDrag.startMouseX = e.nativeEvent.clientX;
        _ctrlDrag.startMouseY = e.nativeEvent.clientY;
        _ctrlDrag.startPos = [...note.position] as [number, number, number];
        _ctrlDrag.historyStarted = false;
        document.body.style.cursor = 'move';
        store.setGizmoDragAxis('x');
        window.addEventListener('pointermove', _ctrlDragMove);
        window.addEventListener('pointerup', _ctrlDragUp);
      },
    };
  };
  const handlers = useMemo(() => ({
    normal: makeHandlers('normal'),
    dim: makeHandlers('dim'),
    faded: makeHandlers('faded'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), []);

  return (
    <>
      {(['normal', 'dim', 'faded'] as PoolKey[]).map((k) => (
        <instancedMesh
          key={k}
          ref={refs[k]}
          args={[UNIT_NOTE_SPHERE, mats[k], NOTE_POOL_LIMIT]}
          frustumCulled={false}
          {...handlers[k]}
        />
      ))}
    </>
  );
}

function WindowedNotes({
  notes, selectedNoteIds, bpm,
}: {
  notes: Note[];
  selectedNoteIds: number[];
  bpm: number;
}) {
  const [range, setRange] = useState<[number, number]>([0, Math.min(PERF.maxNotes, notes.length)]);
  // Rails extend forward in time from their head note, so a long rail should
  // stay mounted until its TAIL clears the trigger — even after the head note
  // has left the window. Track how far back to look (the longest rail).
  const [railStart, setRailStart] = useState(0);
  const maxRailBeats = useMemo(() => {
    let m = 0;
    for (const n of notes) {
      if (n.has_rail && n.rail_nodes && n.rail_nodes.length) {
        const last = n.rail_nodes[n.rail_nodes.length - 1];
        const tailBeat = ((last[2] / 0.02) / 1000) * (bpm / 60);
        m = Math.max(m, tailBeat - n.time_beats);
      }
    }
    return Math.min(m, 64); // cap so we never mount an absurd back-range
  }, [notes, bpm]);

  useFrame(() => {
    const store = useEditorStore.getState();
    const rawBeat = chartBeat();
    const minBeat = rawBeat - VISIBLE_BEATS_BEHIND - WINDOW_MARGIN_BEHIND;
    // Notes have their OWN adaptive draw distance (FOV), tunable separately from
    // obstacles (the debug HUD exposes both).
    const maxBeat = rawBeat + PERF.noteViewBeats + WINDOW_MARGIN_AHEAD;

    const newStart = _lowerBound(notes, minBeat);
    const rawEnd = _upperBound(notes, maxBeat);
    // Cap to the adaptive note budget (the governor lowers it when the
    // framerate drops). VR notes are INSTANCED (3 draw calls regardless of
    // count) so their budget covers the whole visible runway — otherwise a
    // dense section starves the budget and the NEXT section's notes would
    // flood in mid-tunnel the moment the window clears the gap. The per-mesh
    // Console path keeps the lower cap.
    const cap = isConsoleView ? PERF.maxNotes : PERF.maxNotesVR;
    const newEnd = Math.min(rawEnd, newStart + cap);
    // Look back by the longest rail so trailing rails stay mounted.
    const newRailStart = _lowerBound(notes, minBeat - maxRailBeats);

    // Only trigger React re-render if range actually changed
    setRange(prev => {
      if (prev[0] === newStart && prev[1] === newEnd) return prev;
      return [newStart, newEnd];
    });
    setRailStart(prev => (prev === newRailStart ? prev : newRailStart));
  });

  const visibleNotes = useMemo(() => {
    const result: Array<{ note: Note; index: number }> = [];
    // Clamp to the CURRENT notes length + guard each entry: on a map switch the
    // `range` state lags one frame behind a now-shorter `notes` array, which
    // would otherwise push `undefined` notes and crash the render.
    const end = Math.min(range[1], notes.length);
    for (let i = Math.max(0, range[0]); i < end; i++) {
      const note = notes[i];
      if (note) result.push({ note, index: i });
    }
    return result;
  }, [notes, range]);

  // Rails of notes just BEHIND the window — render only the tube so a long rail
  // keeps passing through the trigger after its head note has scrolled past.
  const trailingRails = useMemo(() => {
    const result: Array<{ note: Note; index: number }> = [];
    for (let i = railStart; i < range[0]; i++) {
      const n = notes[i];
      if (n && n.has_rail && n.rail_nodes && n.rail_nodes.length) result.push({ note: n, index: i });
    }
    return result;
  }, [notes, railStart, range]);

  const gp = useEditorStore(s => s.gameplaySettings);
  const noteColors = useMemo(
    () => getNoteColorsFromSettings(gp),
    [gp.noteColorLeft, gp.noteColorRight, gp.noteColorOneHand, gp.noteColorBothHands],
  );

  // Editing aids (floor markers, rail-node dots) are invisible during playback,
  // so don't even MOUNT them then — in a dense window they were hundreds of
  // meshes + useFrame callbacks burning frame time while hidden. `playing`
  // changes rarely, so this subscription re-renders only on play/pause.
  const playing = useEditorStore(s => s.playback.playing);

  // VR renders notes through the instanced pools (Stage C); the Console editor
  // keeps per-mesh notes (it uses the merged game FBX geometry + foot pads).
  // Selected / dragging notes always render as classic NoteMesh so the
  // selection ring / drag feedback stay pixel-identical.
  const isConsoleView = useEditorStore(s => s.activeView === 'console');
  const draggingNoteIndex = useEditorStore(s => s.draggingNoteIndex);

  // Ground-projection markers for every visible note AND rail control point —
  // rendered as ONE InstancedMesh (see InstancedFloorMarkers).
  const floorMarkers = useMemo<MarkerDesc[]>(() => {
    const out: MarkerDesc[] = [];
    for (const { note } of visibleNotes) {
      const color = noteColors[note.note_type] || '#ffffff';
      out.push({ x: note.position[0] * GRID_SCALE * 10, beat: note.time_beats, color });
      if (note.has_rail && note.rail_nodes) {
        for (const node of note.rail_nodes) {
          out.push({ x: node[0] * GRID_SCALE * 10, beat: ((node[2] / 0.02) / 1000) * (bpm / 60), color });
        }
      }
    }
    return out;
  }, [visibleNotes, noteColors, bpm]);

  return (
    <>
      {/* Notes — VR: three instanced pools + classic meshes for the (few)
          selected/dragging notes; Console: classic per-mesh path. */}
      {isConsoleView ? (
        visibleNotes.map(({ note, index }) => (
          <NoteMesh
            key={`n-${note.time_ms}-${index}`}
            note={note}
            index={index}
            selected={selectedNoteIds.includes(index)}
          />
        ))
      ) : (
        <>
          <InstancedNotePools visibleNotes={visibleNotes} />
          {visibleNotes
            .filter(({ index }) => selectedNoteIds.includes(index) || index === draggingNoteIndex)
            .map(({ note, index }) => (
              <NoteMesh
                key={`n-${note.time_ms}-${index}`}
                note={note}
                index={index}
                selected={selectedNoteIds.includes(index)}
              />
            ))}
        </>
      )}
      {/* Floor "shadow" spheres (notes + rail nodes) — one instanced draw call */}
      <InstancedFloorMarkers markers={floorMarkers} />
      {/* Rails (windowed + long trailing rails still crossing the trigger) */}
      {(() => {
        const railList = [...visibleNotes.filter(({ note }) => note.has_rail), ...trailingRails];
        RENDER_STATS.rails = railList.length;   // debug HUD counter (mounted rails)
        return railList.map(({ note, index }) => (
          <RailLine key={`r-${note.time_ms}-${index}`} note={note} index={index} bpm={bpm} />
        ));
      })()}
      {/* Rail Control-Point Nodes — editing aid, only mounted while paused */}
      {!playing && visibleNotes.map(({ note, index: ni }) => {
        if (!note.has_rail || !note.rail_nodes || note.rail_nodes.length === 0) return null;
        return note.rail_nodes.map((node, nodeIdx) => (
          <RailNodeMesh
            key={`rn-${ni}-${nodeIdx}`}
            note={note}
            noteIndex={ni}
            nodeIndex={nodeIdx}
            node={node as [number, number, number]}
            bpm={bpm}
          />
        ));
      })}
    </>
  );
}

/** Windowed slide/obstacle renderer — same beat-window approach */
function WindowedSlides({
  slides, bpm,
}: {
  slides: Array<import('../../types/beatmap').Slide>;
  bpm: number;
}) {
  const [range, setRange] = useState<[number, number]>([0, Math.min(PERF.maxObstacles, slides.length)]);
  const configs = useEditorStore(s => s.gameplaySettings.obstacleConfigs);
  const selectedSlideIds = useEditorStore(s => s.selectedSlideIds);
  // Console: `center` slides are jump launchers (ConsoleJumpLaunchers draws the
  // ramp at the launch + landing beats), so hide the duplicate wall/floor-mark.
  const isConsole = useEditorStore(s => s.activeView === 'console');

  useFrame(() => {
    const store = useEditorStore.getState();
    const rawBeat = chartBeat();
    const minBeat = rawBeat - VISIBLE_BEATS_BEHIND - WINDOW_MARGIN_BEHIND;
    // Obstacles use the ADAPTIVE draw distance (shortened under load / in dense
    // sections) instead of the full runway — a nearer horizon means fewer walls
    // on screen while everything WITHIN it stays fully visible (no gaps).
    const maxBeat = rawBeat + PERF.obstacleViewBeats + WINDOW_MARGIN_AHEAD;

    // slides are sorted by time_ms; convert beat thresholds to ms
    const minMs = (minBeat / (bpm / 60)) * 1000;
    const maxMs = (maxBeat / (bpm / 60)) * 1000;

    let lo = 0, hi = slides.length;
    while (lo < hi) { const m = (lo + hi) >>> 1; if (slides[m].time_ms < minMs) lo = m + 1; else hi = m; }
    const newStart = lo;
    lo = 0; hi = slides.length;
    while (lo < hi) { const m = (lo + hi) >>> 1; if (slides[m].time_ms <= maxMs) lo = m + 1; else hi = m; }
    const newEnd = Math.min(lo, newStart + PERF.maxObstacles);

    setRange(prev => {
      if (prev[0] === newStart && prev[1] === newEnd) return prev;
      return [newStart, newEnd];
    });

    // ONE central tick updates z/pulse/dim for every mounted obstacle (the
    // per-obstacle useFrame + full material traverse is gone — see ObstacleModel).
    tickObstacles();
  });

  const visible = useMemo(() => slides.slice(range[0], range[1]), [slides, range]);

  // Split the visible obstacles: the bulk (configurable shapes, NOT selected)
  // render through the shared InstancedObstaclePools — a constant handful of draw
  // calls for the whole dense section. Selected obstacles (need the highlight +
  // gizmo) and any non-model fallback types keep the classic per-obstacle
  // ObstacleModel; those are always few.
  const { instanced, classic } = useMemo(() => {
    const inst: Array<{ slide: import('../../types/beatmap').Slide; index: number }> = [];
    const cls: Array<{ slide: import('../../types/beatmap').Slide; index: number }> = [];
    for (let i = 0; i < visible.length; i++) {
      const slide = visible[i];
      const index = range[0] + i;
      if (isConsole && slide.wall_type === 'center') continue; // console jump launcher draws it
      if (CONFIGURABLE_KEYS.has(slide.wall_type) && !selectedSlideIds.includes(index)) inst.push({ slide, index });
      else cls.push({ slide, index });
    }
    return { instanced: inst, classic: cls };
  }, [visible, range, isConsole, selectedSlideIds]);

  // Purple ground markers under the obstacles — same instanced pool as notes.
  const slideMarkers = useMemo<MarkerDesc[]>(() => {
    const out: MarkerDesc[] = [];
    for (const slide of visible) {
      if (isConsole && slide.wall_type === 'center') continue;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const cfg = (configs as any)[slide.wall_type];
      const [cx] = resolveWallXY(slide.wall_type, slide.position, cfg);
      out.push({ x: cx, beat: slide.time_beats, color: '#A78BFA' });
    }
    return out;
  }, [visible, isConsole, configs]);

  return (
    <Suspense fallback={null}>
      {/* Bulk obstacles → shared instanced pools (few draw calls). */}
      <InstancedObstaclePools entries={instanced} />
      {/* Selected / fallback obstacles → classic per-obstacle mesh. */}
      {classic.map(({ slide, index }) => (
        <ObstacleModel
          key={`s-${slide.time_ms}-${index}`}
          slide={slide}
          index={index}
          selected={selectedSlideIds.includes(index)}
        />
      ))}
      {/* Purple floor markers — one instanced draw call (hops on the snap). */}
      <InstancedFloorMarkers markers={slideMarkers} />
    </Suspense>
  );
}

// Synth Console square / triangle obstacles — neon outline frames the hoverboard
// rider flies through. The VR editor never drew these; render them as a line-loop
// at the obstacle's beat so they scroll with the BeatScrollGroup. Console-only.
const DEG2RAD = Math.PI / 180;

function ConsoleObstacleFrame({
  obstacle, shape, color, bpm,
}: {
  obstacle: import('../../types/beatmap').Obstacle;
  shape: 'square' | 'triangle';
  color: string;
  bpm: number;
}) {
  const ref = useRef<THREE.LineLoop>(null);
  const geometry = useMemo(() => {
    const S = 1.45;
    const pts: number[][] = shape === 'square'
      ? [[-S, -S, 0], [S, -S, 0], [S, S, 0], [-S, S, 0]]
      : [[-S, -S, 0], [S, -S, 0], [0, S, 0]];
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(pts.flat()), 3));
    return g;
  }, [shape]);
  useEffect(() => () => geometry.dispose(), [geometry]);

  const beat = obstacle.time_beats ?? ((obstacle.time_ms / 1000) * (bpm / 60));
  const x = (obstacle.position ? obstacle.position[0] : 0) * GRID_SCALE * 10;
  const y = (obstacle.position ? obstacle.position[1] : 0) * GRID_SCALE * 10 + NOTE_Y_OFFSET;
  const z = -beat * BEAT_SPACING;
  const rot = (obstacle.z_rotation || 0) * DEG2RAD;

  // The BeatScrollGroup scrolls these but doesn't cull them — hide off-window
  // frames each frame (the same window NoteMesh uses) so far obstacles cost nothing.
  useFrame(() => {
    // Offset-adjusted chart beat (same frame as BeatScrollGroup + note.time_beats).
    // NOT a raw currentTime/60*bpm — offset≠0 maps would cull at the wrong time.
    const cb = chartBeat();
    const relZ = (beat - cb) * BEAT_SPACING;
    if (ref.current) ref.current.visible =
      relZ >= -VISIBLE_BEATS_BEHIND * BEAT_SPACING && relZ <= VISIBLE_BEATS_AHEAD * BEAT_SPACING;
  });

  return (
    <lineLoop ref={ref} position={[x, y, z]} rotation={[0, 0, rot]} geometry={geometry}>
      <lineBasicMaterial color={color} toneMapped={false} transparent opacity={0.92} />
    </lineLoop>
  );
}

// ── Synth Console AIR obstacles (the "triangle" obstacles) ───────────────────
// Replaces the placeholder triangle line-frames with the real game air-obstacle
// mesh (`SM_Obstacle_Air` — the orange frame the rider flies through). Loaded
// once via a provider and shared (geometry + one neon material) across every
// instance, which each cull off-window like ConsoleObstacleFrame did.
const AIR_OBSTACLE_FBX = '/models/console/obstacles/SM_Obstacle_Air.fbx';
const AIR_OBSTACLE_ARROW_FBX = '/models/console/obstacles/SM_Obstacle_Air_Arrow.fbx';
useFBX.preload(AIR_OBSTACLE_FBX);
useFBX.preload(AIR_OBSTACLE_ARROW_FBX);
const AIR_OBSTACLE_SCALE = 1.2;

const ConsoleAirObstacleCtx = createContext<{
  plain: THREE.BufferGeometry; withArrow: THREE.BufferGeometry; mat: THREE.ShaderMaterial;
} | null>(null);

function ConsoleAirObstacleProvider({ children }: { children: React.ReactNode }) {
  const frameFbx = useFBX(AIR_OBSTACLE_FBX);
  const arrowFbx = useFBX(AIR_OBSTACLE_ARROW_FBX);
  const geos = useMemo(() => {
    const frame = mergeFbxGeometry(frameFbx);
    if (!frame) return null;
    const plain = normalizeNoteGeometry(frame);          // bare frame (squares)
    let withArrow = plain;                                // frame + direction arrow (triangles)
    const arrow = mergeFbxGeometry(arrowFbx);
    if (arrow) {
      const combined = mergeGeometries([frame.clone(), arrow], false);
      withArrow = normalizeNoteGeometry(combined ?? frame);
    }
    return { plain, withArrow };
  }, [frameFbx, arrowFbx]);
  const mat = useMemo(
    () => makeNeonMaterial({
      color: '#FF8A3D', fresnelColor: '#ffd9a0', fresnelPower: 2.2,
      intensity: 0.9, transparent: true, opacity: 0.7, flicker: 0.1, flickerSpeed: 5,
    }),
    [],
  );
  useEffect(() => () => mat.dispose(), [mat]);
  useFrame(() => { mat.uniforms.uTime.value = (typeof performance !== 'undefined' ? performance.now() : Date.now()) / 1000; });
  const value = useMemo(() => (geos ? { ...geos, mat } : null), [geos, mat]);
  return <ConsoleAirObstacleCtx.Provider value={value}>{children}</ConsoleAirObstacleCtx.Provider>;
}

function ConsoleAirObstacle({ obstacle, withArrow = false }: { obstacle: import('../../types/beatmap').Obstacle; withArrow?: boolean }) {
  const ctx = useContext(ConsoleAirObstacleCtx);
  const ref = useRef<THREE.Mesh>(null);
  const beat = obstacle.time_beats ?? 0;
  const x = (obstacle.position ? obstacle.position[0] : 0) * GRID_SCALE * 10;
  const y = (obstacle.position ? obstacle.position[1] : 0) * GRID_SCALE * 10 + NOTE_Y_OFFSET;
  const rot = (obstacle.z_rotation || 0) * DEG2RAD;

  useFrame(() => {
    const relZ = (beat - chartBeat()) * BEAT_SPACING;
    if (ref.current) ref.current.visible =
      relZ >= -VISIBLE_BEATS_BEHIND * BEAT_SPACING && relZ <= VISIBLE_BEATS_AHEAD * BEAT_SPACING;
  });

  if (!ctx) return null;
  return (
    <mesh
      ref={ref}
      geometry={withArrow ? ctx.withArrow : ctx.plain}
      material={ctx.mat}
      position={[x, y, -beat * BEAT_SPACING]}
      rotation={[0, 0, rot]}
      scale={[AIR_OBSTACLE_SCALE, AIR_OBSTACLE_SCALE, AIR_OBSTACLE_SCALE]}
    />
  );
}

// Windowed mount for air obstacles: only the ~PERF.maxObstacles nearest the
// playhead are mounted (binary-searched by beat), instead of the ENTIRE chart.
// A dense map otherwise mounted thousands of meshes + per-mesh useFrames of
// which only ~40 are ever on screen — the single biggest Console slowdown.
function WindowedAirObstacles({ obstacles, withArrow, bpm }: {
  obstacles: Array<import('../../types/beatmap').Obstacle>;
  withArrow: boolean;
  bpm: number;
}) {
  // Precompute each obstacle's beat + keep sorted so the binary search is valid
  // (backend order is usually sorted, but don't rely on it).
  const sorted = useMemo(() => {
    const withBeat = obstacles.map(o => ({ o, beat: o.time_beats ?? ((o.time_ms / 1000) * (bpm / 60)) }));
    withBeat.sort((a, b) => a.beat - b.beat);
    return withBeat;
  }, [obstacles, bpm]);

  const [range, setRange] = useState<[number, number]>(() => [0, Math.min(PERF.maxObstacles, sorted.length)]);

  useFrame(() => {
    const rawBeat = chartBeat();
    const minBeat = rawBeat - VISIBLE_BEATS_BEHIND - WINDOW_MARGIN_BEHIND;
    const maxBeat = rawBeat + PERF.obstacleViewBeats + WINDOW_MARGIN_AHEAD;
    let lo = 0, hi = sorted.length;
    while (lo < hi) { const m = (lo + hi) >>> 1; if (sorted[m].beat < minBeat) lo = m + 1; else hi = m; }
    const newStart = lo;
    lo = 0; hi = sorted.length;
    while (lo < hi) { const m = (lo + hi) >>> 1; if (sorted[m].beat <= maxBeat) lo = m + 1; else hi = m; }
    const newEnd = Math.min(lo, newStart + PERF.maxObstacles);
    setRange(prev => (prev[0] === newStart && prev[1] === newEnd) ? prev : [newStart, newEnd]);
  });

  const visible = useMemo(() => sorted.slice(range[0], range[1]), [sorted, range]);
  return (
    <>
      {visible.map(({ o }, i) => (
        <ConsoleAirObstacle key={`ao-${o.time_ms}-${range[0] + i}`} obstacle={o} withArrow={withArrow} />
      ))}
    </>
  );
}

// Synth VR air-obstacle wrapper: feed the Square / Triangle arrays to the shared
// instanced VR air-obstacle pools (one instanced draw per shape). The pool
// FOV-culls per instance, so the handful an average map carries needs no extra
// windowing. `index` is kept for a future select/delete pass.
function VRAirObstacles({ squares, triangles }: {
  squares: Array<import('../../types/beatmap').Obstacle>;
  triangles: Array<import('../../types/beatmap').Obstacle>;
}) {
  const entries = useMemo(() => {
    const out: Array<{ ob: import('../../types/beatmap').Obstacle; shape: ObstacleShape; index: number }> = [];
    squares.forEach((ob, i) => out.push({ ob, shape: 'square', index: i }));
    triangles.forEach((ob, i) => out.push({ ob, shape: 'triangle', index: i }));
    return out;
  }, [squares, triangles]);
  return <InstancedAirObstaclePools entries={entries} />;
}

// Synth Console chain notes — a line linking each is_chain_note note to its
// same-hand predecessor (like the game's ChainNotesLineRenderer). Built as ONE
// vertex-coloured lineSegments (a single draw call) that scrolls with the group.
function ConsoleChainLines({ notes, bpm }: { notes: Note[]; bpm: number }) {
  const gp = useEditorStore(s => s.gameplaySettings);
  const noteColors = useMemo(
    () => getNoteColorsFromSettings(gp),
    [gp.noteColorLeft, gp.noteColorRight, gp.noteColorOneHand, gp.noteColorBothHands],
  );

  const geometry = useMemo(() => {
    const positions: number[] = [];
    const colors: number[] = [];
    const col = new THREE.Color();
    const CHAIN_GAP = 2.0;   // beats — max gap for two notes to count as one chain
    const lastByHand: Record<string, Note | undefined> = {};
    for (const note of notes) {
      // Chains only link a real left/right hand to its own predecessor. 'special'
      // (one/both-hands) notes share one hand bucket and must NOT cross-link.
      if (note.hand !== 'left' && note.hand !== 'right') continue;
      const prev = lastByHand[note.hand];
      if (note.is_chain_note && prev && (note.time_beats - prev.time_beats) <= CHAIN_GAP) {
        positions.push(
          prev.position[0] * GRID_SCALE * 10, prev.position[1] * GRID_SCALE * 10 + NOTE_Y_OFFSET, -prev.time_beats * BEAT_SPACING,
          note.position[0] * GRID_SCALE * 10, note.position[1] * GRID_SCALE * 10 + NOTE_Y_OFFSET, -note.time_beats * BEAT_SPACING,
        );
        col.set(noteColors[note.note_type] || '#ffffff');
        colors.push(col.r, col.g, col.b, col.r, col.g, col.b);
      }
      lastByHand[note.hand] = note;
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(positions), 3));
    g.setAttribute('color', new THREE.BufferAttribute(new Float32Array(colors), 3));
    return g;
  }, [notes, noteColors]);

  useEffect(() => () => geometry.dispose(), [geometry]);

  if (geometry.attributes.position.count === 0) return null;
  return (
    <lineSegments geometry={geometry}>
      <lineBasicMaterial vertexColors toneMapped={false} transparent opacity={0.7} />
    </lineSegments>
  );
}

// ── Synth Console jump launchers (floor→air) ─────────────────────────────────
// A `center` slide (wall_type === 'center', GameEnums.WallType.Center → IsJump)
// is a JUMP LAUNCHER: the rider hops and the floor breaks at that beat (the
// short-jump form of the game's floor→air mechanic). We place the game's
// `SM_BeatTrack_JumpShort` launcher ramp on the road at each one, with the
// shared neon material. (The full long-jump "flying ribbon" section needs a map
// that actually contains long-jump/AirWall data — GimmeLove has only centers.)
// Console-only; rendered inside BeatScrollGroup so it scrolls with the chart.
const JUMP_LAUNCHER_FBX = '/models/console/track/SM_BeatTrack_JumpShort.fbx';
useFBX.preload(JUMP_LAUNCHER_FBX);
const LAUNCHER_SCALE = 1.4;

function ConsoleJumpLaunchers({ slides }: { slides: Array<import('../../types/beatmap').Slide> }) {
  const fbx = useFBX(JUMP_LAUNCHER_FBX);
  const geo = useMemo(() => {
    const merged = mergeFbxGeometry(fbx);
    return merged ? normalizeNoteGeometry(merged) : null;
  }, [fbx]);
  const mat = useMemo(
    () => makeNeonMaterial({
      color: '#1CC5F0', fresnelColor: '#Bfefff', intensity: 1.5,
      transparent: true, opacity: 0.9, flicker: 0.12, flickerSpeed: 5,
    }),
    [],
  );
  useEffect(() => () => mat.dispose(), [mat]);

  // center slides only (jump launchers); filter preserves the slides' sort.
  const launchers = useMemo(
    () => slides.filter(s => s.wall_type === 'center'),
    [slides],
  );
  const [range, setRange] = useState<[number, number]>(() => [0, Math.min(PERF.maxObstacles, launchers.length)]);

  useFrame(() => {
    mat.uniforms.uTime.value = (typeof performance !== 'undefined' ? performance.now() : Date.now()) / 1000;
    // Window to the nearest launchers so a long chart doesn't mount them all.
    const rawBeat = chartBeat();
    const minBeat = rawBeat - VISIBLE_BEATS_BEHIND - WINDOW_MARGIN_BEHIND;
    const maxBeat = rawBeat + PERF.obstacleViewBeats + WINDOW_MARGIN_AHEAD;
    let lo = 0, hi = launchers.length;
    while (lo < hi) { const m = (lo + hi) >>> 1; if (launchers[m].time_beats < minBeat) lo = m + 1; else hi = m; }
    const newStart = lo;
    lo = 0; hi = launchers.length;
    while (lo < hi) { const m = (lo + hi) >>> 1; if (launchers[m].time_beats <= maxBeat) lo = m + 1; else hi = m; }
    const newEnd = Math.min(lo, newStart + PERF.maxObstacles);
    setRange(prev => (prev[0] === newStart && prev[1] === newEnd) ? prev : [newStart, newEnd]);
  });

  const visible = useMemo(() => launchers.slice(range[0], range[1]), [launchers, range]);
  if (!geo) return null;

  return (
    <>
      {visible.map((s, i) => {
        const x = (s.position ? s.position[0] : 0) * GRID_SCALE * 10;
        return (
          <mesh
            key={`jl-${range[0] + i}`}
            geometry={geo}
            material={mat}
            position={[x, 0.02, -s.time_beats * BEAT_SPACING]}
            scale={[LAUNCHER_SCALE, LAUNCHER_SCALE, LAUNCHER_SCALE]}
          />
        );
      })}
    </>
  );
}

/** Windowed time-ms markers (crouches, jumps) */
function WindowedTimeMarkers({
  times, bpm, color, label,
}: {
  times: number[];
  bpm: number;
  color: string;
  label: string;
}) {
  const [range, setRange] = useState<[number, number]>([0, Math.min(100, times.length)]);

  useFrame(() => {
    const store = useEditorStore.getState();
    const rawBeat = chartBeat();
    const minMs = ((rawBeat - VISIBLE_BEATS_BEHIND - WINDOW_MARGIN_BEHIND) / (bpm / 60)) * 1000;
    const maxMs = ((rawBeat + VISIBLE_BEATS_AHEAD + WINDOW_MARGIN_AHEAD) / (bpm / 60)) * 1000;

    let lo = 0, hi = times.length;
    while (lo < hi) { const m = (lo + hi) >>> 1; if (times[m] < minMs) lo = m + 1; else hi = m; }
    const newStart = lo;
    lo = 0; hi = times.length;
    while (lo < hi) { const m = (lo + hi) >>> 1; if (times[m] <= maxMs) lo = m + 1; else hi = m; }
    const newEnd = Math.min(lo, newStart + MAX_VISIBLE_NOTES);

    setRange(prev => {
      if (prev[0] === newStart && prev[1] === newEnd) return prev;
      return [newStart, newEnd];
    });
  });

  return (
    <>
      {times.slice(range[0], range[1]).map((timeMs, i) => (
        <TimeMarker
          key={`${label}-${timeMs}-${range[0] + i}`}
          timeMs={timeMs}
          bpm={bpm}
          color={color}
          label={label}
        />
      ))}
    </>
  );
}

function SceneContent() {
  const beatmap = useEditorStore(s => s.beatmap);
  const activeDifficulty = useEditorStore(s => s.activeDifficulty);
  // NOTE: currentBeat is NOT subscribed reactively here. All beat-relative
  // children read it imperatively via useFrame / BeatScrollGroup to avoid
  // re-rendering the entire scene tree 60×/sec during playback.
  const selectedNoteIds = useEditorStore(s => s.selectedNoteIds);
  const figureMode = useEditorStore(s => s.figureMode);
  const bgBottom = useEditorStore(s => s.interfaceSettings.bgGradientBottom);
  const lightColor = useEditorStore(s => s.interfaceSettings.lightColor);
  const lightIntensity = useEditorStore(s => s.interfaceSettings.lightIntensity);
  const lightRotX = useEditorStore(s => s.interfaceSettings.lightRotationX);
  const lightRotY = useEditorStore(s => s.interfaceSettings.lightRotationY);
  const ambientColor = useEditorStore(s => s.interfaceSettings.ambientColor);
  const ambientIntensity = useEditorStore(s => s.interfaceSettings.ambientIntensity);
  const fogNear = useEditorStore(s => s.interfaceSettings.fogNear);
  const fogFar = useEditorStore(s => s.interfaceSettings.fogFar);
  const pl1Color = useEditorStore(s => s.interfaceSettings.pointLight1Color);
  const pl1Intensity = useEditorStore(s => s.interfaceSettings.pointLight1Intensity);
  const pl2Color = useEditorStore(s => s.interfaceSettings.pointLight2Color);
  const pl2Intensity = useEditorStore(s => s.interfaceSettings.pointLight2Intensity);
  const pl3Color = useEditorStore(s => s.interfaceSettings.pointLight3Color);
  const pl3Intensity = useEditorStore(s => s.interfaceSettings.pointLight3Intensity);
  const bookmarks = useEditorStore(s => s.bookmarks);
  const flashEvents = useEditorStore(s => s.flashEvents);
  const activeView = useEditorStore(s => s.activeView);   // 'console' enables console-only renderers

  const difficulty = beatmap?.difficulties[activeDifficulty];
  const bpm = beatmap?.bpm || 120;
  const isConsole = activeView === 'console';

  return (
    <>
      {/* Lighting — configurable from Settings > Interface > 3D Lighting */}
      <ambientLight intensity={ambientIntensity} color={ambientColor} />
      <directionalLight
        position={[
          Math.sin(lightRotY * Math.PI / 180) * 10,
          Math.sin(lightRotX * Math.PI / 180) * 10,
          -Math.cos(lightRotY * Math.PI / 180) * 10,
        ]}
        intensity={lightIntensity}
        color={lightColor}
      />
      <pointLight position={[0, 2, -5]} intensity={pl1Intensity} color={pl1Color} />
      <pointLight position={[0, 2, -20]} intensity={pl2Intensity} color={pl2Color} distance={30} />
      <pointLight position={[0, 3, -40]} intensity={pl3Intensity} color={pl3Color} distance={50} />

      <CameraController />
      <ScrollHandler />
      <BoxSelectHandler />

      {/* Atmosphere — gradient background + fog matching bottom color */}
      <BackgroundGradient />
      <fog attach="fog" args={[bgBottom, fogNear, fogFar]} />

      {!isConsole && <TunnelFrame />}
      {isConsole && <Suspense fallback={null}><ConsoleRoad notes={difficulty?.notes ?? []} bpm={bpm} /></Suspense>}
      <IntensityFloorTrail />
      <BeatGrid />
      <StepLines />
      <Playhead />
      <SparkParticles />
      <DeleteBursts />
      <FlashLightEffect />
      {/* Console: the figure is always the real game Rider (replaces the
          mannequin). VR/Flow keep the procedural mannequin / dancer mesh. */}
      {figureMode !== 'off' && isConsole && <Suspense fallback={null}><RiderModel /></Suspense>}
      {figureMode === 'mannequin' && !isConsole && <HumanFigure />}
      {figureMode === 'mesh' && !isConsole && <Suspense fallback={null}><DancerModel /></Suspense>}
      <GridGuide />
      <NotePreview />
      <NoteGizmo />
      <RailNodeGizmo />
      <SlideGizmo />
      <TimeMarkerGizmo />
      <DesignMeshGizmo />
      <DesignPathEditor />
      <PlacementPlane />
      {/* Shared adaptive perf governor — tunes the visible-object budget to FPS. */}
      <AdaptivePerfGovernor />
      {/* Potato: hold the render at 30fps. Mounted conditionally — a
          priority>0 useFrame takes over rendering for the whole canvas. */}
      <PotatoFrameCapGate />

      {/* Beat-relative objects — windowed rendering for performance.
          Only notes/rails/obstacles within the visible beat range (+margin) are mounted. */}
      <BeatScrollGroup>
        {/* Loop region band (right-drag) — scrolls with the notes */}
        <LoopOverlay />
        {/* Selected-notes marching-ants band — 3D twin of the timeline overlay */}
        <SelectionFloorOverlay />

        {/* Notes + Rails + Rail Nodes (windowed). Console wraps them in the
            game-note-geometry provider (Suspense-loaded); VR renders directly. */}
        {difficulty && difficulty.notes.length > 0 && (
          isConsole ? (
            <Suspense fallback={null}>
              <ConsoleNoteGeoProvider>
                <WindowedNotes notes={difficulty.notes} selectedNoteIds={selectedNoteIds} bpm={bpm} />
              </ConsoleNoteGeoProvider>
            </Suspense>
          ) : (
            <WindowedNotes notes={difficulty.notes} selectedNoteIds={selectedNoteIds} bpm={bpm} />
          )
        )}

        {/* Slides / Obstacles (windowed) */}
        {difficulty && difficulty.slides.length > 0 && (
          <WindowedSlides slides={difficulty.slides} bpm={bpm} />
        )}

        {/* Synth Console air obstacles — real game SM_Obstacle_Air mesh.
            Squares = bare frame; triangles = frame + direction arrow. One shared
            provider (geometry + material loaded once). */}
        {isConsole && difficulty && ((difficulty.squares?.length || 0) + (difficulty.triangles?.length || 0) > 0) && (
          <Suspense fallback={null}>
            <ConsoleAirObstacleProvider>
              {difficulty.squares && difficulty.squares.length > 0 && (
                <WindowedAirObstacles obstacles={difficulty.squares} withArrow={false} bpm={bpm} />
              )}
              {difficulty.triangles && difficulty.triangles.length > 0 && (
                <WindowedAirObstacles obstacles={difficulty.triangles} withArrow bpm={bpm} />
              )}
            </ConsoleAirObstacleProvider>
          </Suspense>
        )}

        {/* Synth VR air obstacles — Square / Triangle drawn with the real VR
            obstacle shapes (stood upright + rolled by zRotation). Console draws
            its own game-mesh version above; this is the VR-editor equivalent. */}
        {!isConsole && difficulty && ((difficulty.squares?.length || 0) + (difficulty.triangles?.length || 0) > 0) && (
          <Suspense fallback={null}>
            <VRAirObstacles squares={difficulty.squares ?? []} triangles={difficulty.triangles ?? []} />
          </Suspense>
        )}

        {/* Synth Console chain-note links (console-only) */}
        {isConsole && difficulty && difficulty.notes.length > 0 && (
          <ConsoleChainLines notes={difficulty.notes} bpm={bpm} />
        )}

        {/* Synth Console jump launchers — center slides (floor→air hop points) */}
        {isConsole && difficulty && difficulty.slides.length > 0 && (
          <Suspense fallback={null}>
            <ConsoleJumpLaunchers slides={difficulty.slides} />
          </Suspense>
        )}

        {/* Crouches (windowed) */}
        {difficulty && difficulty.crouches.length > 0 && (
          <WindowedTimeMarkers times={difficulty.crouches} bpm={bpm} color="#6B648A" label="CROUCH" />
        )}

        {/* Jumps (windowed) */}
        {difficulty && difficulty.jumps.length > 0 && (
          <WindowedTimeMarkers times={difficulty.jumps} bpm={bpm} color="#34D399" label="JUMP" />
        )}

        {/* Bookmarks (global — visible across all difficulties) */}
        {bookmarks.map((bm, i) => (
          <BookmarkMarker key={`bm-${i}`} bookmark={bm} />
        ))}
        {/* Snap rings for click-to-place when the Bookmark tool is active. */}
        <BookmarkPlacementRings />

        {/* Flash Light Events (global — visible across all difficulties) */}
        {flashEvents.map((ev, i) => (
          <FlashEventMarker key={`flash-${i}`} event={ev} />
        ))}
        {/* Snap rings for click-to-place when the Light tool is active. */}
        <FlashPlacementRings />
        {/* One-shot ray bursts when a light/bookmark is added/removed. */}
        <LightBurstLayer />

        {/* Designative wall layer (global — reuses obstacle FBX shapes, instanced). */}
        <Suspense fallback={null}>
          <DesignShapes />
        </Suspense>
      </BeatScrollGroup>
    </>
  );
}

// ── Renderer GL config per render mode ────────────────────────────────────────

function getGlProps(mode: RenderMode): Partial<THREE.WebGLRendererParameters> {
  switch (mode) {
    case 'gpu':
      return {
        antialias: true,
        // alpha so a loaded .visualizer background can show through when the
        // scene background is cleared (see BackgroundGradient).
        alpha: true,
        powerPreference: 'high-performance',
        precision: 'highp',
        stencil: false,
        depth: true,
      };
    case 'cpu':
      return {
        antialias: false,
        alpha: true,
        powerPreference: 'low-power',
        precision: 'lowp',
        stencil: false,
        depth: true,
      };
    default: // 'auto'
      return {
        antialias: true,
        alpha: true,
        // Dual-GPU laptops pick the INTEGRATED chip on 'default' — telemetry
        // showed users mapping on the weak GPU while a discrete one idled.
        powerPreference: 'high-performance',
      };
  }
}

function getDpr(mode: RenderMode): number | [number, number] {
  switch (mode) {
    case 'gpu':  return [1, 2];            // High DPI — let R3F scale up
    case 'cpu':  return 1;                 // Lock to 1x — save GPU work
    default:     return [1, 1.5];          // Auto: moderate
  }
}

/** Mounts the frame cap only while the active preset caps the framerate (it
 *  must not exist otherwise — a priority>0 useFrame owns the canvas's render
 *  pass). The cap value is the preset's user-editable fpsCap. Unmounted during
 *  a benchmark run: the benchmark measures the editor's real uncapped rate, and
 *  a 30fps cap would floor every reading at 30. */
function PotatoFrameCapGate() {
  const fpsCap = useEditorStore(s => s.performanceSettings[s.perfMode].fpsCap);
  const benchmarking = useEditorStore(s => s.benchmarkOpen);
  return fpsCap > 0 && !benchmarking ? <PotatoFrameCap cap={fpsCap} /> : null;
}

// ── Exported Canvas ─────────────────────────────────────────────────────────

export default function TunnelScene() {
  const renderMode = useEditorStore(s => s.renderMode);
  const cameraFov = useEditorStore(s => s.interfaceSettings.cameraFov);
  // Active perf preset (user-editable): antialias false forces MSAA off (a
  // context-creation flag → the Canvas key remounts it); dprCap pins the pixel
  // ratio before the render scale. Defaults reproduce the old potato tier.
  const presetAa = useEditorStore(s => s.performanceSettings[s.perfMode].antialias);
  const presetDprCap = useEditorStore(s => s.performanceSettings[s.perfMode].dprCap);
  const glProps = useMemo(() => {
    const p = getGlProps(renderMode);
    // A preset's `true` DEFERS to the render mode's own choice (cpu mode also
    // drops MSAA); only `false` forces it off.
    return presetAa ? p : { ...p, antialias: false };
  }, [renderMode, presetAa]);
  // Resolved to a single number and scaled by the adaptive budget here, in the
  // PROP — never imperatively (see perf/useScaledDpr.ts: R3F re-asserts this
  // prop every render, and fighting it reallocated the drawing buffer, which
  // was the Perf/Auto-mode flash).
  const dpr = useScaledDpr(
    useMemo(() => getDpr(renderMode), [renderMode]),
    presetDprCap ?? undefined,
  );

  // Cold-mount resize kick: on a fresh page load the Canvas' container can be
  // measured at 0×0 before the surrounding flex layout settles, leaving R3F stuck
  // at its 300×150 default — the scene renders into a tiny offscreen buffer and
  // the viewport looks blank until the first real window resize. (It never showed
  // up under the dev server because HMR re-renders kept re-measuring it.) The
  // settle timing varies (map fetch + layout reflow), so nudge a window resize a
  // handful of times across ~1s; R3F re-measures the real container on each and
  // the extra events are cheap + idempotent once it's the right size.
  useEffect(() => {
    const fire = () => { try { window.dispatchEvent(new Event('resize')); } catch { /* noop */ } };
    const timers = [0, 60, 160, 360, 700, 1200].map(ms => setTimeout(fire, ms));
    return () => { timers.forEach(clearTimeout); };
  }, []);

  // Placement toast overlay
  const [toast, setToast] = useState<string | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    _toastCallback = (msg: string) => {
      setToast(msg);
      if (toastTimer.current) clearTimeout(toastTimer.current);
      toastTimer.current = setTimeout(() => setToast(null), 2000);
    };
    return () => { _toastCallback = null; };
  }, []);

  return (
    <>
      <Canvas
        // antialias is a context-creation flag — the only way to change MSAA
        // is a fresh context, so key on the RESOLVED aa value. (dpr is a live
        // prop — never key on it.)
        key={glProps.antialias ? 'aa' : 'no-aa'}
        camera={{
          position: [0, 1.6, 3],     // Player eye level, slightly behind the playhead
          fov: cameraFov,              // Wide FOV for strong perspective like the reference
          near: 0.1,
          far: 500,
          rotation: [-0.15, 0, 0],    // Slight downward tilt to see the floor grid
        }}
        gl={glProps}
        dpr={dpr}
        // Always transparent: the backdrop is the shared bg3dLayer DOM gradient
        // behind this canvas (driven by the BACKGROUND theme picker), so the 3D
        // scene must never paint an opaque CSS background over it.
        style={{ background: 'transparent' }}
        onContextMenu={(e) => e.preventDefault()}
        onPointerMissed={(e) => {
          // Left-click on empty space (off every object) clears the note
          // selection AND the loop region, if either exists. Skipped right
          // after a box-select drag (which also ends on empty space).
          if ((e as MouseEvent).button !== 0 || _suppressMissedClear) return;
          const st = useEditorStore.getState();
          if (st.selectedNoteIds.length > 0 || st.selectedSlideIds.length > 0) st.clearSelection();
          if (st.loopRegion) st.setLoopRegion(null);
        }}
      >
        <SceneContent />
      </Canvas>
      {toast && (
        <div style={{
          position: 'absolute',
          top: 12,
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'rgba(239, 68, 68, 0.15)',
          border: '1px solid rgba(239, 68, 68, 0.4)',
          borderRadius: 8,
          padding: '6px 16px',
          color: '#F87171',
          fontSize: 11,
          fontWeight: 600,
          fontFamily: font.sans,
          letterSpacing: 0.3,
          pointerEvents: 'none',
          zIndex: 10,
          whiteSpace: 'nowrap',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
          animation: 'fadeIn 0.15s ease',
        }}>
          {toast}
        </div>
      )}
    </>
  );
}
